import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useSocket } from '../../context/socket';
import {
  LOCALOPS_OPS_LAST_TASK_ID_KEY,
  canViewLocalOpsOps,
} from '../../utils/localOpsOpsAccess';
import './localOpsOpsHealth.css';

const STATUS_LABELS = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  idle: 'Not checked',
};

const formatJgk = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0 JGK';
  return `${Math.round(amount).toLocaleString('en-US')} JGK`;
};

const formatUsd = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '--';
  return `$${amount.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return 'Not checked';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const truthyLabel = (value) => {
  if (value === true) return 'Pass';
  if (value === false) return 'Fail';
  return 'Unknown';
};

const TASK_STATUS_OPTIONS = [
  '',
  'created',
  'discoverable',
  'accepted',
  'in_progress',
  'completed',
  'cancelled',
  'expired',
];

const LAUNCH_ATTRIBUTION_ENDPOINT =
  'local-ops/debug/camden-launch-attribution';
const OPERATOR_VISIBILITY_ENDPOINT =
  'local-ops/debug/operator-visibility';
const PROOF_ATTRIBUTION_ENDPOINT =
  'local-ops/debug/proof-attributions';
const CONCIERGE_INTAKE_ENDPOINT =
  'local-ops/intake-drafts/collision-specialist-ride/send-magic-link';
const ROLE_GRANTS_LOOKUP_ENDPOINT = 'local-ops/admin/role-grants/player';
const ROLE_GRANTS_ENDPOINT = 'local-ops/admin/role-grants';
const ROLE_GRANTS_REVOKE_ENDPOINT = 'local-ops/admin/role-grants/revoke';

const COLLISION_SPECIALIST_PICKUP_ADDRESS =
  'Collision Specialist, 134 West Main Street, Camden, TN 38320';

const buildDefaultConciergeIntakeForm = () => ({
  deliveryAddress: '',
  customerEmail: '',
  customerPhone: '',
  notes: '',
});

const ROLE_GRANT_OPTIONS = Object.freeze([
  {
    value: 'conciergeAdmin',
    label: 'LocalOps Concierge',
    description:
      'Can help capture local opportunities and send review links. Cannot access global admin.',
  },
  {
    value: 'communityTaskAdmin',
    label: 'Verified Community Partner',
    description:
      'Can create approved Community Tasks for unpaid local help once Community Tasks are enabled. Paid errands, shopping, driving, deliveries, and supply runs should be posted as paid LocalOps tasks.',
  },
]);

const ROLE_GRANT_LABELS = ROLE_GRANT_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const buildDefaultRoleGrantForm = () => ({
  playerId: '',
  role: 'conciergeAdmin',
  regionSlug: 'camden',
  note: '',
  organizationName: '',
});

const CAMDEN_LAUNCH_ATTRIBUTION_PARAMS = Object.freeze({
  regionSlug: 'camden',
  marketSlug: 'camden',
});

const PROOF_ATTRIBUTION_ADMIN_PARAMS = Object.freeze({
  regionSlug: 'camden',
  limit: 25,
});

const isProofAttributionAdminEnabled = () => {
  const configured = String(
    process.env.REACT_APP_LOCALOPS_PROOF_ATTRIBUTION_ADMIN_ENABLED || '',
  )
    .trim()
    .toLowerCase();
  return configured !== '0' && configured !== 'false' && configured !== 'off';
};

export const OPS_HEALTH_SNAPSHOT_VERSION = 2;
export const OPS_HEALTH_SNAPSHOT_PREFIX =
  'localopsOpsHealth:lastSnapshot:v2';
const OPS_HEALTH_MAX_SNAPSHOT_ROWS = 80;
const AI_REPORT_MAX_DEBUG_IDS = 18;
const OPS_HEALTH_SNAPSHOT_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;
const OPS_HEALTH_ROLLING_WINDOW_CAVEAT =
  'These reports cover overlapping rolling windows. Changes may reflect new events entering the window and older events aging out; they are not guaranteed counts of events created after the baseline.';

const LAUNCH_EVENT_LABELS = Object.freeze({
  localops_task_published: 'Task posts',
  localops_task_accepted: 'Task accepts',
  localops_task_completion_verified: 'Task completions',
  localops_action_credit_awarded: 'Action Credit',
  localops_offering_published: 'Offering posts',
  localops_offering_reserved: 'Offering reservations',
  localops_offering_confirmation_requested: 'Confirmation requested',
  localops_offering_settlement_resolved: 'Offering settlements',
});

const LAUNCH_EVENT_ORDER = [
  'localops_task_published',
  'localops_task_accepted',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
  'localops_offering_published',
  'localops_offering_reserved',
  'localops_offering_confirmation_requested',
  'localops_offering_settlement_resolved',
];

const MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER = [
  'localops_task_published',
  'localops_task_accepted',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
];

const VISIT_EVENT_LABELS = Object.freeze({
  localops_region_landing_viewed: 'Landing views',
  localops_region_engaged_10s: '10s engaged',
  localops_region_engaged_60s: '60s engaged',
  localops_region_cta_clicked: 'CTA clicks',
});

const VISIT_EVENT_ORDER = [
  'localops_region_landing_viewed',
  'localops_region_engaged_10s',
  'localops_region_engaged_60s',
  'localops_region_cta_clicked',
];

const LANDING_CHOICE_ROWS = Object.freeze([
  {
    key: 'landing_viewed',
    label: 'Landing viewed',
    eventName: 'localops_region_landing_viewed',
    source: 'durable',
    trackedDetail: 'durable region visit event',
  },
  {
    key: 'requester_mode_shown',
    label: 'Requester mode shown',
    eventName: 'localops_playermap_requester_mode_shown',
    source: 'durable',
    trackedDetail: 'durable PlayerMap impression event',
  },
  {
    key: 'primary_action_shown',
    label: 'Primary action shown',
    eventName: 'localops_playermap_primary_action_shown',
    source: 'durable',
    trackedDetail: 'durable resolver primary-action event',
  },
  {
    key: 'need_help_clicked',
    label: 'I need help clicked',
    source: 'cta',
    ctaIds: ['intent_help', 'partner_post_task'],
  },
  {
    key: 'want_to_earn_clicked',
    label: 'I want to earn clicked',
    source: 'cta',
    ctaIds: ['intent_earn'],
  },
  {
    key: 'post_task_clicked',
    label: 'Post a task clicked',
    source: 'cta',
    ctaIds: ['playermap_post_task_clicked'],
  },
  {
    key: 'offer_locally_clicked',
    label: 'Offer something locally clicked',
    source: 'cta',
    ctaIds: ['playermap_offer_locally_clicked'],
  },
  {
    key: 'community_task_clicked',
    label: 'Help with Community Task clicked',
    source: 'cta',
    ctaIds: ['playermap_help_community_task_clicked'],
  },
  {
    key: 'camden_live_clicked',
    label: 'Enter Camden Live clicked',
    source: 'cta',
    ctaIds: ['playermap_enter_camden_live_clicked'],
  },
  {
    key: 'starter_ideas_opened',
    label: 'Starter ideas opened',
    source: 'cta',
    ctaIds: ['playermap_starter_ideas_opened'],
  },
  {
    key: 'starter_idea_selected',
    label: 'Starter idea selected',
    source: 'cta',
    ctaIds: [
      'playermap_starter_idea_selected',
      'playermap_starter_idea_selected_ride_help',
    ],
  },
  {
    key: 'ride_help_starter_selected',
    label: 'Ride Help starter selected',
    source: 'cta',
    ctaIds: ['playermap_starter_idea_selected_ride_help'],
  },
]);

const REQUESTER_CTA_IDS = Object.freeze(['intent_help', 'partner_post_task']);
const EARNER_CTA_IDS = Object.freeze(['intent_earn']);

const CREATE_SHEET_FUNNEL_ROWS = Object.freeze([
  {
    key: 'sheet_opened',
    label: 'Create sheet opened',
    ctaIds: ['localops_create_sheet_opened'],
  },
  {
    key: 'type_selected',
    label: 'Task type / starter selected',
    ctaIds: ['localops_create_task_type_selected'],
  },
  {
    key: 'details_step_reached',
    label: 'Details step reached',
    ctaIds: ['localops_create_details_step_reached'],
  },
  {
    key: 'location_confirmed',
    label: 'Location field confirmed',
    ctaIds: ['localops_create_location_confirmed'],
  },
  {
    key: 'amount_set',
    label: 'Amount entered / suggested',
    ctaIds: ['localops_create_amount_set'],
  },
  {
    key: 'review_step_reached',
    label: 'Review step reached',
    ctaIds: ['localops_create_review_step_reached'],
  },
  {
    key: 'publish_attempted',
    label: 'Publish attempted',
    ctaIds: ['localops_create_publish_attempted'],
  },
  {
    key: 'publish_blocked',
    label: 'Publish blocked',
    ctaIds: ['localops_create_publish_blocked'],
  },
  {
    key: 'topup_required',
    label: 'Top-up required',
    ctaIds: ['localops_create_topup_required'],
  },
  {
    key: 'topup_started',
    label: 'Top-up started',
    ctaIds: ['localops_create_topup_started'],
  },
  {
    key: 'publish_succeeded',
    label: 'Publish succeeded',
    ctaIds: ['localops_create_publish_succeeded'],
  },
  {
    key: 'publish_failed',
    label: 'Publish failed',
    ctaIds: ['localops_create_publish_failed'],
  },
]);

const CREATE_SHEET_FUNNEL_LABELS = CREATE_SHEET_FUNNEL_ROWS.reduce(
  (acc, row) => {
    acc[row.key] = row.label;
    return acc;
  },
  {},
);

const CREATE_SHEET_JOURNEY_ROWS = Object.freeze([
  {
    key: 'uniqueFlows',
    label: 'Unique create flows',
    detail: 'client-reported createFlowId count',
  },
  {
    key: 'reachedDetails',
    label: 'Reached Details',
    detail: 'flow reached the details step',
  },
  {
    key: 'reachedReview',
    label: 'Reached Review',
    detail: 'flow reached final review',
  },
  {
    key: 'nextUnavailable',
    label: 'Review unavailable',
    detail: 'disabled Next/Review state was visible',
  },
  {
    key: 'explicitlyClosed',
    label: 'Explicitly closed',
    detail: 'sheet close event reported',
  },
  {
    key: 'unknownStops',
    label: 'Unknown after Details',
    detail: 'reached Details without Review, close, or publish success',
  },
  {
    key: 'publishAttempted',
    label: 'Publish attempted',
    detail: 'client reported publish attempt',
  },
  {
    key: 'publishSucceeded',
    label: 'Publish succeeded',
    detail: 'client reported publish success',
  },
]);

const CREATE_SHEET_JOURNEY_LABELS = CREATE_SHEET_JOURNEY_ROWS.reduce(
  (acc, row) => {
    acc[row.key] = row.label;
    return acc;
  },
  {},
);

const LOCATION_FUNNEL_ROWS = Object.freeze([
  {
    key: 'primer_shown',
    label: 'Location primer shown',
    detail: 'primer displayed before location sharing',
    ctaIds: ['localops_location_primer_shown'],
  },
  {
    key: 'browse_first_clicked',
    label: 'Browse first clicked',
    detail: 'user chose browse-first path',
    ctaIds: ['localops_location_browse_first_clicked'],
  },
  {
    key: 'use_my_location_clicked',
    label: 'Use my location clicked',
    detail: 'user clicked location permission CTA',
    ctaIds: ['localops_location_use_my_location_clicked'],
  },
  {
    key: 'permission_granted',
    label: 'Permission granted',
    detail: 'browser location permission succeeded',
    ctaIds: ['localops_location_permission_granted'],
  },
  {
    key: 'permission_denied',
    label: 'Permission denied',
    detail: 'browser denied/unavailable/timeout signal',
    ctaIds: ['localops_location_permission_denied'],
  },
  {
    key: 'save_attempted',
    label: 'Location save attempted',
    detail: 'client attempted save-location flow',
    ctaIds: ['localops_location_save_attempted'],
  },
  {
    key: 'saved',
    label: 'Location saved',
    detail: 'usable saved location completed',
    ctaIds: ['localops_location_saved'],
  },
  {
    key: 'save_failed',
    label: 'Location save failed',
    detail: 'safe failure code recorded',
    ctaIds: ['localops_location_save_failed'],
  },
  {
    key: 'gate_create_task',
    label: 'Gate opened: create task',
    detail: 'create task action needed saved location',
    ctaIds: ['localops_location_gate_opened'],
    actionContexts: ['create_task'],
  },
  {
    key: 'gate_accept_task',
    label: 'Gate opened: accept task',
    detail: 'accept task action needed saved location',
    ctaIds: ['localops_location_gate_opened'],
    actionContexts: ['accept_task'],
  },
  {
    key: 'gate_start_task',
    label: 'Gate opened: start task',
    detail: 'start task action needed saved location',
    ctaIds: ['localops_location_gate_opened'],
    actionContexts: ['start_task'],
  },
  {
    key: 'gate_create_offering',
    label: 'Gate opened: create offering',
    detail: 'create offering action needed saved location',
    ctaIds: ['localops_location_gate_opened'],
    actionContexts: ['create_offering'],
  },
  {
    key: 'gate_reserve_offering',
    label: 'Gate opened: reserve offering',
    detail: 'reserve offering action needed saved location',
    ctaIds: ['localops_location_gate_opened'],
    actionContexts: ['reserve_offering'],
  },
  {
    key: 'retry_clicked',
    label: 'Retry clicked',
    detail: 'user retried after a gate or denial',
    ctaIds: ['localops_location_retry_clicked'],
  },
  {
    key: 'retry_success',
    label: 'Retry success',
    detail: 'gated action resumed after location save',
    ctaIds: ['localops_location_retry_success'],
  },
]);

const LOCATION_FUNNEL_LABELS = LOCATION_FUNNEL_ROWS.reduce((acc, row) => {
  acc[row.key] = row.label;
  return acc;
}, {});

const AUTH_EVENT_LABELS = Object.freeze({
  localops_magic_link_requested: 'Magic links requested',
  localops_magic_link_completed: 'Magic links completed',
  localops_magic_link_player_created: 'Magic-link players created',
  localops_magic_link_existing_player_login: 'Returning magic-link logins',
});

const AUTH_EVENT_ORDER = [
  'localops_magic_link_requested',
  'localops_magic_link_completed',
  'localops_magic_link_player_created',
  'localops_magic_link_existing_player_login',
];

const SHARE_FUNNEL_EVENT_LABELS = Object.freeze({
  localops_share_landing_viewed: 'Share landings',
  localops_share_accept_clicked: 'Landing Accept clicks',
  localops_share_continue_started: 'Continue started',
  localops_share_magic_link_requested: 'Magic links requested',
  localops_share_magic_link_completed: 'Magic links completed',
  localops_share_playermap_arrived: 'PlayerMap arrivals',
  localops_share_task_sheet_opened: 'Task Sheet opens',
  localops_share_task_accept_clicked: 'Sheet Accept clicks',
  localops_share_task_accept_succeeded: 'Accept succeeded',
  localops_share_task_accept_failed: 'Accept failed',
  localops_share_offering_sheet_opened: 'Offering Sheet opens',
  localops_share_offering_reserve_clicked: 'Reserve clicks',
  localops_share_offering_reserve_succeeded: 'Reserve succeeded',
  localops_share_offering_reserve_failed: 'Reserve failed',
  localops_task_completion_verified: 'Completed / verified',
  localops_action_credit_awarded: 'Action Credit',
});

const SHARE_FUNNEL_EVENT_ORDER = [
  'localops_share_landing_viewed',
  'localops_share_accept_clicked',
  'localops_share_continue_started',
  'localops_share_magic_link_requested',
  'localops_share_magic_link_completed',
  'localops_share_playermap_arrived',
  'localops_share_task_sheet_opened',
  'localops_share_task_accept_clicked',
  'localops_share_task_accept_succeeded',
  'localops_share_task_accept_failed',
  'localops_share_offering_sheet_opened',
  'localops_share_offering_reserve_clicked',
  'localops_share_offering_reserve_succeeded',
  'localops_share_offering_reserve_failed',
  'localops_task_completion_verified',
  'localops_action_credit_awarded',
];

const padDatePart = (value) => String(value).padStart(2, '0');

const toDateInputValue = (date) =>
  [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join('-');

const toLocalDateBoundaryIso = (value, boundary) => {
  if (!value) return '';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(year, month - 1, day);
  if (boundary === 'end') {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

export function buildDefaultTaskFinderFilters(now = new Date()) {
  const to = new Date(now);
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    playerId: '',
    q: '',
    from: toDateInputValue(from),
    to: toDateInputValue(to),
    status: '',
    boosted: false,
    rewarded: false,
    anomalyOnly: false,
    sort: 'newest',
    limit: '25',
  };
}

const buildTaskFinderParams = (filters) => {
  const params = {
    from: toLocalDateBoundaryIso(filters.from, 'start'),
    to: toLocalDateBoundaryIso(filters.to, 'end'),
    sort: filters.sort || 'newest',
    limit: filters.limit || '25',
  };
  if (filters.playerId) params.playerId = filters.playerId.trim();
  if (filters.q) params.q = filters.q.trim();
  if (filters.status) params.status = filters.status;
  if (filters.boosted) params.boosted = 'true';
  if (filters.rewarded) params.rewarded = 'true';
  if (filters.anomalyOnly) params.anomalyOnly = 'true';
  return params;
};

const formatCount = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count)) return '0';
  return Math.round(count).toLocaleString('en-US');
};

const getTotalLaunchEvents = (report) =>
  Object.values(report?.totalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalLaunchVisitEvents = (report) =>
  Object.values(report?.visitTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalLaunchAuthEvents = (report) =>
  Object.values(report?.authTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalMagicLinkPlayerActionEvents = (report) =>
  Object.values(report?.magicLinkPlayerActionTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const getTotalShareFunnelEvents = (report) =>
  Object.values(report?.shareFunnelTotalsByEvent || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );

const hasDurableSourceAttribution = (row) =>
  !!(
    row?.source ||
    row?.src ||
    row?.partnerSource ||
    row?.partnerAction ||
    row?.shareId
  );

const getSourceLabel = (row) => {
  if (!hasDurableSourceAttribution(row)) return 'Unattributed';
  return row?.sourceKey || row?.src || row?.source || 'Unattributed';
};

const formatSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.regionSlug) parts.push(`region ${row.regionSlug}`);
  if (row?.marketSlug) parts.push(`market ${row.marketSlug}`);
  if (row?.outcome) parts.push(`outcome ${row.outcome}`);
  return parts.join(' / ');
};

const buildLaunchSourceRows = (report) => {
  const bySource = new Map();
  Object.entries(report?.eventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = getSourceLabel(row);
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatVisitSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.ctaId) parts.push(`cta ${row.ctaId}`);
  return parts.join(' / ');
};

const buildVisitSourceRows = (report) => {
  if (Array.isArray(report?.visitSourceBreakdown)) {
    return report.visitSourceBreakdown
      .map((row) => ({
        sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
        details: formatVisitSourceDetails(row),
        counts: row?.counts || {},
        total: Number(row?.total || 0),
      }))
      .sort(
        (a, b) =>
          b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
      );
  }

  const bySource = new Map();
  Object.entries(report?.visitEventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = row?.sourceKey || row?.src || row?.source || 'direct';
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatVisitSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatAuthSourceDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.returnToKind) parts.push(`return ${row.returnToKind}`);
  if (row?.playerState) parts.push(`player ${row.playerState}`);
  return parts.join(' / ');
};

const buildAuthSourceRows = (report) => {
  if (Array.isArray(report?.authSourceBreakdown)) {
    return report.authSourceBreakdown
      .map((row) => ({
        sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
        details: formatAuthSourceDetails(row),
        counts: row?.counts || {},
        total: Number(row?.total || 0),
      }))
      .sort(
        (a, b) =>
          b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
      );
  }

  const bySource = new Map();
  Object.entries(report?.authEventBreakdown || {}).forEach(([eventName, rows]) => {
    (rows || []).forEach((row) => {
      const sourceLabel = row?.sourceKey || row?.src || row?.source || 'direct';
      if (!bySource.has(sourceLabel)) {
        bySource.set(sourceLabel, {
          sourceLabel,
          details: new Set(),
          counts: {},
          total: 0,
        });
      }
      const current = bySource.get(sourceLabel);
      const count = Number(row?.count || 0);
      current.counts[eventName] = Number(current.counts[eventName] || 0) + count;
      current.total += count;
      const details = formatAuthSourceDetails(row);
      if (details) current.details.add(details);
    });
  });

  return Array.from(bySource.values())
    .map((row) => ({
      ...row,
      details: Array.from(row.details).slice(0, 2).join(' | '),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatMagicLinkPlayerActionDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.routeKind) parts.push(`route ${row.routeKind}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.returnToKind) parts.push(`return ${row.returnToKind}`);
  if (row?.acquisitionEventName) {
    parts.push(`via ${AUTH_EVENT_LABELS[row.acquisitionEventName] || row.acquisitionEventName}`);
  }
  return parts.join(' / ');
};

const buildMagicLinkPlayerActionRows = (report) => {
  if (!Array.isArray(report?.magicLinkPlayerActionSourceBreakdown)) return [];
  return report.magicLinkPlayerActionSourceBreakdown
    .map((row) => ({
      sourceLabel: row?.sourceKey || row?.src || row?.source || 'direct',
      details: formatMagicLinkPlayerActionDetails(row),
      counts: row?.counts || {},
      total: Number(row?.total || 0),
    }))
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const formatShareFunnelDetails = (row) => {
  const parts = [];
  if (row?.source) parts.push(`source ${row.source}`);
  if (row?.src) parts.push(`src ${row.src}`);
  if (row?.intent) parts.push(`intent ${row.intent}`);
  if (row?.shareType) parts.push(`share ${row.shareType}`);
  if (row?.shareId) parts.push(`id ${row.shareId}`);
  if (row?.failureReason) parts.push(`failure ${row.failureReason}`);
  return parts.join(' / ');
};

const cleanDisplayText = (value, max = 140) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const getShareFunnelTaskKey = (row) =>
  cleanDisplayText(row?.taskId || row?.shareId || '', 120);

const getShareFunnelTaskTitle = (row, taskTitleLookup) => {
  const explicitTitle = cleanDisplayText(
    row?.taskTitle ||
      row?.offeringTitle ||
      row?.title ||
      row?.task?.title ||
      row?.offering?.title ||
      row?.entityTitle ||
      row?.shareTitle,
  );
  if (explicitTitle) return explicitTitle;
  const taskKey = getShareFunnelTaskKey(row);
  return taskKey ? taskTitleLookup.get(taskKey) || '' : '';
};

const buildShareFunnelDisplayLabel = (row, taskTitle) =>
  [
    row?.sourceKey || row?.src || row?.source || 'direct',
    row?.shareType || '',
    taskTitle || row?.taskId || row?.shareId || '',
    row?.failureReason || '',
  ]
    .filter(Boolean)
    .join(' / ');

const toSafeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasOwnMetric = (source, key) =>
  !!source && Object.prototype.hasOwnProperty.call(source, key);

const trackedDisplayMetric = (value) => ({
  value: toSafeNumber(value),
  status: 'tracked',
  label: formatCount(value),
});

const notTrackedDisplayMetric = (reason) => ({
  value: null,
  status: 'notTracked',
  label: 'not tracked yet',
  reason,
});

const getLandingCtaRows = (report) => {
  const rows = report?.visitEventBreakdown?.localops_region_cta_clicked;
  return Array.isArray(rows) ? rows : [];
};

const buildLandingCtaCountState = (report) => {
  const rows = getLandingCtaRows(report);
  const knownCtaMetric =
    rows.length > 0 ||
    Array.isArray(report?.visitEventBreakdown?.localops_region_cta_clicked) ||
    hasOwnMetric(report?.visitTotalsByEvent, 'localops_region_cta_clicked');
  if (!knownCtaMetric) {
    return {
      supported: false,
      counts: new Map(),
    };
  }
  const counts = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const ctaId = cleanDisplayText(row?.ctaId, 80);
    if (!ctaId) return;
    counts.set(ctaId, toSafeNumber(counts.get(ctaId)) + toSafeNumber(row?.count));
  });
  return {
    supported: true,
    counts,
  };
};

const isLocationCtaId = (ctaId) =>
  String(ctaId || '').startsWith('localops_location_');

const getLocationRowActionContext = (row) =>
  cleanDisplayText(
    row?.landingIntentType ||
      row?.actionContext ||
      row?.intentType ||
      row?.intent ||
      '',
    80,
  );

const buildLocationFunnelRows = (report) => {
  const ctaState = buildLandingCtaCountState(report);
  const ctaRows = getLandingCtaRows(report);
  const hasLocationEvents = ctaRows.some((row) =>
    isLocationCtaId(row?.ctaId),
  );
  return LOCATION_FUNNEL_ROWS.map((row) => {
    if (!ctaState.supported) {
      return {
        ...row,
        hasLocationEvents: false,
        metric: notTrackedDisplayMetric(
          'Durable CTA breakdown is missing from the payload.',
        ),
        detail: 'not in payload',
      };
    }
    if (!hasLocationEvents) {
      return {
        ...row,
        hasLocationEvents: false,
        metric: notTrackedDisplayMetric('No location gate events recorded yet.'),
        detail: 'No location gate events recorded yet.',
      };
    }
    const count = ctaRows.reduce((sum, ctaRow) => {
      const ctaId = cleanDisplayText(ctaRow?.ctaId, 120);
      if (!(row.ctaIds || []).includes(ctaId)) return sum;
      if (row.actionContexts?.length) {
        const actionContext = getLocationRowActionContext(ctaRow);
        if (!row.actionContexts.includes(actionContext)) return sum;
      }
      return sum + toSafeNumber(ctaRow?.count);
    }, 0);
    return {
      ...row,
      hasLocationEvents: true,
      metric: trackedDisplayMetric(count),
      detail: row.detail,
    };
  });
};

const buildLandingChoiceFunnelRows = (report) => {
  const ctaState = buildLandingCtaCountState(report);
  const visitTotals = report?.visitTotalsByEvent || {};
  return LANDING_CHOICE_ROWS.map((row) => {
    if (row.source === 'durable') {
      const supported = hasOwnMetric(visitTotals, row.eventName);
      return {
        ...row,
        metric: supported
          ? trackedDisplayMetric(visitTotals[row.eventName])
          : notTrackedDisplayMetric('Durable visit metric is missing.'),
        detail: supported
          ? row.trackedDetail || 'durable launch event'
          : 'not in payload',
      };
    }
    if (row.source === 'cta') {
      if (!ctaState.supported) {
        return {
          ...row,
          metric: notTrackedDisplayMetric(
            'Durable CTA breakdown is missing from the payload.',
          ),
          detail: 'not in payload',
        };
      }
      const count = (row.ctaIds || []).reduce(
        (sum, ctaId) => sum + toSafeNumber(ctaState.counts.get(ctaId)),
        0,
      );
      return {
        ...row,
        metric: trackedDisplayMetric(count),
        detail: `durable CTA IDs: ${(row.ctaIds || []).join(', ')}`,
      };
    }
    return {
      ...row,
      metric: notTrackedDisplayMetric(row.reason),
      detail: row.reason,
    };
  });
};

const buildLandingChoiceSnapshot = (report) =>
  buildLandingChoiceFunnelRows(report).reduce((acc, row) => {
    const value = getMetricNumber(row.metric);
    if (value !== null) acc[row.key] = value;
    return acc;
  }, {});

const buildCreateSheetFunnelRows = (report) => {
  const ctaState = buildLandingCtaCountState(report);
  return CREATE_SHEET_FUNNEL_ROWS.map((row) => {
    if (!ctaState.supported) {
      return {
        ...row,
        metric: notTrackedDisplayMetric(
          'Durable CTA breakdown is missing from the payload.',
        ),
        detail: 'not in payload',
      };
    }
    const count = (row.ctaIds || []).reduce(
      (sum, ctaId) => sum + toSafeNumber(ctaState.counts.get(ctaId)),
      0,
    );
    return {
      ...row,
      metric: trackedDisplayMetric(count),
      detail: `durable create-sheet CTA IDs: ${(row.ctaIds || []).join(', ')}`,
    };
  });
};

const buildCreateSheetFunnelSnapshot = (report) =>
  buildCreateSheetFunnelRows(report).reduce((acc, row) => {
    const value = getMetricNumber(row.metric);
    if (value !== null) acc[row.key] = value;
    return acc;
  }, {});

const getCreateSheetJourneyDiagnostics = (report) =>
  report?.createSheetJourneyDiagnostics || null;

const buildCreateSheetJourneyRows = (report) => {
  const diagnostics = getCreateSheetJourneyDiagnostics(report);
  if (!diagnostics || diagnostics.source !== 'client_reported_tracking') {
    return CREATE_SHEET_JOURNEY_ROWS.map((row) => ({
      ...row,
      hasJourneyDiagnostics: false,
      metric: notTrackedDisplayMetric(
        'Create Sheet journey diagnostics are missing from the payload.',
      ),
      detail: 'not in payload',
    }));
  }
  const summary = diagnostics.flowSummary || {};
  return CREATE_SHEET_JOURNEY_ROWS.map((row) => {
    const value =
      row.key === 'uniqueFlows'
        ? diagnostics.uniqueFlows
        : summary[row.key];
    return {
      ...row,
      hasJourneyDiagnostics: true,
      metric: trackedDisplayMetric(toSafeNumber(value)),
      detail: `${row.detail}; client-reported tracking, not authoritative task state`,
    };
  });
};

const buildCreateSheetJourneySnapshot = (report) =>
  buildCreateSheetJourneyRows(report).reduce((acc, row) => {
    const value = getMetricNumber(row.metric);
    if (value !== null) acc[row.key] = value;
    return acc;
  }, {});

const buildLocationFunnelSnapshot = (report) =>
  buildLocationFunnelRows(report).reduce((acc, row) => {
    const value = getMetricNumber(row.metric);
    if (value !== null) acc[row.key] = value;
    return acc;
  }, {});

const countLandingCtas = (ctaState, ctaIds) =>
  (ctaIds || []).reduce(
    (sum, ctaId) => sum + toSafeNumber(ctaState.counts.get(ctaId)),
    0,
  );

const buildDemandSupplySignal = (report) => {
  const ctaState = buildLandingCtaCountState(report);
  if (!ctaState.supported) {
    return {
      signal: 'Not enough tracked data',
      tone: 'unknown',
      helpCount: null,
      earnCount: null,
      helpLabel: 'not tracked yet',
      earnLabel: 'not tracked yet',
      insight: 'Durable landing-choice CTA data is not available yet.',
      recommendation: 'Keep using the funnel rows until CTA tracking is durable.',
    };
  }

  const helpCount = countLandingCtas(ctaState, REQUESTER_CTA_IDS);
  const earnCount = countLandingCtas(ctaState, EARNER_CTA_IDS);
  const base = {
    helpCount,
    earnCount,
    helpLabel: `${formatCount(helpCount)} people clicked "I need help"`,
    earnLabel: `${formatCount(earnCount)} people clicked "I want to earn"`,
  };

  if (helpCount === 0 && earnCount === 0) {
    return {
      ...base,
      signal: 'No clear signal',
      tone: 'unknown',
      insight: 'Durable CTA counts are tracked, but both intent paths are at 0.',
      recommendation: 'Keep pushing traffic until one intent path shows movement.',
    };
  }

  if (earnCount >= Math.max(helpCount * 2, helpCount + 10)) {
    return {
      ...base,
      signal: 'Supply-heavy',
      tone: 'supply',
      insight: 'Helpers are showing up faster than requesters.',
      recommendation: 'Push requester/business task creation.',
    };
  }

  if (helpCount >= Math.max(earnCount * 2, earnCount + 10)) {
    return {
      ...base,
      signal: 'Demand-heavy',
      tone: 'demand',
      insight: 'Requesters are showing up faster than helpers.',
      recommendation: 'Push helper recruiting and task acceptance.',
    };
  }

  return {
    ...base,
    signal: 'Balanced / early',
    tone: 'balanced',
    insight: 'Requester and earner intent are close enough that no dominant imbalance is proven.',
    recommendation: 'Keep both requester and helper channels moving.',
  };
};

const resolveConversionRate = (conversion, key, numerator, denominator) => {
  const explicit = Number(conversion?.[key]);
  if (Number.isFinite(explicit)) return explicit;
  const safeDenominator = toSafeNumber(denominator);
  if (safeDenominator <= 0) return null;
  return Number((toSafeNumber(numerator) / safeDenominator).toFixed(4));
};

const buildShareFunnelRows = (report, taskTitleLookup = new Map()) => {
  if (!Array.isArray(report?.shareFunnelSourceBreakdown)) return [];
  return report.shareFunnelSourceBreakdown
    .map((row) => {
      const counts = row?.counts || {};
      const conversion = row?.conversion || {};
      const taskTitle = getShareFunnelTaskTitle(row, taskTitleLookup);
      const sourceLabel = [
        row?.sourceKey || row?.src || row?.source || 'direct',
        row?.shareType || '',
        row?.taskId || row?.offeringId || row?.shareId || '',
        row?.failureReason || '',
      ]
        .filter(Boolean)
        .join(' / ');
      return {
        taskId: row?.taskId || row?.shareId || '',
        offeringId: row?.offeringId || '',
        shareType: row?.shareType || '',
        sourceKey: row?.sourceKey || row?.src || row?.source || 'direct',
        sourceLabel,
        displayLabel: buildShareFunnelDisplayLabel(row, taskTitle),
        taskTitle,
        details: formatShareFunnelDetails(row),
        counts,
        total: Number(row?.total || 0),
        conversion: {
          ...conversion,
          landingToAcceptSuccessRate: resolveConversionRate(
            conversion,
            'landingToAcceptSuccessRate',
            counts.localops_share_task_accept_succeeded,
            counts.localops_share_landing_viewed,
          ),
          acceptSuccessToCompletionVerifiedRate: resolveConversionRate(
            conversion,
            'acceptSuccessToCompletionVerifiedRate',
            counts.localops_task_completion_verified,
            counts.localops_share_task_accept_succeeded,
          ),
          landingToCompletionVerifiedRate: resolveConversionRate(
            conversion,
            'landingToCompletionVerifiedRate',
            counts.localops_task_completion_verified,
            counts.localops_share_landing_viewed,
          ),
        },
      };
    })
    .sort(
      (a, b) =>
        b.total - a.total || a.sourceLabel.localeCompare(b.sourceLabel),
    );
};

const SOURCE_SHARE_PAGE_SIZE = 10;

const DEFAULT_SOURCE_SHARE_CONTROLS = {
  search: '',
  source: 'all',
  entityType: 'all',
  outcome: 'all',
  sort: 'actionable',
  page: 1,
};

const SOURCE_SHARE_OUTCOME_OPTIONS = [
  { value: 'all', label: 'All outcomes' },
  { value: 'completion', label: 'Completion' },
  { value: 'accept', label: 'Accepted' },
  { value: 'high_intent', label: 'High intent' },
  { value: 'traffic_only', label: 'Traffic only' },
  { value: 'low_signal', label: 'Low signal' },
];

const SOURCE_SHARE_SORT_OPTIONS = [
  { value: 'actionable', label: 'Actionable first' },
  { value: 'landings', label: 'Most landings' },
  { value: 'accepts', label: 'Most accepts' },
  { value: 'completed', label: 'Most completed' },
  { value: 'events', label: 'Most events' },
  { value: 'source', label: 'Source A-Z' },
];

const getShareFunnelEntityType = (row) => {
  const explicit = cleanDisplayText(row?.shareType || row?.entityType || '', 40)
    .toLowerCase();
  if (explicit.includes('offering')) return 'offering';
  if (explicit.includes('task')) return 'task';
  if (row?.offeringId) return 'offering';
  if (row?.taskId) return 'task';
  return 'unknown';
};

const getShareFunnelOutcomeKey = (row) => {
  const outcome = deriveSourceOutcome(row).label;
  if (outcome === 'Converts to completion') return 'completion';
  if (outcome === 'Converts to accept') return 'accept';
  if (outcome === 'High intent') return 'high_intent';
  if (outcome === 'Traffic only') return 'traffic_only';
  return 'low_signal';
};

const scoreShareFunnelActionability = (row) => {
  const counts = row?.counts || {};
  const completion = toSafeNumber(counts.localops_task_completion_verified);
  const actionCredit = toSafeNumber(counts.localops_action_credit_awarded);
  const acceptSuccess = toSafeNumber(counts.localops_share_task_accept_succeeded);
  const reserveSuccess = toSafeNumber(
    counts.localops_share_offering_reserve_succeeded,
  );
  const taskSheetOpens = toSafeNumber(counts.localops_share_task_sheet_opened);
  const offeringSheetOpens = toSafeNumber(
    counts.localops_share_offering_sheet_opened,
  );
  const acceptClicks = toSafeNumber(counts.localops_share_accept_clicked);
  const reserveClicks = toSafeNumber(counts.localops_share_offering_reserve_clicked);
  const landings = toSafeNumber(counts.localops_share_landing_viewed);

  if (completion > 0 || actionCredit > 0) return 20 + completion + actionCredit;
  if (acceptSuccess > 0 || reserveSuccess > 0) {
    return 16 + acceptSuccess + reserveSuccess;
  }
  if (taskSheetOpens > 0 || offeringSheetOpens > 0) {
    return 12 + taskSheetOpens + offeringSheetOpens;
  }
  if (acceptClicks > 0 || reserveClicks > 0) return 10 + acceptClicks + reserveClicks;
  if (landings > 0) return 6 + landings;
  return toSafeNumber(row?.total);
};

export const buildManagedShareFunnelRows = (
  shareFunnelRows = [],
  controls = DEFAULT_SOURCE_SHARE_CONTROLS,
) => {
  const current = { ...DEFAULT_SOURCE_SHARE_CONTROLS, ...(controls || {}) };
  const search = cleanDisplayText(current.search, 120).toLowerCase();
  const filtered = (shareFunnelRows || [])
    .map((row) => {
      const outcomeKey = getShareFunnelOutcomeKey(row);
      const entityType = getShareFunnelEntityType(row);
      return {
        ...row,
        entityType,
        outcomeKey,
        outcome: deriveSourceOutcome(row),
        actionabilityScore: scoreShareFunnelActionability(row),
      };
    })
    .filter((row) => {
      if (current.source !== 'all' && row.sourceKey !== current.source) {
        return false;
      }
      if (current.entityType !== 'all' && row.entityType !== current.entityType) {
        return false;
      }
      if (current.outcome !== 'all' && row.outcomeKey !== current.outcome) {
        return false;
      }
      if (!search) return true;
      const haystack = [
        row.displayLabel,
        row.sourceLabel,
        row.sourceKey,
        row.taskTitle,
        row.taskId,
        row.offeringId,
        row.details,
        row.failureReason,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });

  const sorted = [...filtered].sort((a, b) => {
    if (current.sort === 'landings') {
      return (
        toSafeNumber(b.counts?.localops_share_landing_viewed) -
          toSafeNumber(a.counts?.localops_share_landing_viewed) ||
        b.actionabilityScore - a.actionabilityScore ||
        a.sourceLabel.localeCompare(b.sourceLabel)
      );
    }
    if (current.sort === 'accepts') {
      return (
        toSafeNumber(b.counts?.localops_share_task_accept_succeeded) -
          toSafeNumber(a.counts?.localops_share_task_accept_succeeded) ||
        b.actionabilityScore - a.actionabilityScore ||
        a.sourceLabel.localeCompare(b.sourceLabel)
      );
    }
    if (current.sort === 'completed') {
      return (
        toSafeNumber(b.counts?.localops_task_completion_verified) -
          toSafeNumber(a.counts?.localops_task_completion_verified) ||
        toSafeNumber(b.counts?.localops_action_credit_awarded) -
          toSafeNumber(a.counts?.localops_action_credit_awarded) ||
        b.actionabilityScore - a.actionabilityScore ||
        a.sourceLabel.localeCompare(b.sourceLabel)
      );
    }
    if (current.sort === 'events') {
      return (
        toSafeNumber(b.total) - toSafeNumber(a.total) ||
        a.sourceLabel.localeCompare(b.sourceLabel)
      );
    }
    if (current.sort === 'source') {
      return a.sourceLabel.localeCompare(b.sourceLabel);
    }
    return (
      b.actionabilityScore - a.actionabilityScore ||
      toSafeNumber(b.total) - toSafeNumber(a.total) ||
      a.sourceLabel.localeCompare(b.sourceLabel)
    );
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / SOURCE_SHARE_PAGE_SIZE));
  const requestedPage = Number(current.page) || 1;
  const page = Math.min(Math.max(1, requestedPage), totalPages);
  const start = (page - 1) * SOURCE_SHARE_PAGE_SIZE;
  return {
    rows: sorted,
    pageRows: sorted.slice(start, start + SOURCE_SHARE_PAGE_SIZE),
    totalRows: sorted.length,
    totalPages,
    page,
    start,
    end: Math.min(start + SOURCE_SHARE_PAGE_SIZE, sorted.length),
  };
};

const addTaskTitleLookupEntry = (lookup, task) => {
  if (!task || typeof task !== 'object') return;
  const title = cleanDisplayText(
    task.title ||
      task.taskTitle ||
      task.offeringTitle ||
      task.name ||
      task.label,
  );
  if (!title) return;
  [
    task.taskId,
    task.offeringId,
    task.id,
    task._id,
    task.shareId,
    task.entityId,
  ].forEach((id) => {
    const key = cleanDisplayText(id, 120);
    if (key && !lookup.has(key)) lookup.set(key, title);
  });
};

const buildTaskTitleLookup = ({
  launchAttribution,
  operatorOfferingRows,
  operatorTaskRows,
  taskResults,
  taskReport,
} = {}) => {
  const lookup = new Map();
  (operatorTaskRows || []).forEach((task) =>
    addTaskTitleLookupEntry(lookup, task),
  );
  (operatorOfferingRows || []).forEach((offering) =>
    addTaskTitleLookupEntry(lookup, offering),
  );
  (taskResults || []).forEach((task) => addTaskTitleLookupEntry(lookup, task));
  addTaskTitleLookupEntry(lookup, taskReport?.task || taskReport);
  (launchAttribution?.shareFunnelSourceBreakdown || []).forEach((row) => {
    const title = cleanDisplayText(
      row?.taskTitle ||
        row?.offeringTitle ||
        row?.title ||
        row?.task?.title ||
        row?.offering?.title ||
        row?.entityTitle ||
        row?.shareTitle,
    );
    if (!title) return;
    [row?.taskId, row?.shareId].forEach((id) => {
      const key = cleanDisplayText(id, 120);
      if (key && !lookup.has(key)) lookup.set(key, title);
    });
  });
  return lookup;
};

const LAUNCH_DELTA_GROUPS = [
  {
    key: 'visitTotalsByEvent',
    label: 'Visit funnel',
    labels: VISIT_EVENT_LABELS,
  },
  {
    key: 'authTotalsByEvent',
    label: 'Magic-link auth',
    labels: AUTH_EVENT_LABELS,
  },
  {
    key: 'magicLinkPlayerActionTotalsByEvent',
    label: 'Magic-link player actions',
    labels: LAUNCH_EVENT_LABELS,
  },
  {
    key: 'shareFunnelTotalsByEvent',
    label: 'Share funnel',
    labels: SHARE_FUNNEL_EVENT_LABELS,
  },
  {
    key: 'totalsByEvent',
    label: 'Canonical task-source events',
    labels: LAUNCH_EVENT_LABELS,
  },
  {
    key: 'landingChoiceFunnel',
    label: 'Landing Choice Funnel',
    labels: LANDING_CHOICE_ROWS.reduce((acc, row) => {
      acc[row.key] = row.label;
      return acc;
    }, {}),
  },
  {
    key: 'createSheetFunnel',
    label: 'Create Sheet Funnel',
    labels: CREATE_SHEET_FUNNEL_LABELS,
  },
  {
    key: 'createSheetJourney',
    label: 'Create Sheet Journey Diagnostics',
    labels: CREATE_SHEET_JOURNEY_LABELS,
  },
  {
    key: 'locationFunnel',
    label: 'Location Permission / Fresh Saved Location Funnel',
    labels: LOCATION_FUNNEL_LABELS,
  },
];

const LAUNCH_SOURCE_DELTA_GROUPS = [
  {
    key: 'visitSourceBreakdown',
    label: 'Visit source',
    labels: VISIT_EVENT_LABELS,
    buildRows: buildVisitSourceRows,
  },
  {
    key: 'authSourceBreakdown',
    label: 'Magic-link auth source',
    labels: AUTH_EVENT_LABELS,
    buildRows: buildAuthSourceRows,
  },
  {
    key: 'magicLinkPlayerActionSourceBreakdown',
    label: 'Magic-link player-action source',
    labels: LAUNCH_EVENT_LABELS,
    buildRows: buildMagicLinkPlayerActionRows,
  },
  {
    key: 'shareFunnelSourceBreakdown',
    label: 'Share source',
    labels: SHARE_FUNNEL_EVENT_LABELS,
    buildRows: buildShareFunnelRows,
  },
  {
    key: 'eventBreakdown',
    label: 'Canonical source',
    labels: LAUNCH_EVENT_LABELS,
    buildRows: buildLaunchSourceRows,
  },
];

const LAUNCH_SUMMARY_DELTA_FIELDS = [
  {
    path: ['magicLinkPlayerActionCoverage', 'attributed'],
    key: 'magicLinkPlayerActionCoverage:attributed',
    group: 'Magic-link player actions',
    label: 'Matched player actions',
  },
  {
    path: ['magicLinkPlayerActionCoverage', 'total'],
    key: 'magicLinkPlayerActionCoverage:total',
    group: 'Magic-link player actions',
    label: 'Total player actions',
  },
  {
    path: ['unattributedCanonicalEventCount'],
    key: 'unattributedCanonicalEventCount',
    group: 'Canonical task-source events',
    label: 'Unattributed canonical events',
  },
  {
    path: ['actionCreditJoinCoverage', 'attributed'],
    key: 'actionCreditJoinCoverage:attributed',
    group: 'Canonical task-source events',
    label: 'Source to Action Credit',
  },
];

const getPathNumber = (item, path) =>
  Number(path.reduce((current, part) => current?.[part], item) || 0);

const normalizeDeltaNumber = (value) => {
  if (value === null) return null;
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const addDelta = (deltas, item) => {
  const before = normalizeDeltaNumber(item.before);
  const after = normalizeDeltaNumber(item.after);
  if (before === null || after === null) return;
  const delta = after - before;
  if (!delta) return;
  deltas.push({
    ...item,
    before,
    after,
    delta,
  });
};

export const buildLaunchAttributionDeltas = (previous, next) => {
  if (!previous || !next) return [];
  const deltas = [];
  LAUNCH_DELTA_GROUPS.forEach((group) => {
    const previousCounts = previous?.[group.key] || {};
    const nextCounts = next?.[group.key] || {};
    const keys = new Set([
      ...Object.keys(previousCounts),
      ...Object.keys(nextCounts),
    ]);
    keys.forEach((eventName) => {
      addDelta(deltas, {
        key: `${group.key}:${eventName}`,
        type: 'metric',
        sectionKey: group.key,
        eventName,
        group: group.label,
        label: group.labels[eventName] || eventName,
        before: previousCounts[eventName],
        after: nextCounts[eventName],
      });
    });
  });

  LAUNCH_SOURCE_DELTA_GROUPS.forEach((group) => {
    const previousRows = new Map(
      group.buildRows(previous).map((row) => [row.sourceLabel, row]),
    );
    const nextRows = new Map(
      group.buildRows(next).map((row) => [row.sourceLabel, row]),
    );
    const sourceLabels = new Set([
      ...previousRows.keys(),
      ...nextRows.keys(),
    ]);
    sourceLabels.forEach((sourceLabel) => {
      const previousRow = previousRows.get(sourceLabel) || { counts: {} };
      const nextRow = nextRows.get(sourceLabel) || { counts: {} };
      const eventNames = new Set([
        ...Object.keys(previousRow.counts || {}),
        ...Object.keys(nextRow.counts || {}),
      ]);
      eventNames.forEach((eventName) => {
        addDelta(deltas, {
          key: `${group.key}:${sourceLabel}:${eventName}`,
          type: 'source-cell',
          sectionKey: group.key,
          sourceLabel,
          eventName,
          group: group.label,
          label: `${group.labels[eventName] || eventName} from ${sourceLabel}`,
          before: previousRow.counts?.[eventName],
          after: nextRow.counts?.[eventName],
        });
      });
      addDelta(deltas, {
        key: `${group.key}:${sourceLabel}:total`,
        type: 'source-row',
        sectionKey: group.key,
        sourceLabel,
        group: group.label,
        label: `Total from ${sourceLabel}`,
        before: previousRow.total,
        after: nextRow.total,
      });
    });
  });

  LAUNCH_SUMMARY_DELTA_FIELDS.forEach((field) => {
    addDelta(deltas, {
      key: field.key,
      type: 'summary',
      group: field.group,
      label: field.label,
      before: getPathNumber(previous, field.path),
      after: getPathNumber(next, field.path),
    });
  });

  return deltas.sort((a, b) => {
    const sectionCompare = String(a.group).localeCompare(String(b.group));
    if (sectionCompare) return sectionCompare;
    return Math.abs(b.delta) - Math.abs(a.delta);
  });
};

const buildDeltaLookup = (items = []) => {
  const byKey = new Map();
  items.forEach((item) => byKey.set(item.key, item));
  return byKey;
};

const LIVE_DELTA_FLASH_MS = 10000;

const markLiveDeltas = (items = []) =>
  items.map((item) => ({
    ...item,
    live: true,
  }));

const getMetricDelta = (lookup, sectionKey, eventName) =>
  lookup.get(`${sectionKey}:${eventName}`);

const getSourceCellDelta = (lookup, sectionKey, sourceLabel, eventName) =>
  lookup.get(`${sectionKey}:${sourceLabel}:${eventName}`);

const getSourceRowDelta = (lookup, sectionKey, sourceLabel) =>
  lookup.get(`${sectionKey}:${sourceLabel}:total`);

const getSummaryDelta = (lookup, key) => lookup.get(key);

const getTriageCardDelta = (card, launchDeltaLookup, operatorDeltaLookup) => {
  if (!card?.deltaKey) return null;
  if (card.deltaSource === 'operator') {
    return getSummaryDelta(operatorDeltaLookup, card.deltaKey);
  }
  return getSummaryDelta(launchDeltaLookup, card.deltaKey);
};

const getMetricNumber = (value) => {
  if (value && typeof value === 'object' && value.status === 'notTracked') {
    return null;
  }
  if (value && typeof value === 'object' && 'value' in value) {
    return toSafeNumber(value.value);
  }
  return toSafeNumber(value);
};

const getPathMetricNumber = (item, path) =>
  getMetricNumber(path.reduce((current, part) => current?.[part], item));

const OPERATOR_SUMMARY_DELTA_FIELDS = [
  ['systemSummary', 'totalLocalPlayers'],
  ['systemSummary', 'newLocalPlayers7d'],
  ['systemSummary', 'reachablePlayers'],
  ['systemSummary', 'mapReadyPlayers'],
  ['systemSummary', 'taskEligiblePlayers'],
  ['systemSummary', 'verifiedLocalPlayers'],
  ['systemSummary', 'openFundedTasks'],
  ['systemSummary', 'acceptedTasks'],
  ['systemSummary', 'completedTasks'],
  ['systemSummary', 'availableOfferings'],
  ['systemSummary', 'actionCreditIssued'],
  ['systemSummary', 'sourceActivityRows'],
  ['activationFunnel', 'magicLinksRequested'],
  ['activationFunnel', 'magicLinksCompleted'],
  ['activationFunnel', 'playerMapArrivals'],
  ['activationFunnel', 'locationSaved'],
  ['activationFunnel', 'firstTaskCreated'],
  ['activationFunnel', 'firstOfferingCreated'],
  ['locationMapReadiness', 'saved'],
  ['locationMapReadiness', 'missing'],
  ['locationMapReadiness', 'stale'],
];

const OPERATOR_ROW_DELTA_GROUPS = [
  {
    rowsKey: 'activePlayerRows',
    idKey: 'playerId',
    keyPrefix: 'activePlayerRows',
    fields: [
      ['actionCreditCount'],
      ['magicLink', 'loginCount'],
      ['activationEmail', 'sendCount'],
    ],
  },
  {
    rowsKey: 'taskRows',
    idKey: 'taskId',
    keyPrefix: 'taskRows',
    fields: [
      ['reopenCount'],
      ['helperReleaseCount'],
      ['fanout', 'emailSentCount'],
      ['fanout', 'emailPixelOpens'],
      ['fanout', 'ownerReNotifyCount'],
      ['fanout', 'lastOwnerReNotifyRecipientCount'],
      ['fanout', 'lastOwnerReNotifyEmailSentCount'],
      ['raisePay', 'renotifyRecipientCount'],
      ['raisePay', 'renotifyEmailSentCount'],
    ],
  },
  {
    rowsKey: 'offeringRows',
    idKey: 'offeringId',
    keyPrefix: 'offeringRows',
    fields: [
      ['reservations'],
      ['fulfilledOrders'],
      ['shareLandings'],
    ],
  },
  {
    rowsKey: 'sourceAttributionRows',
    idKey: 'sourceKey',
    keyPrefix: 'sourceAttributionRows',
    fields: [['total']],
  },
  {
    rowsKey: 'emailNotificationProofRows',
    idKey: 'proofId',
    fallbackIdKey: 'notificationId',
    keyPrefix: 'emailNotificationProofRows',
    fields: [['pixelOpenCount']],
  },
];

const buildOperatorRowMap = (rows = [], idKey, fallbackIdKey) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = row?.[idKey] || (fallbackIdKey ? row?.[fallbackIdKey] : '');
    if (key) map.set(String(key), row);
  });
  return map;
};

export const buildOperatorVisibilityDeltas = (previous, next) => {
  if (!previous || !next) return [];
  const deltas = [];
  OPERATOR_SUMMARY_DELTA_FIELDS.forEach((path) => {
    addDelta(deltas, {
      key: `operator:${path.join('.')}`,
      group: 'LocalOps Operator Visibility',
      label: path.join('.'),
      before: getPathMetricNumber(previous, path),
      after: getPathMetricNumber(next, path),
    });
  });

  OPERATOR_ROW_DELTA_GROUPS.forEach((group) => {
    const previousRows = buildOperatorRowMap(
      previous?.[group.rowsKey],
      group.idKey,
      group.fallbackIdKey,
    );
    const nextRows = buildOperatorRowMap(
      next?.[group.rowsKey],
      group.idKey,
      group.fallbackIdKey,
    );
    const rowIds = new Set([...previousRows.keys(), ...nextRows.keys()]);
    rowIds.forEach((rowId) => {
      const previousRow = previousRows.get(rowId) || {};
      const nextRow = nextRows.get(rowId) || {};
      group.fields.forEach((fieldPath) => {
        addDelta(deltas, {
          key: `operator:${group.keyPrefix}:${rowId}:${fieldPath.join('.')}`,
          group: 'LocalOps Operator Visibility',
          label: fieldPath.join('.'),
          before: getPathMetricNumber(previousRow, fieldPath),
          after: getPathMetricNumber(nextRow, fieldPath),
        });
      });
    });
  });

  return deltas;
};

const formatDeltaValue = (delta) => {
  if (!delta) return '';
  if (delta.delta > 0) return `+${formatCount(delta.delta)}`;
  return 'Updated';
};

const shouldHighlightDelta = (delta) => !!delta;

const formatShortId = (value) => {
  const text = cleanDisplayText(value, 80);
  if (!text) return '';
  if (text.length <= 14) return text;
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
};

const EntityDebugId = ({ label, value }) => {
  const shortId = formatShortId(value);
  if (!shortId) return null;
  return (
    <small className="ops-health-entity-id">
      {label} {shortId}
    </small>
  );
};

const getPlayerDisplayLabel = (row) =>
  cleanDisplayText(
    row?.handle ||
      row?.playerId ||
      row?.player?.playerId ||
      row?.displayName ||
      'Unknown player',
  );

const getTaskDisplayTitle = (row) =>
  cleanDisplayText(
    row?.title ||
      row?.taskTitle ||
      row?.task?.title ||
      row?.entityTitle ||
      'Unknown task',
  );

const getOfferingDisplayTitle = (row) =>
  cleanDisplayText(
    row?.title ||
      row?.offeringTitle ||
      row?.offering?.title ||
      row?.entityTitle ||
      'Unknown offering',
  );

const formatActionCreditCoverage = (coverage) => {
  const total = Number(coverage?.total || 0);
  if (!total) return 'No Action Credit events';
  const attributed = Number(coverage?.attributed || 0);
  const rawCoverage = Number(coverage?.coverage);
  const percent = Number.isFinite(rawCoverage)
    ? `${(rawCoverage * 100).toFixed(1).replace(/\.0$/, '')}%`
    : 'Unknown';
  return `${formatCount(attributed)} of ${formatCount(total)} attributed (${percent})`;
};

const formatConversionPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '--';
  return `${(parsed * 100).toFixed(1).replace(/\.0$/, '')}%`;
};

const formatConversionWithFraction = (value, numerator, denominator) => {
  const safeNumerator = toSafeNumber(numerator);
  const safeDenominator = toSafeNumber(denominator);
  if (safeDenominator <= 0) return '--';
  return `${formatConversionPercent(value)} (${formatCount(safeNumerator)} / ${formatCount(safeDenominator)})`;
};

const addReadinessAnomalies = (items, readiness) => {
  const summary = readiness?.summary || {};
  (summary.failures || []).forEach((failure) => {
    items.push({
      source: 'readiness',
      severity: 'error',
      code: failure,
      message: failure,
    });
  });
  (summary.warnings || []).forEach((warning) => {
    items.push({
      source: 'readiness',
      severity: 'warning',
      code: warning,
      message: warning,
    });
  });
};

const addRollupAnomalies = (items, rollup) => {
  const duplicateKeys = rollup?.duplicateIdempotencyKeys || [];
  duplicateKeys.forEach((key) => {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'duplicate_idempotency_key',
      message: `Duplicate idempotency key: ${key}`,
    });
  });

  const missingRefs = Number(rollup?.missingSourceTransactionRefCount || 0);
  if (missingRefs > 0) {
    items.push({
      source: 'rollup',
      severity: 'warning',
      code: 'missing_source_transaction_ref',
      message: `${missingRefs} journal entries are missing source transaction references.`,
    });
  }
};

const addTaskAnomalies = (items, taskReport) => {
  (taskReport?.anomalies || []).forEach((item) => {
    items.push({
      source: 'task',
      severity: item.severity || 'warning',
      code: item.code || 'task_anomaly',
      message: item.message || item.code || 'Task accounting anomaly',
    });
  });
};

export function collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }) {
  const items = [];
  addReadinessAnomalies(items, readiness);
  addRollupAnomalies(items, rollup);
  addTaskAnomalies(items, taskReport);
  return items;
}

export function deriveLocalOpsOpsStatus({ readiness, rollup, anomalies, error }) {
  if (error) return 'red';
  if (!readiness || !rollup) return 'idle';
  if (readiness?.summary?.pass === false || rollup?.pass === false) return 'red';
  if ((anomalies || []).some((item) => item.severity === 'error')) return 'red';
  if ((anomalies || []).length > 0) return 'yellow';
  return 'green';
}

const DeltaBadge = ({ delta }) =>
  delta ? (
    <em
      className={`ops-health-delta-badge${
        delta.delta < 0 ? ' ops-health-delta-badge--neutral' : ''
      }${
        delta.live ? ' ops-health-delta-badge--live' : ''
      }`}
    >
      {formatDeltaValue(delta)}
    </em>
  ) : null;

const isTypedMetric = (value) =>
  value &&
  typeof value === 'object' &&
  Object.prototype.hasOwnProperty.call(value, 'status') &&
  Object.prototype.hasOwnProperty.call(value, 'label');

const getMetricValue = (value) => {
  if (isTypedMetric(value)) return value.label;
  if (value === undefined || value === null || value === '') return '--';
  return value;
};

const Metric = ({ label, value, delta }) => (
  <div
    className={`ops-health-metric${
      shouldHighlightDelta(delta) ? ' ops-health-metric--changed' : ''
    }${
      delta?.live ? ' ops-health-metric--live-changed' : ''
    }${
      isTypedMetric(value) && value.status === 'notTracked'
        ? ' ops-health-metric--not-tracked'
        : ''
    }`}
  >
    <span>{label}</span>
    <strong>{getMetricValue(value)}</strong>
    <DeltaBadge delta={delta} />
  </div>
);

const OperatorField = ({ label, value, delta, className = '' }) => (
  <span
    className={`ops-health-operator-field${
      shouldHighlightDelta(delta) ? ' ops-health-operator-field--changed' : ''
    }${
      delta?.live ? ' ops-health-operator-field--live-changed' : ''
    }${className ? ` ${className}` : ''}`}
  >
    <small>{label}</small>
    <strong title={cleanDisplayText(getMetricValue(value), 240)}>
      {getMetricValue(value)}
      <DeltaBadge delta={delta} />
    </strong>
  </span>
);

const OPEN_TASK_STATUSES = new Set([
  'created',
  'discoverable',
  'open',
  'published',
  'funded',
  'available',
]);

const ACCEPTED_TASK_STATUSES = new Set(['accepted', 'in_progress', 'started']);
const COMPLETED_TASK_STATUSES = new Set(['completed', 'verified', 'paid']);
const TERMINAL_NON_ACTIONABLE_TASK_STATUSES = new Set([
  'canceled',
  'cancelled',
  'closed',
  'expired',
  'archived',
  'void',
]);

const getTrackedMetricNumber = (value) => {
  if (isTypedMetric(value) && value.status === 'notTracked') return null;
  if (value === undefined || value === null || value === '') return null;
  const rawValue = isTypedMetric(value) ? value.value : value;
  if (rawValue === undefined || rawValue === null || rawValue === '') return null;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const cleanSnapshotText = (value, max = 120) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const stableSnapshotHash = (value) => {
  const text = cleanSnapshotText(value, 512);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const buildSnapshotFilterHash = (filters = {}) => {
  const normalized = Object.keys(filters || {})
    .sort()
    .reduce((acc, key) => {
      const value = cleanSnapshotText(filters[key], 80);
      if (value) acc[key] = value;
      return acc;
    }, {});
  return stableSnapshotHash(JSON.stringify(normalized));
};

export const buildOpsHealthSnapshotKey = ({
  reportType = 'unknown',
  filters = {},
} = {}) => {
  const normalizedReportType = cleanSnapshotText(reportType, 80) || 'unknown';
  const marketSlug =
    cleanSnapshotText(filters.marketSlug || filters.market, 80) || 'camden';
  const regionSlug =
    cleanSnapshotText(filters.regionSlug || filters.region || marketSlug, 80) ||
    'camden';
  return [
    OPS_HEALTH_SNAPSHOT_PREFIX,
    normalizedReportType,
    marketSlug,
    regionSlug,
    buildSnapshotFilterHash(filters),
  ].join(':');
};

export const getOpsSnapshotRowKey = (value) => {
  const cleaned = cleanSnapshotText(value, 240);
  return cleaned ? `h_${stableSnapshotHash(cleaned)}` : '';
};

const toSnapshotCount = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSnapshotMetric = (value) => {
  if (isTypedMetric(value)) {
    if (value.status === 'notTracked') {
      return {
        status: 'notTracked',
        label: cleanSnapshotText(value.label || 'not tracked', 80),
      };
    }
    const metricValue = getTrackedMetricNumber(value);
    return metricValue === null ? undefined : { value: metricValue };
  }
  const parsed = getTrackedMetricNumber(value);
  return parsed === null ? undefined : parsed;
};

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, item]) => {
    if (item !== undefined && item !== null && item !== '') acc[key] = item;
    return acc;
  }, {});

const pickMetricTree = (source = {}, fields = []) =>
  compactObject(
    fields.reduce((acc, field) => {
      const value = toSnapshotMetric(source?.[field]);
      if (value !== undefined) acc[field] = value;
      return acc;
    }, {}),
  );

const pickCounts = (counts = {}) =>
  Object.entries(counts || {}).reduce((acc, [eventName, value]) => {
    const safeEventName = cleanSnapshotText(eventName, 120);
    if (safeEventName) acc[safeEventName] = toSnapshotCount(value);
    return acc;
  }, {});

const pickConversion = (conversion = {}) =>
  Object.entries(conversion || {}).reduce((acc, [key, value]) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) acc[cleanSnapshotText(key, 120)] = parsed;
    return acc;
  }, {});

const limitSnapshotRows = (rows = []) =>
  Array.isArray(rows) ? rows.slice(0, OPS_HEALTH_MAX_SNAPSHOT_ROWS) : [];

const buildLaunchSourceSnapshotRows = (rows = [], options = {}) =>
  limitSnapshotRows(rows).map((row) => {
    const snapshotRow = {
      sourceKey: cleanSnapshotText(row?.sourceKey || row?.src || row?.source),
      source: cleanSnapshotText(row?.source),
      src: cleanSnapshotText(row?.src),
      routeKind: cleanSnapshotText(row?.routeKind),
      intent: cleanSnapshotText(row?.intent),
      returnToKind: cleanSnapshotText(row?.returnToKind),
      playerState: cleanSnapshotText(row?.playerState),
      acquisitionEventName: cleanSnapshotText(row?.acquisitionEventName),
      shareType: cleanSnapshotText(row?.shareType),
      shareId: options.keepShareKeys ? cleanSnapshotText(row?.shareId) : '',
      taskId: options.keepShareKeys ? cleanSnapshotText(row?.taskId) : '',
      failureReason: cleanSnapshotText(row?.failureReason),
      counts: pickCounts(row?.counts),
      total: toSnapshotCount(row?.total),
      conversion: pickConversion(row?.conversion),
    };
    if (row?.count !== undefined && row?.count !== null && row?.count !== '') {
      snapshotRow.count = toSnapshotCount(row.count);
    }
    return compactObject(snapshotRow);
  });

export const buildLaunchAttributionSnapshot = (report = {}) => ({
  landingChoiceFunnel: buildLandingChoiceSnapshot(report),
  createSheetFunnel: buildCreateSheetFunnelSnapshot(report),
  createSheetJourney: buildCreateSheetJourneySnapshot(report),
  locationFunnel: buildLocationFunnelSnapshot(report),
  visitTotalsByEvent: pickCounts(report?.visitTotalsByEvent),
  authTotalsByEvent: pickCounts(report?.authTotalsByEvent),
  magicLinkPlayerActionTotalsByEvent: pickCounts(
    report?.magicLinkPlayerActionTotalsByEvent,
  ),
  shareFunnelTotalsByEvent: pickCounts(report?.shareFunnelTotalsByEvent),
  totalsByEvent: pickCounts(report?.totalsByEvent),
  visitSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.visitSourceBreakdown,
  ),
  authSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.authSourceBreakdown,
  ),
  magicLinkPlayerActionSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.magicLinkPlayerActionSourceBreakdown,
  ),
  shareFunnelSourceBreakdown: buildLaunchSourceSnapshotRows(
    report?.shareFunnelSourceBreakdown,
    { keepShareKeys: true },
  ),
  eventBreakdown: Object.entries(report?.eventBreakdown || {}).reduce(
    (acc, [eventName, rows]) => {
      const safeEventName = cleanSnapshotText(eventName, 120);
      if (safeEventName) {
        acc[safeEventName] = buildLaunchSourceSnapshotRows(rows);
      }
      return acc;
    },
    {},
  ),
  magicLinkPlayerActionCoverage: pickMetricTree(
    report?.magicLinkPlayerActionCoverage,
    ['attributed', 'total', 'unmatched'],
  ),
  actionCreditJoinCoverage: pickMetricTree(report?.actionCreditJoinCoverage, [
    'attributed',
    'total',
    'unattributed',
  ]),
  unattributedCanonicalEventCount: toSnapshotCount(
    report?.unattributedCanonicalEventCount,
  ),
});

const buildOperatorRowSnapshot = (rows = [], idField, fields, nested = {}) =>
  limitSnapshotRows(rows)
    .map((row) => {
      const id = getOpsSnapshotRowKey(row?.[idField]);
      if (!id) return null;
      const snapshot = { [idField]: id };
      fields.forEach((field) => {
        const value = toSnapshotMetric(row?.[field]);
        if (value !== undefined) snapshot[field] = value;
      });
      Object.entries(nested).forEach(([nestedKey, nestedFields]) => {
        const nestedSnapshot = pickMetricTree(row?.[nestedKey], nestedFields);
        if (Object.keys(nestedSnapshot).length) {
          snapshot[nestedKey] = nestedSnapshot;
        }
      });
      return snapshot;
    })
    .filter(Boolean);

export const buildOperatorVisibilitySnapshot = (report = {}) => ({
  systemSummary: pickMetricTree(report?.systemSummary, [
    'totalLocalPlayers',
    'newLocalPlayers7d',
    'reachablePlayers',
    'mapReadyPlayers',
    'taskEligiblePlayers',
    'verifiedLocalPlayers',
    'openFundedTasks',
    'acceptedTasks',
    'completedTasks',
    'availableOfferings',
    'actionCreditIssued',
    'sourceActivityRows',
  ]),
  activationFunnel: pickMetricTree(report?.activationFunnel, [
    'magicLinksRequested',
    'magicLinksCompleted',
    'playerMapArrivals',
    'locationSaved',
    'firstTaskCreated',
    'firstOfferingCreated',
  ]),
  locationMapReadiness: pickMetricTree(report?.locationMapReadiness, [
    'saved',
    'missing',
    'stale',
  ]),
  activePlayerRows: buildOperatorRowSnapshot(
    report?.activePlayerRows,
    'playerId',
    ['actionCreditCount'],
    {
      magicLink: ['loginCount'],
      activationEmail: ['sendCount'],
    },
  ),
  taskRows: buildOperatorRowSnapshot(
    report?.taskRows,
    'taskId',
    ['reopenCount', 'helperReleaseCount'],
    {
      fanout: [
        'emailSentCount',
        'emailPixelOpens',
        'ownerReNotifyCount',
        'lastOwnerReNotifyRecipientCount',
        'lastOwnerReNotifyEmailSentCount',
      ],
      raisePay: [
        'renotifyRecipientCount',
        'renotifyEmailSentCount',
      ],
    },
  ),
  offeringRows: buildOperatorRowSnapshot(report?.offeringRows, 'offeringId', [
    'reservations',
    'fulfilledOrders',
    'shareLandings',
  ]),
  sourceAttributionRows: limitSnapshotRows(report?.sourceAttributionRows).map(
    (row) =>
      compactObject({
        sourceKey: cleanSnapshotText(row?.sourceKey || row?.src || row?.source),
        total: toSnapshotMetric(row?.total),
      }),
  ),
  emailNotificationProofRows: limitSnapshotRows(
    report?.emailNotificationProofRows,
  )
    .map((row) => {
      const proofKey = getOpsSnapshotRowKey(
        row?.proofId || row?.notificationId,
      );
      if (!proofKey) return null;
      return compactObject({
        proofId: proofKey,
        notificationId: proofKey,
        pixelOpenCount: toSnapshotMetric(row?.pixelOpenCount),
      });
    })
    .filter(Boolean),
});

const parseOpsHealthDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeOpsHealthReportScope = (filters = {}) => {
  const marketSlug = cleanSnapshotText(
    filters.marketSlug || filters.market,
    80,
  ).toLowerCase();
  const regionSlug = cleanSnapshotText(
    filters.regionSlug || filters.region || marketSlug,
    80,
  ).toLowerCase();
  const defaultWindowDays = Number(filters.defaultWindowDays);
  return compactObject({
    regionSlug,
    marketSlug,
    defaultWindowDays:
      Number.isFinite(defaultWindowDays) && defaultWindowDays > 0
        ? defaultWindowDays
        : undefined,
  });
};

const buildOpsHealthReportWindow = (filters = {}) => {
  const fromDate = parseOpsHealthDate(filters.from);
  const toDate = parseOpsHealthDate(filters.to);
  return {
    from: fromDate ? fromDate.toISOString() : cleanSnapshotText(filters.from),
    to: toDate ? toDate.toISOString() : cleanSnapshotText(filters.to),
    durationMs:
      fromDate && toDate ? toDate.getTime() - fromDate.getTime() : null,
  };
};

export const buildOpsHealthSnapshotEnvelope = ({
  reportType,
  report,
  snapshot,
  capturedAt,
} = {}) => ({
  version: OPS_HEALTH_SNAPSHOT_VERSION,
  reportType: cleanSnapshotText(reportType, 80),
  capturedAt: cleanSnapshotText(capturedAt, 80),
  reportGeneratedAt: cleanSnapshotText(report?.generatedAt, 80),
  reportScope: normalizeOpsHealthReportScope(report?.filters),
  reportWindow: buildOpsHealthReportWindow(report?.filters),
  snapshot,
});

const validateOpsHealthSnapshotEnvelope = (
  envelope,
  { expectedReportType = '', expectedScope = {}, nowMs = Date.now() } = {},
) => {
  if (!envelope || envelope.version !== OPS_HEALTH_SNAPSHOT_VERSION) {
    return { valid: false, reason: 'baseline_invalid' };
  }
  if (!envelope.snapshot || typeof envelope.snapshot !== 'object') {
    return { valid: false, reason: 'baseline_invalid' };
  }
  const capturedAt = parseOpsHealthDate(envelope.capturedAt);
  const generatedAt = parseOpsHealthDate(envelope.reportGeneratedAt);
  if (
    !capturedAt ||
    capturedAt.getTime() > nowMs + OPS_HEALTH_SNAPSHOT_FUTURE_TOLERANCE_MS ||
    !generatedAt
  ) {
    return { valid: false, reason: 'baseline_invalid' };
  }
  if (
    expectedReportType &&
    envelope.reportType !== cleanSnapshotText(expectedReportType, 80)
  ) {
    return { valid: false, reason: 'report_type_mismatch' };
  }
  const scope = normalizeOpsHealthReportScope(envelope.reportScope);
  if (!scope.regionSlug || !scope.marketSlug) {
    return { valid: false, reason: 'baseline_invalid' };
  }
  const normalizedExpectedScope = normalizeOpsHealthReportScope(expectedScope);
  if (
    (normalizedExpectedScope.regionSlug &&
      scope.regionSlug !== normalizedExpectedScope.regionSlug) ||
    (normalizedExpectedScope.marketSlug &&
      scope.marketSlug !== normalizedExpectedScope.marketSlug)
  ) {
    return { valid: false, reason: 'scope_mismatch' };
  }
  const fromDate = parseOpsHealthDate(envelope.reportWindow?.from);
  const toDate = parseOpsHealthDate(envelope.reportWindow?.to);
  const durationMs = Number(envelope.reportWindow?.durationMs);
  if (
    !fromDate ||
    !toDate ||
    fromDate.getTime() >= toDate.getTime() ||
    !Number.isFinite(durationMs) ||
    durationMs <= 0 ||
    durationMs !== toDate.getTime() - fromDate.getTime()
  ) {
    return { valid: false, reason: 'baseline_invalid' };
  }
  return {
    valid: true,
    capturedAtMs: capturedAt.getTime(),
    generatedAtMs: generatedAt.getTime(),
    fromMs: fromDate.getTime(),
    toMs: toDate.getTime(),
    durationMs,
    scope,
  };
};

export const compareOpsHealthSnapshotEnvelopes = ({
  baseline,
  current,
  reportType,
  expectedScope,
  nowMs = Date.now(),
} = {}) => {
  const currentValidation = validateOpsHealthSnapshotEnvelope(current, {
    expectedReportType: reportType,
    expectedScope,
    nowMs,
  });
  if (!currentValidation.valid) {
    return {
      comparable: false,
      currentValid: false,
      reason: 'current_invalid',
      overlapDurationMs: 0,
    };
  }
  const baselineValidation = validateOpsHealthSnapshotEnvelope(baseline, {
    expectedReportType: reportType,
    nowMs,
  });
  if (!baselineValidation.valid) {
    return {
      comparable: false,
      currentValid: true,
      reason: baselineValidation.reason,
      overlapDurationMs: 0,
    };
  }
  if (
    baselineValidation.scope.regionSlug !== currentValidation.scope.regionSlug ||
    baselineValidation.scope.marketSlug !== currentValidation.scope.marketSlug
  ) {
    return {
      comparable: false,
      currentValid: true,
      reason: 'scope_mismatch',
      overlapDurationMs: 0,
    };
  }
  const baselineWindowDays = baselineValidation.scope.defaultWindowDays ?? null;
  const currentWindowDays = currentValidation.scope.defaultWindowDays ?? null;
  if (
    baselineWindowDays !== currentWindowDays ||
    baselineValidation.durationMs !== currentValidation.durationMs
  ) {
    return {
      comparable: false,
      currentValid: true,
      reason: 'window_definition_mismatch',
      overlapDurationMs: 0,
    };
  }
  const overlapStartMs = Math.max(
    baselineValidation.fromMs,
    currentValidation.fromMs,
  );
  const overlapEndMs = Math.min(
    baselineValidation.toMs,
    currentValidation.toMs,
  );
  const overlapDurationMs = overlapEndMs - overlapStartMs;
  if (overlapDurationMs <= 0) {
    return {
      comparable: false,
      currentValid: true,
      reason: 'non_overlapping',
      overlapDurationMs,
    };
  }
  return {
    comparable: true,
    currentValid: true,
    reason: 'comparable',
    overlapDurationMs,
  };
};

export const resolveOpsHealthSnapshotComparison = ({
  baseline,
  current,
  reportType,
  expectedScope,
  nowMs = Date.now(),
} = {}) => {
  const comparison = compareOpsHealthSnapshotEnvelopes({
    baseline,
    current,
    reportType,
    expectedScope,
    nowMs,
  });
  if (!comparison.currentValid) {
    return {
      ...comparison,
      activeBaseline: baseline || null,
      shouldPersistCurrent: false,
      shouldShowDeltas: false,
      status: 'current_invalid',
    };
  }
  if (comparison.comparable) {
    return {
      ...comparison,
      activeBaseline: baseline,
      shouldPersistCurrent: true,
      shouldShowDeltas: true,
      status: 'comparable',
    };
  }
  return {
    ...comparison,
    activeBaseline: current,
    shouldPersistCurrent: true,
    shouldShowDeltas: false,
    status: baseline ? comparison.reason : 'baseline_saved',
  };
};


const getOpsHealthSnapshotEnvelopeMeta = (envelope) => {
  if (!envelope) return null;
  const { snapshot, ...metadata } = envelope;
  return metadata;
};

const formatOpsHealthWindow = (metadata) => {
  const from = metadata?.reportWindow?.from;
  const to = metadata?.reportWindow?.to;
  if (!parseOpsHealthDate(from) || !parseOpsHealthDate(to)) {
    return 'Unavailable';
  }
  return formatDate(from) + ' to ' + formatDate(to);
};

const formatOpsHealthComparisonSpan = (baseline, current) => {
  const baselineDate = parseOpsHealthDate(baseline?.capturedAt);
  const currentDate = parseOpsHealthDate(current?.capturedAt);
  if (!baselineDate || !currentDate) return 'Unavailable';
  const durationMs = currentDate.getTime() - baselineDate.getTime();
  if (durationMs < 0) return 'Unavailable';
  if (durationMs < 60 * 1000) return 'Less than 1 minute';
  const totalMinutes = Math.floor(durationMs / (60 * 1000));
  if (totalMinutes < 60) {
    return String(totalMinutes) + ' minute' + (totalMinutes === 1 ? '' : 's');
  }
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) {
    return String(totalHours) + ' hour' + (totalHours === 1 ? '' : 's');
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return (
    String(days) +
    ' day' +
    (days === 1 ? '' : 's') +
    (hours ? ' ' + String(hours) + ' hour' + (hours === 1 ? '' : 's') : '')
  );
};


const getOpsHealthComparisonStatusMessage = (comparison = {}) => {
  const capturedAt = formatDate(comparison.baseline?.capturedAt);
  switch (comparison.status) {
    case 'baseline_saved':
      return (
        'Fresh comparison baseline saved at ' +
        capturedAt +
        '. Changes will appear after a later refresh.'
      );
    case 'baseline_invalid':
    case 'report_type_mismatch':
      return (
        'The previous comparison baseline was not usable. A fresh baseline was saved at ' +
        capturedAt +
        '.'
      );
    case 'scope_mismatch':
    case 'window_definition_mismatch':
      return (
        'The saved baseline used a different report scope or window definition. A fresh comparison baseline was saved at ' +
        capturedAt +
        '.'
      );
    case 'non_overlapping':
      return (
        'The previous report window no longer overlaps the current report window. A fresh comparison baseline was saved at ' +
        capturedAt +
        '.'
      );
    case 'current_invalid':
      return 'Comparison unavailable because the current report window or scope could not be verified.';
    case 'no_changes':
      return 'No report-total changes between the comparison baseline and current report.';
    case 'deltas':
      return 'Changes in rolling-window report totals between the comparison baseline and current report.';
    case 'fetch_error':
      return 'Comparison was not updated because the latest report request failed.';
    default:
      return 'Comparison status has not been established.';
  }
};

const buildOpsHealthComparisonReportLines = (label, comparison = {}) => [
  '- ' + label + ' current fetched: ' + formatDate(comparison.current?.capturedAt),
  '- ' +
    label +
    ' current server generated: ' +
    formatDate(comparison.current?.reportGeneratedAt),
  '- ' + label + ' current report window: ' + formatOpsHealthWindow(comparison.current),
  '- ' +
    label +
    ' baseline captured: ' +
    formatDate(comparison.baseline?.capturedAt),
  '- ' +
    label +
    ' baseline server generated: ' +
    formatDate(comparison.baseline?.reportGeneratedAt),
  '- ' +
    label +
    ' baseline report window: ' +
    formatOpsHealthWindow(comparison.baseline),
  '- ' +
    label +
    ' comparison span: ' +
    formatOpsHealthComparisonSpan(comparison.baseline, comparison.current),
  '- ' + label + ' refresh trigger: ' + (comparison.trigger || 'Not checked'),
  '- ' + label + ' comparison status: ' + getOpsHealthComparisonStatusMessage(comparison),
];


const OpsHealthComparisonMeta = ({
  comparison,
  refreshTrigger,
  testId,
}) => (
  <div data-testid={testId}>
    <p className="ops-health-mini-meta">
      <strong>Current report:</strong> Fetched{' '}
      {formatDate(comparison.current?.capturedAt)} / Server generated{' '}
      {formatDate(comparison.current?.reportGeneratedAt)} / Report window:{' '}
      {formatOpsHealthWindow(comparison.current)}
    </p>
    <p className="ops-health-mini-meta">
      <strong>Comparison baseline:</strong> Captured{' '}
      {formatDate(comparison.baseline?.capturedAt)} / Server generated{' '}
      {formatDate(comparison.baseline?.reportGeneratedAt)} / Report window:{' '}
      {formatOpsHealthWindow(comparison.baseline)} / Comparison span:{' '}
      {formatOpsHealthComparisonSpan(
        comparison.baseline,
        comparison.current,
      )}
    </p>
    <p className="ops-health-mini-meta">
      Refresh trigger: {refreshTrigger || 'Not checked'}. Socket changes and
      tab focus trigger a fresh server report.
    </p>
  </div>
);


export const readOpsHealthSnapshot = (key) => {
  if (typeof window === 'undefined' || !key) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '');
    if (parsed?.version !== OPS_HEALTH_SNAPSHOT_VERSION) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

export const writeOpsHealthSnapshot = (key, envelope) => {
  if (typeof window === 'undefined' || !key || !envelope) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(envelope));
    return true;
  } catch (err) {
    return false;
  }
};

const getTaskStatus = (row) => String(row?.status || '').toLowerCase();

const isOpenTask = (row) => OPEN_TASK_STATUSES.has(getTaskStatus(row));
const isTerminalNonActionableTask = (row) =>
  TERMINAL_NON_ACTIONABLE_TASK_STATUSES.has(getTaskStatus(row));

const isCommunityTaskRow = (row) =>
  row?.communityTask === true ||
  row?.money?.communityTask === true ||
  String(row?.paymentMode || row?.money?.paymentMode || '').toLowerCase() ===
    'community';

const hasFundedTaskMoney = (row) =>
  !isCommunityTaskRow(row) &&
  (String(row?.money?.escrowStatus || '').toLowerCase() === 'held' ||
    toSafeNumber(row?.money?.escrowAmountJGK) > 0 ||
    toSafeNumber(row?.money?.priceUsd) > 0);

const isAcceptedTask = (row) =>
  ACCEPTED_TASK_STATUSES.has(getTaskStatus(row)) || !!row?.acceptedHelperPlayerId;

const getTaskId = (row) =>
  cleanDisplayText(row?.taskId || row?.id || row?._id || '', 120);

const hasActionCreditCompletionProof = (row, completedTaskIds = new Set()) => {
  const taskId = getTaskId(row);
  return !!taskId && completedTaskIds.has(taskId);
};

const isCompletedTask = (row, shareCounts, completedTaskIds) =>
  COMPLETED_TASK_STATUSES.has(getTaskStatus(row)) ||
  !!row?.completedAt ||
  !!row?.completedAtUtc ||
  !!row?.verifiedAt ||
  !!row?.verifiedAtUtc ||
  !!row?.completionVerifiedAt ||
  hasActionCreditCompletionProof(row, completedTaskIds) ||
  toSafeNumber(shareCounts?.localops_task_completion_verified) > 0 ||
  toSafeNumber(shareCounts?.localops_action_credit_awarded) > 0;

const getConfirmedTaskState = (row, shareCounts, completedTaskIds) => {
  const completed = isCompletedTask(row, shareCounts, completedTaskIds);
  const accepted = isAcceptedTask(row);
  const terminal = isTerminalNonActionableTask(row);
  if (completed) {
    return {
      state: 'completed',
      label: 'confirmed task completed: yes',
      confidence: 'High',
      confidenceReason: 'Backend task state or verified completion proof supports this.',
    };
  }
  if (accepted) {
    return {
      state: 'accepted',
      label: 'confirmed task accepted: yes',
      confidence: 'High',
      confidenceReason: 'Backend task status or accepted helper field supports this.',
    };
  }
  if (terminal) {
    return {
      state: 'closed',
      label: `confirmed task closed: ${getTaskStatus(row) || 'yes'}`,
      confidence: 'High',
      confidenceReason:
        'Backend task status says this task is no longer actively actionable.',
    };
  }
  return {
    state: 'open',
    label: 'confirmed task accepted: no',
    confidence: 'Medium',
    confidenceReason: 'Loaded task rows do not show accepted/completed state.',
  };
};

const hoursSince = (value, now = new Date()) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const nowDate = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(nowDate.getTime())) return null;
  return Math.max(0, Math.floor((nowDate.getTime() - date.getTime()) / 3600000));
};

const formatAgeHours = (hours) => {
  if (hours === null) return 'unknown age';
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const aggregateShareCounts = (rows = []) =>
  rows.reduce((totals, row) => {
    Object.entries(row?.counts || {}).forEach(([eventName, value]) => {
      totals[eventName] = Number(totals[eventName] || 0) + Number(value || 0);
    });
    return totals;
  }, {});

const buildShareCountsByTaskId = (shareFunnelRows = []) => {
  const byTaskId = new Map();
  shareFunnelRows.forEach((row) => {
    const taskId = cleanDisplayText(row?.taskId, 120);
    if (!taskId) return;
    if (!byTaskId.has(taskId)) byTaskId.set(taskId, []);
    byTaskId.get(taskId).push(row);
  });
  const countsByTaskId = new Map();
  byTaskId.forEach((rows, taskId) => {
    countsByTaskId.set(taskId, aggregateShareCounts(rows));
  });
  return countsByTaskId;
};

const getShareCount = (shareCounts, eventName) =>
  shareCounts ? toSafeNumber(shareCounts[eventName]) : null;

const buildTaskEvidence = ({
  fanoutSent,
  emailOpened,
  shareCounts,
  helper,
  acceptedAge,
}) => {
  const parts = [];
  if (fanoutSent !== null) parts.push(`helper fanout sent: ${formatCount(fanoutSent)}`);
  if (emailOpened !== null) parts.push(`pixel image loads: ${formatCount(emailOpened)}`);
  if (shareCounts) {
    parts.push(
      `share landings: ${formatCount(getShareCount(shareCounts, 'localops_share_landing_viewed'))}`,
    );
    parts.push(
      `share accept success: ${formatCount(getShareCount(shareCounts, 'localops_share_task_accept_succeeded'))}`,
    );
  }
  if (acceptedAge !== null) parts.push(`accepted age: ${formatAgeHours(acceptedAge)}`);
  if (helper) parts.push(`helper: ${helper}`);
  return parts.length ? parts.join(' / ') : 'Diagnostics only';
};

const deriveTaskActionability = (row, shareCounts, now, completedTaskIds) => {
  const fanoutSent = getTrackedMetricNumber(row?.fanout?.emailSentCount);
  const emailOpened = getTrackedMetricNumber(row?.fanout?.emailPixelOpens);
  const helper = row?.acceptedHelperPlayerId || 'unassigned';
  const canUsePaidHelperFanout = hasFundedTaskMoney(row);
  const actionCreditCompletion = hasActionCreditCompletionProof(
    row,
    completedTaskIds,
  );
  const acceptedAge = isAcceptedTask(row)
    ? hoursSince(row?.acceptedAt || row?.acceptedAtUtc || row?.updatedAt, now)
    : null;
  const shareLandings = getShareCount(shareCounts, 'localops_share_landing_viewed');
  const landingAcceptClicks = getShareCount(
    shareCounts,
    'localops_share_accept_clicked',
  );
  const taskSheetOpens = getShareCount(
    shareCounts,
    'localops_share_task_sheet_opened',
  );
  const taskAcceptSuccess = getShareCount(
    shareCounts,
    'localops_share_task_accept_succeeded',
  );
  const baseEvidence = buildTaskEvidence({
    fanoutSent,
    emailOpened,
    shareCounts,
    helper,
    acceptedAge,
  });

  if (isCompletedTask(row, shareCounts, completedTaskIds)) {
    return {
      label: 'No action needed',
      reason: 'Completed/verified task signal is present.',
      evidence: actionCreditCompletion
        ? 'Action Credit row: yes / Verified completion'
        : shareCounts
        ? `completed: ${formatCount(getShareCount(shareCounts, 'localops_task_completion_verified'))} / Action Credit: ${formatCount(getShareCount(shareCounts, 'localops_action_credit_awarded'))}`
        : `status: ${row?.status || '--'}`,
    };
  }

  if (isTerminalNonActionableTask(row)) {
    return {
      label: 'Historical diagnostics',
      reason: 'Task is closed and should not receive active operator action.',
      evidence: `status: ${row?.status || '--'}`,
    };
  }

  if (isAcceptedTask(row)) {
    if (acceptedAge !== null && acceptedAge >= 24) {
      return {
        label: 'Monitor accepted task',
        reason: 'Accepted task is older than 24h without completion proof.',
        evidence: baseEvidence,
      };
    }
    return {
      label: 'Monitor accepted task',
      reason: 'Accepted task has no completion proof yet.',
      evidence: baseEvidence,
    };
  }

  if (isOpenTask(row) && shareCounts && taskSheetOpens > 0 && taskAcceptSuccess === 0) {
    return {
      label: 'Consider raise pay',
      reason: 'Helpers reached the task sheet but no share-attributed accept landed.',
      evidence: `task sheet opens: ${formatCount(taskSheetOpens)} / share accept success: 0`,
    };
  }

  if (shareCounts && shareLandings > 0 && landingAcceptClicks === 0) {
    return {
      label: 'Share again',
      reason: 'Share traffic has not produced accept intent.',
      evidence: `share landings: ${formatCount(shareLandings)} / accept clicks: 0`,
    };
  }

  if (isOpenTask(row) && canUsePaidHelperFanout && fanoutSent !== null && fanoutSent > 0) {
    return {
      label: 'Renotify helpers',
      reason: 'Funded/open task has helper fanout but remains unassigned.',
      evidence: baseEvidence,
    };
  }

  if (isOpenTask(row) && canUsePaidHelperFanout && (fanoutSent === null || fanoutSent === 0)) {
    return {
      label: 'Check fanout',
      reason: 'Funded/open task has no visible paid helper fanout proof.',
      evidence:
        fanoutSent === null
          ? 'helper fanout sent: not tracked yet'
          : 'helper fanout sent: 0',
    };
  }

  return {
    label: 'Diagnostics only',
    reason: 'Existing payload does not prove a safe recommendation.',
    evidence: baseEvidence,
  };
};

const derivePlayerUsefulness = (row, now) => {
  const actionCredit = getTrackedMetricNumber(row?.actionCreditCount);
  const magicCompletions = getTrackedMetricNumber(row?.magicLink?.loginCount);
  const activationSends = getTrackedMetricNumber(row?.activationEmail?.sendCount);
  const location = String(row?.locationStatus || '').toLowerCase();
  const role = String(row?.role || '').toLowerCase();
  const createdAge = hoursSince(row?.createdAt, now);
  const evidence = [
    actionCredit !== null ? `Action Credit: ${formatCount(actionCredit)}` : '',
    magicCompletions !== null
      ? `magic completed: ${formatCount(magicCompletions)}`
      : '',
    activationSends !== null ? `activation sends: ${formatCount(activationSends)}` : '',
    row?.locationStatus ? `location: ${row.locationStatus}` : '',
  ]
    .filter(Boolean)
    .join(' / ');

  if (role.includes('owner') || role.includes('creator')) {
    return { label: 'Owner only', evidence: evidence || `role: ${row?.role}` };
  }
  if (actionCredit !== null && actionCredit > 0) {
    return { label: 'Verified Local', evidence };
  }
  if (createdAge !== null && createdAge <= 24 * 7) {
    return { label: 'New helper', evidence: evidence || `age: ${formatAgeHours(createdAge)}` };
  }
  if (location.includes('missing')) {
    return { label: 'Needs location', evidence: evidence || 'location: missing' };
  }
  if (magicCompletions !== null && magicCompletions > 0) {
    return { label: 'Active helper', evidence };
  }
  if (location.includes('saved') || location.includes('ready')) {
    return { label: 'Map-ready', evidence };
  }
  return { label: 'Not enough data', evidence: evidence || 'Diagnostics only' };
};

const deriveSourceOutcome = (row) => {
  const counts = row?.counts || {};
  const landings = toSafeNumber(counts.localops_share_landing_viewed);
  const acceptClicks = toSafeNumber(counts.localops_share_accept_clicked);
  const taskSheetOpens = toSafeNumber(counts.localops_share_task_sheet_opened);
  const acceptSuccess = toSafeNumber(counts.localops_share_task_accept_succeeded);
  const completions = toSafeNumber(counts.localops_task_completion_verified);
  const actionCredit = toSafeNumber(counts.localops_action_credit_awarded);
  const evidence = `landings: ${formatCount(landings)} / accepts: ${formatCount(acceptSuccess)} / completed: ${formatCount(completions)} / Action Credit: ${formatCount(actionCredit)}`;

  if (completions > 0 || actionCredit > 0) {
    return { label: 'Converts to completion', evidence };
  }
  if (acceptSuccess > 0) return { label: 'Converts to accept', evidence };
  if (acceptClicks > 0 || taskSheetOpens > 0) {
    return { label: 'High intent', evidence };
  }
  if (landings > 0) return { label: 'Traffic only', evidence };
  if (toSafeNumber(row?.total) > 0) {
    return {
      label: 'Low signal',
      evidence: `${formatCount(row.total)} tracked events / no downstream outcome proof`,
    };
  }
  return { label: 'Not enough data', evidence: 'Diagnostics only' };
};

const buildSourceOutcomeRows = (shareFunnelRows = [], operatorSourceRows = []) => {
  if (shareFunnelRows.length) {
    return shareFunnelRows.map((row) => {
      const outcome = deriveSourceOutcome(row);
      return {
        key: row.sourceLabel,
        displayLabel: row.displayLabel || row.sourceLabel,
        total: row.total,
        outcomeLabel: outcome.label,
        evidence: outcome.evidence,
      };
    });
  }
  return operatorSourceRows.map((row) => {
    const outcome = deriveSourceOutcome(row);
    return {
      key: row.sourceKey,
      displayLabel: row.sourceKey,
      total: row.total,
      outcomeLabel: outcome.label,
      evidence: outcome.evidence,
    };
  });
};

const getBestConvertingSource = (sourceOutcomeRows = []) =>
  sourceOutcomeRows
    .filter((row) =>
      ['Converts to completion', 'Converts to accept'].includes(
        row.outcomeLabel,
      ),
    )
    .sort((a, b) => {
      const score = (row) => {
        if (row.outcomeLabel === 'Converts to completion') return 3;
        if (row.outcomeLabel === 'Converts to accept') return 2;
        return 0;
      };
      return score(b) - score(a) || toSafeNumber(b.total) - toSafeNumber(a.total);
    })[0];

const FUNNEL_BREAK_STEPS = [
  {
    label: 'Share interest, no accept click',
    from: 'localops_share_landing_viewed',
    to: 'localops_share_accept_clicked',
    fromLabel: 'share landings',
    toLabel: 'accept clicks',
  },
  {
    label: 'Continue/auth handoff drop',
    from: 'localops_share_continue_started',
    to: 'localops_share_magic_link_completed',
    fromLabel: 'continue started',
    toLabel: 'magic completed',
  },
  {
    label: 'PlayerMap/task focus drop',
    from: 'localops_share_playermap_arrived',
    to: 'localops_share_task_sheet_opened',
    fromLabel: 'PlayerMap arrivals',
    toLabel: 'task sheet opens',
  },
  {
    label: 'Task terms/pay drop',
    from: 'localops_share_task_accept_clicked',
    to: 'localops_share_task_accept_succeeded',
    fromLabel: 'sheet accept clicks',
    toLabel: 'share accept success',
  },
  {
    label: 'Completion risk',
    from: 'localops_share_task_accept_succeeded',
    to: 'localops_task_completion_verified',
    fromLabel: 'share accept success',
    toLabel: 'completed',
  },
  {
    label: 'Credit join issue',
    from: 'localops_task_completion_verified',
    to: 'localops_action_credit_awarded',
    fromLabel: 'completed',
    toLabel: 'Action Credit',
  },
];

const deriveWeakestFunnelBreak = (shareFunnelRows = []) => {
  const counts = aggregateShareCounts(shareFunnelRows);
  return FUNNEL_BREAK_STEPS.map((step) => {
    const from = toSafeNumber(counts[step.from]);
    const to = toSafeNumber(counts[step.to]);
    if (from <= 0) return null;
    return {
      ...step,
      from,
      to,
      rate: to / from,
      drop: from - to,
      evidence: `${step.fromLabel}: ${formatCount(from)} / ${step.toLabel}: ${formatCount(to)}`,
    };
  })
    .filter((step) => step && step.drop > 0)
    .sort((a, b) => a.rate - b.rate || b.drop - a.drop)[0];
};

const buildEntityTitleLookup = (operatorVisibility = {}) => {
  const lookup = new Map();
  (operatorVisibility.taskRows || []).forEach((row) => {
    if (row?.taskId && row?.title) lookup.set(`task:${row.taskId}`, row.title);
  });
  (operatorVisibility.offeringRows || []).forEach((row) => {
    if (row?.offeringId && row?.title) {
      lookup.set(`offering:${row.offeringId}`, row.title);
    }
  });
  return lookup;
};

const buildPlayerLabelLookup = (operatorVisibility = {}) => {
  const lookup = new Map();
  (operatorVisibility.activePlayerRows || []).forEach((row) => {
    if (row?.playerId) lookup.set(row.playerId, row.handle || row.playerId);
  });
  return lookup;
};

const buildActionCreditRows = (operatorVisibility = {}) => {
  const entityTitleLookup = buildEntityTitleLookup(operatorVisibility);
  const playerLabelLookup = buildPlayerLabelLookup(operatorVisibility);
  return (operatorVisibility.actionCreditRows || []).map((row) => {
    const entityKey = `${row?.entityType || 'entity'}:${row?.entityId || ''}`;
    const title = entityTitleLookup.get(entityKey) || entityKey;
    const playerLabel =
      playerLabelLookup.get(row?.playerId) || row?.playerId || 'unknown player';
    return {
      ...row,
      title,
      meta: [
        row?.entityId,
        row?.role ? `${row.role} ${playerLabel}` : playerLabel,
        '+1 Action Credit',
        'Verified completion',
      ]
        .filter(Boolean)
        .join(' / '),
    };
  });
};

const buildCompletedTaskIdSet = (operatorVisibility = {}) => {
  const taskIds = new Set();
  (operatorVisibility.actionCreditRows || []).forEach((row) => {
    const entityType = String(row?.entityType || '').toLowerCase();
    const entityId = cleanDisplayText(row?.entityId || row?.taskId || '', 120);
    if (entityId && (!entityType || entityType === 'task')) {
      taskIds.add(entityId);
    }
  });
  return taskIds;
};

export const buildOperatorActionability = ({
  operatorVisibility = {},
  shareFunnelRows = [],
  now = new Date(),
} = {}) => {
  const safeOperatorVisibility = operatorVisibility || {};
  const shareCountsByTaskId = buildShareCountsByTaskId(shareFunnelRows);
  const completedTaskIds = buildCompletedTaskIdSet(safeOperatorVisibility);
  const taskRows = (safeOperatorVisibility.taskRows || []).map((row) => {
    const shareCounts = shareCountsByTaskId.get(String(row?.taskId || ''));
    const confirmedState = getConfirmedTaskState(
      row,
      shareCounts,
      completedTaskIds,
    );
    return {
      ...row,
      actionability: deriveTaskActionability(
        row,
        shareCounts,
        now,
        completedTaskIds,
      ),
      shareCounts,
      confirmedState,
    };
  });
  const taskRowsById = new Map(
    taskRows
      .map((row) => [cleanDisplayText(row?.taskId, 120), row])
      .filter(([taskId]) => !!taskId),
  );
  const playerRows = (safeOperatorVisibility.activePlayerRows || []).map((row) => ({
    ...row,
    usefulness: derivePlayerUsefulness(row, now),
  }));
  const sourceRows = buildSourceOutcomeRows(
    shareFunnelRows,
    safeOperatorVisibility.sourceAttributionRows || [],
  );
  const bestSource = getBestConvertingSource(sourceRows);
  const weakestFunnelBreak = deriveWeakestFunnelBreak(shareFunnelRows);
  const actionCreditRows = buildActionCreditRows(safeOperatorVisibility);
  const attentionTasks = taskRows.filter((row) =>
    [
      'Renotify helpers',
      'Consider raise pay',
      'Share again',
      'Check fanout',
      'Monitor accepted task',
    ].includes(row.actionability.label),
  );
  const fanoutNoAcceptTasks = taskRows.filter((row) => {
    const fanoutSent = getTrackedMetricNumber(row?.fanout?.emailSentCount);
    return (
      fanoutSent !== null &&
      fanoutSent > 0 &&
      !isTerminalNonActionableTask(row) &&
      !isAcceptedTask(row) &&
      !isCompletedTask(row, row.shareCounts, completedTaskIds)
    );
  });
  const shareInterestNoAccept = shareFunnelRows.filter((row) => {
    const counts = row?.counts || {};
    const linkedTask = taskRowsById.get(cleanDisplayText(row?.taskId, 120));
    const confirmedState = linkedTask?.confirmedState?.state || 'unknown';
    const terminalTask = linkedTask ? isTerminalNonActionableTask(linkedTask) : false;
    return (
      toSafeNumber(counts.localops_share_landing_viewed) > 0 &&
      toSafeNumber(counts.localops_share_task_accept_succeeded) === 0 &&
      !['accepted', 'completed', 'closed'].includes(confirmedState) &&
      !terminalTask
    );
  });
  const measurementMismatchRows = shareFunnelRows.filter((row) => {
    const counts = row?.counts || {};
    const linkedTask = taskRowsById.get(cleanDisplayText(row?.taskId, 120));
    const confirmedState = linkedTask?.confirmedState?.state || 'unknown';
    return (
      toSafeNumber(counts.localops_share_landing_viewed) > 0 &&
      toSafeNumber(counts.localops_share_task_accept_succeeded) === 0 &&
      ['accepted', 'completed'].includes(confirmedState)
    );
  });
  const acceptedAgingTasks = taskRows.filter(
    (row) => row.actionability.label === 'Monitor accepted task',
  );
  const triageCards = [
    attentionTasks[0]
      ? {
          title: 'Top task action',
          count: attentionTasks.length,
          label: attentionTasks[0].title || attentionTasks[0].taskId,
          action: attentionTasks[0].actionability.label,
          reason: attentionTasks[0].actionability.reason,
          evidence: attentionTasks[0].actionability.evidence,
          confidence: 'High',
          confidenceReason: attentionTasks[0].confirmedState?.confidenceReason,
          operatorTranslation:
            'This recommendation is based on loaded Ops Health task rows, not a production behavior change.',
          deltaKey: `operator:taskRows:${getOpsSnapshotRowKey(attentionTasks[0].taskId)}:fanout.emailSentCount`,
          deltaSource: 'operator',
        }
      : null,
    fanoutNoAcceptTasks[0]
      ? {
          title: 'Fanout sent, no confirmed accept',
          count: fanoutNoAcceptTasks.length,
          label: fanoutNoAcceptTasks[0].title || fanoutNoAcceptTasks[0].taskId,
          action: 'Renotify helpers',
          reason: 'Helper fanout exists but the task is still unassigned.',
          evidence: fanoutNoAcceptTasks[0].actionability.evidence,
          confidence: 'High',
          confidenceReason:
            'Task row is funded/open, has fanout proof, and has no confirmed helper assignment.',
          operatorTranslation:
            'This is a real task-state gap if the task is still open; use share attribution only as supporting evidence.',
          deltaKey: `operator:taskRows:${getOpsSnapshotRowKey(fanoutNoAcceptTasks[0].taskId)}:fanout.emailSentCount`,
          deltaSource: 'operator',
        }
      : null,
    shareInterestNoAccept[0]
      ? {
          title: 'Share interest, no confirmed accept',
          count: shareInterestNoAccept.length,
          label: shareInterestNoAccept[0].displayLabel,
          action: 'Share again',
          reason: 'Share traffic exists without a confirmed accepted/completed task row.',
          evidence: `share landings: ${formatCount(
            shareInterestNoAccept[0].counts?.localops_share_landing_viewed,
          )} / share accept success: ${formatCount(
            shareInterestNoAccept[0].counts?.localops_share_task_accept_succeeded,
          )}`,
          confidence: 'Medium',
          confidenceReason:
            'Tracking shows share visits, but loaded task rows do not confirm acceptance.',
          operatorTranslation:
            'This is a share-funnel signal, not proof that the task cannot be accepted.',
          deltaKey: `shareFunnelSourceBreakdown:${shareInterestNoAccept[0].sourceLabel}:localops_share_landing_viewed`,
          deltaSource: 'launch',
        }
      : null,
    measurementMismatchRows[0]
      ? {
          title: 'Measurement mismatch',
          count: measurementMismatchRows.length,
          label: measurementMismatchRows[0].displayLabel,
          action: 'Inspect attribution join',
          reason:
            'Task state confirms accept/completion, but share-funnel accept success stayed at 0.',
          evidence: `share landings: ${formatCount(
            measurementMismatchRows[0].counts?.localops_share_landing_viewed,
          )} / share accept success: 0 / ${taskRowsById.get(cleanDisplayText(measurementMismatchRows[0].taskId, 120))?.confirmedState?.label || 'confirmed task state present'}`,
          confidence: 'Low',
          confidenceReason:
            'Confirmed task state contradicts share-funnel attribution, so this is measurement noise until proven otherwise.',
          operatorTranslation:
            'Accept happened according to task state; do not treat this as a task failure.',
          deltaKey: `shareFunnelSourceBreakdown:${measurementMismatchRows[0].sourceLabel}:localops_share_landing_viewed`,
          deltaSource: 'launch',
        }
      : null,
    acceptedAgingTasks[0]
      ? {
          title: 'Accepted task aging',
          count: acceptedAgingTasks.length,
          label: acceptedAgingTasks[0].title || acceptedAgingTasks[0].taskId,
          action: 'Review completion status',
          reason: acceptedAgingTasks[0].actionability.reason,
          evidence: acceptedAgingTasks[0].actionability.evidence,
          confidence: 'Medium',
          confidenceReason:
            'Confirmed accepted state exists, but completion proof is not present in the loaded rows.',
          operatorTranslation:
            'This is an operational follow-up signal, not an attribution failure.',
          deltaKey: `operator:taskRows:${getOpsSnapshotRowKey(acceptedAgingTasks[0].taskId)}:helperReleaseCount`,
          deltaSource: 'operator',
        }
      : null,
    weakestFunnelBreak
      ? {
          title: 'Weakest funnel step',
          count: weakestFunnelBreak.drop,
          label: weakestFunnelBreak.label,
          action: 'Inspect funnel drop',
          reason: 'Largest supported conversion drop in the share funnel.',
          evidence: weakestFunnelBreak.evidence,
          confidence: 'Medium',
          confidenceReason:
            'This is aggregated share-funnel instrumentation and may include measurement gaps.',
          operatorTranslation:
            'Use this as a funnel investigation cue, not proof that OAuth or location is the only blocker.',
          deltaKey: `shareFunnelTotalsByEvent:${weakestFunnelBreak.from}`,
          deltaSource: 'launch',
        }
      : null,
    bestSource
      ? {
          title: 'Best converting source',
          count: 1,
          label: bestSource.displayLabel,
          action: 'Reuse winning source',
          reason: bestSource.outcomeLabel,
          evidence: bestSource.evidence,
          confidence: 'High',
          confidenceReason:
            'The source has downstream completion or Action Credit evidence in loaded rows.',
          operatorTranslation:
            'Reuse this source while checking whether the result is repeatable.',
          deltaKey: `shareFunnelSourceBreakdown:${bestSource.sourceLabel}:total`,
          deltaSource: 'launch',
        }
      : null,
  ].filter(Boolean);

  return {
    taskRows,
    playerRows,
    sourceRows,
    actionCreditRows,
    weakestFunnelBreak,
    triageCards: triageCards.length
      ? triageCards
      : [
      {
        title: 'No urgent action',
        count: 0,
        label: 'Current rows do not prove a safe operator intervention.',
        action: 'No action needed',
        reason: 'Completed, closed, or insufficient-data rows are kept out of the action queue.',
        evidence: 'Use Diagnostics for raw IDs and low-signal rows.',
        confidence: 'High',
        confidenceReason:
          'No loaded row currently proves a safe operator intervention.',
        operatorTranslation:
          'Keep watching. Do not infer a product failure from missing attribution alone.',
      },
    ],
  };
};

const getOperatorPersonLabel = (display, fallbackId, emptyLabel = '--') => {
  const label = cleanDisplayText(display?.label || display?.handle || '', 80);
  if (label) return label;
  const fallback = cleanDisplayText(fallbackId, 80);
  return fallback || emptyLabel;
};

const formatReportMetric = (value) => cleanDisplayText(getMetricValue(value), 120) || '--';

const MAGIC_LINK_HANDOFF_STATE_LABELS = Object.freeze({
  pending: 'Pending',
  provisioned_not_completed: 'Provisioned, not completed',
  timing_unknown: 'Timing unknown',
  classification_unavailable: 'Classification unavailable',
});

const formatMagicLinkHandoffRate = (rate = {}) => {
  if (rate.status !== 'available') return rate.reason || 'Unavailable';
  return `${formatCount(rate.percentage)}% (${formatCount(
    rate.numerator,
  )}/${formatCount(rate.denominator)} matured)`;
};

const formatMagicLinkHandoffAge = (age) => age?.label || '--';

const formatMagicLinkHandoffState = (state) =>
  MAGIC_LINK_HANDOFF_STATE_LABELS[state] || cleanDisplayText(state, 80) || '--';

const formatTaskReward = (row) => {
  const priceUsd =
    row?.money?.priceUsd ??
    row?.priceUsd ??
    row?.price ??
    row?.amountUsd;
  if (Number.isFinite(Number(priceUsd)) && Number(priceUsd) > 0) {
    return `${formatUsd(priceUsd)} helper reward`;
  }
  const amountJgk =
    row?.money?.escrowAmountJGK ??
    row?.priceJGK ??
    row?.amountJGK ??
    row?.escrowAmountJGK;
  if (Number.isFinite(Number(amountJgk)) && Number(amountJgk) > 0) {
    return `${formatJgk(amountJgk)} task value`;
  }
  if (isCommunityTaskRow(row)) return 'community help';
  return '--';
};

const getTaskRegionLabel = (row) =>
  cleanDisplayText(row?.regionLabel || row?.region || row?.marketSlug || '', 80) ||
  '--';

const getTaskReportTitle = (taskReport) =>
  getTaskDisplayTitle(taskReport?.task || taskReport || {});

const truncateReportList = (items = [], max = AI_REPORT_MAX_DEBUG_IDS) => {
  const safeItems = (items || []).filter(Boolean).map((item) => cleanDisplayText(item, 120));
  if (safeItems.length <= max) return safeItems.join(', ') || '--';
  return `${safeItems.slice(0, max).join(', ')}, ... ${safeItems.length - max} more`;
};

const createReportLines = (title, lines = []) => [
  `## ${title}`,
  ...lines.filter((line) => line !== null && line !== undefined && line !== ''),
  '',
];

const formatTaskReportLine = (row) => {
  const title = getTaskDisplayTitle(row);
  const creator = getOperatorPersonLabel(
    row?.creatorDisplay,
    row?.creatorPlayerId,
    'unknown requester',
  );
  const helper = getOperatorPersonLabel(
    row?.helperDisplay,
    row?.acceptedHelperPlayerId,
    'unassigned',
  );
  const confirmedState = row?.confirmedState?.label || 'confirmed task state not loaded';
  return [
    `- ${title}`,
    `status: ${row?.status || '--'}`,
    `region: ${getTaskRegionLabel(row)}`,
    `reward: ${formatTaskReward(row)}`,
    `requester: ${creator}`,
    `helper: ${helper}`,
    confirmedState,
    row?.acceptedAt ? `acceptedAt: ${formatDate(row.acceptedAt)}` : '',
    row?.completedAt ? `completedAt: ${formatDate(row.completedAt)}` : '',
    row?.actionability?.label ? `action: ${row.actionability.label}` : '',
    row?.actionability?.evidence ? `evidence: ${row.actionability.evidence}` : '',
  ]
    .filter(Boolean)
    .join(' / ');
};

const formatShareReportLine = (row, taskRowsById) => {
  const counts = row?.counts || {};
  const taskRow = taskRowsById.get(cleanDisplayText(row?.taskId, 120));
  const confirmedState =
    taskRow?.confirmedState?.label || 'confirmed task state not loaded';
  const mismatch =
    taskRow &&
    ['accepted', 'completed'].includes(taskRow.confirmedState?.state) &&
    toSafeNumber(counts.localops_share_task_accept_succeeded) === 0;
  return [
    `- ${row?.displayLabel || row?.sourceLabel || 'unknown share row'}`,
    `share landings: ${formatCount(counts.localops_share_landing_viewed)}`,
    `accept clicks: ${formatCount(counts.localops_share_accept_clicked)}`,
    `continue started: ${formatCount(counts.localops_share_continue_started)}`,
    `magic completed: ${formatCount(counts.localops_share_magic_link_completed)}`,
    `task sheet opens: ${formatCount(counts.localops_share_task_sheet_opened)}`,
    `share accept success: ${formatCount(counts.localops_share_task_accept_succeeded)}`,
    confirmedState,
    mismatch ? 'measurement mismatch: yes' : '',
  ]
    .filter(Boolean)
    .join(' / ');
};

const PROOF_ATTRIBUTION_STATE_LABELS = Object.freeze({
  new_requester_candidate: 'Ready: new requester candidate',
  ready: 'Ready for policy evaluation',
  waiting: 'Waiting on task state',
  blocked: 'Blocked',
  unknown: 'Unknown',
});

const PROOF_REFERRAL_POLICY_STATE_LABELS = Object.freeze({
  pass: 'Meets v1 policy criteria',
  no_credit: 'No credit under v1',
  waiting: 'Waiting on required task state',
  blocked: 'Blocked by policy or integrity',
  unknown: 'Unable to evaluate',
});

const PROOF_REFERRAL_POLICY_REASON_LABELS = Object.freeze({
  new_requester_verified_completion: 'New requester verified completion',
  existing_requester: 'Existing requester',
  unsupported_payment_mode: 'Unsupported payment mode',
  funding_not_confirmed: 'Funding not confirmed',
  task_not_accepted: 'Task not accepted',
  task_not_completed: 'Task not completed',
  completion_not_verified: 'Completion not verified',
  self_referral: 'Self-referral',
  task_canceled: 'Task canceled',
  task_refunded: 'Task refunded',
  task_disputed: 'Task disputed',
  task_rejected: 'Task rejected',
  task_voided: 'Task voided',
  task_reversed: 'Task reversed',
  duplicate_task_attribution: 'Duplicate task attribution',
  source_ref_mismatch: 'Source player mismatch',
  task_creator_mismatch: 'Task creator mismatch',
  conflicting_proof_identity: 'Conflicting proof identity',
  conflicting_task_creator_identity: 'Conflicting task creator identity',
  unsupported_source: 'Unsupported source',
  unsupported_origin_surface: 'Unsupported origin surface',
  invalid_source_proof: 'Invalid source proof',
  missing_task: 'Missing task',
  missing_proof: 'Missing proof',
  missing_proof_player: 'Missing proof player',
  missing_task_creator: 'Missing task creator',
  missing_source_ref_player: 'Missing source proof player',
  funding_state_unknown: 'Funding state unknown',
  completion_state_unknown: 'Completion state unknown',
  requester_history_unknown: 'Requester history unknown',
  malformed_policy_input: 'Malformed policy input',
});

const PROOF_REFERRAL_ISSUANCE_LABELS = Object.freeze({
  none: 'No issuance decision',
  issued: 'JGK issued',
  no_credit: 'No JGK issued',
  blocked: 'No JGK issued',
  not_issued_budget: 'Not issued - budget',
  not_issued_cap: 'Not issued - cap',
  not_issued_region: 'Not issued - region',
  not_issued_cutoff: 'Not issued - before launch cutoff',
});

const PROOF_REFERRAL_ISSUANCE_REASON_LABELS = Object.freeze({
  daily_budget_exhausted: 'Daily budget exhausted',
  player_daily_cap_reached: 'Player daily cap reached',
  region_not_enabled: 'Region not enabled',
  before_launch_cutoff: 'Before launch cutoff',
  existing_requester: 'Existing requester',
  self_referral: 'Self-referral',
  unsupported_payment_mode: 'Unsupported payment mode',
});

const formatBooleanState = (value) => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'unknown';
};

const formatProofAttributionCount = (value) => {
  if (value === 0) return '0';
  return formatReportMetric(value);
};

const getProofAttributionStateLabel = (item) =>
  PROOF_ATTRIBUTION_STATE_LABELS[
    cleanDisplayText(item?.previewEvaluation?.previewState, 80)
  ] || PROOF_ATTRIBUTION_STATE_LABELS.unknown;

const getProofAttributionPolicyLabel = (item) => {
  const policy = item?.policyEvaluation || {};
  return (
    cleanDisplayText(policy.label, 120) ||
    PROOF_REFERRAL_POLICY_STATE_LABELS[
      cleanDisplayText(policy.state, 80)
    ] ||
    PROOF_REFERRAL_POLICY_STATE_LABELS.unknown
  );
};

const formatProofAttributionPolicyReason = (item) => {
  const reasonCodes = Array.isArray(item?.policyEvaluation?.reasonCodes)
    ? item.policyEvaluation.reasonCodes
    : [];
  const firstReason = cleanDisplayText(reasonCodes[0], 120);
  return (
    PROOF_REFERRAL_POLICY_REASON_LABELS[firstReason] ||
    firstReason.replace(/_/g, ' ') ||
    'No policy reason'
  );
};

const getProofAttributionIssuanceLabel = (item) => {
  const outcome = cleanDisplayText(item?.issuanceDecision?.outcome || 'none', 80);
  return PROOF_REFERRAL_ISSUANCE_LABELS[outcome] || outcome.replace(/_/g, ' ');
};

const formatProofAttributionIssuanceReason = (item) => {
  const reasonCodes = Array.isArray(item?.issuanceDecision?.issuanceReasonCodes)
    ? item.issuanceDecision.issuanceReasonCodes
    : [];
  const firstReason = cleanDisplayText(reasonCodes[0], 120);
  return (
    PROOF_REFERRAL_ISSUANCE_REASON_LABELS[firstReason] ||
    PROOF_REFERRAL_POLICY_REASON_LABELS[firstReason] ||
    firstReason.replace(/_/g, ' ') ||
    'No issuance reason'
  );
};

const formatProofReferralConfigurationState = (state) => {
  const clean = cleanDisplayText(state, 80);
  if (clean === 'ready') return 'Ready';
  if (clean === 'explicitly_disabled') return 'Explicitly disabled';
  if (clean === 'configuration_invalid') return 'Configuration problem';
  return clean.replace(/_/g, ' ') || 'Unknown';
};

const getProofAttributionTaskTitle = (item) =>
  cleanDisplayText(item?.newTask?.title || 'Unknown attributed task', 160);

const getProofAttributionRefLabel = (item) =>
  cleanDisplayText(
    item?.sourceProof?.helper?.displayName ||
      item?.sourceProof?.refPlayerId ||
      item?.persistedAttribution?.refPlayerId ||
      'Unknown proof player',
    120,
  );

const formatProofAttributionAcquisition = (value) => {
  const clean = cleanDisplayText(value, 80);
  if (clean === 'new_requester') return 'New requester';
  if (clean === 'existing_requester') return 'Existing requester';
  return clean || 'Unknown';
};

const formatProofAttributionReportLine = (item) => {
  const evaluation = item?.previewEvaluation || {};
  const requesterFacts = item?.requesterAcquisitionFacts || {};
  const reasons = Array.isArray(evaluation.reasons)
    ? evaluation.reasons.join(', ')
    : '';
  const flags = Array.isArray(evaluation.integrityFlags)
    ? evaluation.integrityFlags.join(', ')
    : '';
  const policy = item?.policyEvaluation || {};
  const policyReasons = Array.isArray(policy.reasonCodes)
    ? policy.reasonCodes.join(', ')
    : '';
  const issuance = item?.issuanceDecision || {};
  const issuanceReasons = Array.isArray(issuance.issuanceReasonCodes)
    ? issuance.issuanceReasonCodes.join(', ')
    : '';
  return [
    `- ${getProofAttributionTaskTitle(item)}`,
    `state: ${getProofAttributionStateLabel(item)}`,
    `policy v1: ${getProofAttributionPolicyLabel(item)}`,
    `policy version: ${policy.policyVersion || 'unknown'}`,
    policyReasons ? `policy reasons: ${policyReasons}` : '',
    `issuance: ${getProofAttributionIssuanceLabel(item)}`,
    issuance.rewardAmountJGK
      ? `issued amount: ${formatCount(issuance.rewardAmountJGK)} JGK`
      : '',
    issuanceReasons ? `issuance reasons: ${issuanceReasons}` : '',
    issuance.issuedAt ? `issued at: ${formatDate(issuance.issuedAt)}` : '',
    `source player: ${getProofAttributionRefLabel(item)}`,
    `requester: ${requesterFacts.canonicalTaskCreatorPublicId || 'unknown'}`,
    `acquisition: ${formatProofAttributionAcquisition(
      requesterFacts.acquisitionState,
    )}`,
    `prior funded tasks: ${formatProofAttributionCount(
      requesterFacts.priorFundedTaskCount,
    )}`,
    `funded: ${formatBooleanState(evaluation.funded)}`,
    `accepted: ${formatBooleanState(evaluation.accepted)}`,
    `verified completed: ${formatBooleanState(evaluation.verifiedCompleted)}`,
    reasons ? `reasons: ${reasons}` : '',
    flags ? `integrity flags: ${flags}` : '',
    'no manual approval or payout controls exist in Ops Health',
  ]
    .filter(Boolean)
    .join(' / ');
};

export const buildLocalOpsAiReport = ({
  launchAttribution,
  operatorVisibility,
  proofAttributionReport,
  operatorActionability,
  landingChoiceRows,
  createSheetFunnelRows,
  createSheetJourneyRows,
  locationFunnelRows,
  demandSupplySignal,
  shareFunnelRows,
  generatedAt = new Date(),
  snapshotComparisons = {},
} = {}) => {
  const safeLaunch = launchAttribution || {};
  const safeOperator = operatorVisibility || {};
  const launchSnapshotComparison =
    snapshotComparisons.launchAttribution || {};
  const operatorSnapshotComparison =
    snapshotComparisons.operatorVisibility || {};
  const taskTitleLookup = buildTaskTitleLookup({
    launchAttribution: safeLaunch,
    operatorOfferingRows: safeOperator.offeringRows || [],
    operatorTaskRows: safeOperator.taskRows || [],
  });
  const safeShareRows = shareFunnelRows || buildShareFunnelRows(safeLaunch, taskTitleLookup);
  const safeActionability =
    operatorActionability ||
    buildOperatorActionability({
      operatorVisibility: safeOperator,
      shareFunnelRows: safeShareRows,
      now: safeOperator.generatedAt ? new Date(safeOperator.generatedAt) : new Date(),
    });
  const safeLandingChoiceRows =
    landingChoiceRows || buildLandingChoiceFunnelRows(safeLaunch);
  const safeCreateSheetFunnelRows =
    createSheetFunnelRows || buildCreateSheetFunnelRows(safeLaunch);
  const safeCreateSheetJourneyRows =
    createSheetJourneyRows || buildCreateSheetJourneyRows(safeLaunch);
  const safeCreateSheetJourneyDiagnostics =
    getCreateSheetJourneyDiagnostics(safeLaunch);
  const safeLocationFunnelRows =
    locationFunnelRows || buildLocationFunnelRows(safeLaunch);
  const safeDemandSupplySignal =
    demandSupplySignal || buildDemandSupplySignal(safeLaunch);
  const summary = safeOperator.systemSummary || {};
  const activation = safeOperator.activationFunnel || {};
  const magicLinkHandoff = safeOperator.magicLinkHandoff || {};
  const magicLinkHandoffScope = magicLinkHandoff.scope || {};
  const magicLinkHandoffCohort = magicLinkHandoff.newPlayerCohort || {};
  const magicLinkHandoffRate =
    magicLinkHandoffCohort.maturedCompletionRate || {};
  const magicLinkHandoffLifetime = magicLinkHandoff.lifetime || {};
  const magicLinkHandoffClassification = magicLinkHandoff.classification || {};
  const magicLinkExistingActivity =
    magicLinkHandoff.existingPlayerActivity || {};
  const magicLinkRawActivity = magicLinkHandoff.rawActivity || {};
  const location = safeOperator.locationMapReadiness || {};
  const shareTotals = safeLaunch.shareFunnelTotalsByEvent || {};
  const authTotals = safeLaunch.authTotalsByEvent || {};
  const visitTotals = safeLaunch.visitTotalsByEvent || {};
  const canonicalTotals = safeLaunch.totalsByEvent || {};
  const proofAttributionItems = Array.isArray(proofAttributionReport?.items)
    ? proofAttributionReport.items
    : [];
  const taskRows = safeActionability.taskRows || [];
  const taskRowsById = new Map(
    taskRows
      .map((row) => [cleanDisplayText(row?.taskId, 120), row])
      .filter(([taskId]) => !!taskId),
  );
  const region =
    safeOperator.filters?.marketSlug ||
    safeOperator.filters?.regionSlug ||
    safeLaunch.filters?.marketSlug ||
    safeLaunch.filters?.regionSlug ||
    'camden';
  const hasLocationFunnelEvents = safeLocationFunnelRows.some(
    (row) => row.hasLocationEvents,
  );

  const lines = [
    'Analyze this LocalOps Ops Health report. Identify the weakest funnel step, likely measurement gaps, immediate low-risk actions, and no-go risks. Separate confirmed task state from tracking/funnel attribution. Recommend the next 3-7 actions.',
    '',
    '# LocalOps Ops Health AI Report',
    '',
    ...createReportLines('Executive Snapshot', [
      `- Region/market: ${region}`,
      `- Generated for AI: ${formatDate(generatedAt)}`,
      `- Ops payload generatedAt: ${formatDate(safeOperator.generatedAt || safeLaunch.generatedAt)}`,
      `- Local players: ${formatReportMetric(summary.totalLocalPlayers)}`,
      `- Reachable players: ${formatReportMetric(summary.reachablePlayers)}`,
      `- Fresh saved locations: ${formatReportMetric(summary.mapReadyPlayers || location.saved)}`,
      `- Verified locals: ${formatReportMetric(summary.verifiedLocalPlayers)}`,
      `- Open funded tasks: ${formatReportMetric(summary.openFundedTasks)}`,
      `- Currently accepted tasks: ${formatReportMetric(summary.acceptedTasks)}`,
      `- Completed tasks: ${formatReportMetric(summary.completedTasks)}`,
      `- Active offerings: ${formatReportMetric(summary.availableOfferings)}`,
      `- High-priority alerts: ${(safeActionability.triageCards || []).length}`,
    ]),
    ...createReportLines('Snapshot Comparison and Current Report Totals', [
      ...buildOpsHealthComparisonReportLines(
        'Launch Attribution',
        launchSnapshotComparison,
      ),
      ...buildOpsHealthComparisonReportLines(
        'Operator Visibility',
        operatorSnapshotComparison,
      ),
      '- Rolling-window caveat: ' + OPS_HEALTH_ROLLING_WINDOW_CAVEAT,
      `- Current report task posts: ${formatCount(canonicalTotals.localops_task_published)}`,
      `- Current report task accepts: ${formatCount(canonicalTotals.localops_task_accepted)}`,
      `- Current report task completions: ${formatCount(canonicalTotals.localops_task_completion_verified)}`,
      `- Current report magic-link completions: ${formatCount(authTotals.localops_magic_link_completed)}`,
      `- Current report location saved/fresh: ${formatReportMetric(activation.locationSaved || location.saved)}`,
    ]),
    ...createReportLines('Demand/Supply Signal', [
      `- Signal: ${safeDemandSupplySignal.signal}`,
      `- Helper intent: ${safeDemandSupplySignal.earnLabel}`,
      `- Requester intent: ${safeDemandSupplySignal.helpLabel}`,
      `- Insight: ${safeDemandSupplySignal.insight}`,
      `- Recommended action: ${safeDemandSupplySignal.recommendation}`,
      ...safeLandingChoiceRows.map(
        (row) => `- ${row.label}: ${formatReportMetric(row.metric)} (${row.detail})`,
      ),
    ]),
    ...createReportLines('Requester Funnel', [
      `- Region landing viewed: ${formatCount(visitTotals.localops_region_landing_viewed)}`,
      `- I need help clicked: ${
        safeLandingChoiceRows.find((row) => row.key === 'need_help_clicked')?.metric?.label ||
        '--'
      }`,
      `- Post task clicked: ${
        safeLandingChoiceRows.find((row) => row.key === 'post_task_clicked')?.metric?.label ||
        '--'
      }`,
      '- Auth chooser opened: not tracked yet',
      `- Magic link requested: ${formatCount(authTotals.localops_magic_link_requested)}`,
      `- Magic link completed: ${formatCount(authTotals.localops_magic_link_completed)}`,
      `- PlayerMap reached: ${formatReportMetric(activation.playerMapArrivals)}`,
      ...safeCreateSheetFunnelRows.map(
        (row) => `- ${row.label}: ${formatReportMetric(row.metric)} (${row.detail})`,
      ),
      ...safeCreateSheetJourneyRows.map(
        (row) => `- Journey ${row.label}: ${formatReportMetric(row.metric)} (${row.detail})`,
      ),
      ...(safeCreateSheetJourneyDiagnostics?.blockerBreakdown?.length
        ? safeCreateSheetJourneyDiagnostics.blockerBreakdown
            .slice(0, 6)
            .map(
              (row) =>
                `- Journey blocker: ${row.firstBlockingField || 'unknown'} / reasons: ${(row.reasonCodes || []).join(', ') || 'unknown'} / flows: ${formatCount(row.flowCount)} / events: ${formatCount(row.count)}`,
            )
        : ['- Journey blockers: none reported yet']),
      `- Task created/funded/published: ${formatCount(canonicalTotals.localops_task_published)}`,
      '- Caveat: journey diagnostics are client-reported tracking. Backend task events remain authoritative task creation truth.',
    ]),
    ...createReportLines('Helper/Accept Funnel', [
      `- Earn intent clicked: ${
        safeLandingChoiceRows.find((row) => row.key === 'want_to_earn_clicked')?.metric?.label ||
        '--'
      }`,
      '- Helper profile / Ride Ready path: see Useful Players / Helpers in dashboard.',
      `- Task viewed/share landings: ${formatCount(shareTotals.localops_share_landing_viewed)}`,
      `- Accept clicked: ${formatCount(shareTotals.localops_share_task_accept_clicked)}`,
      `- Share accept success: ${formatCount(shareTotals.localops_share_task_accept_succeeded)}`,
      `- Confirmed task accepted: ${formatCount(canonicalTotals.localops_task_accepted)}`,
      `- Completed: ${formatCount(canonicalTotals.localops_task_completion_verified)}`,
    ]),
    ...createReportLines('Auth/Magic Link Funnel', [
      `- Continue started: ${formatCount(shareTotals.localops_share_continue_started)}`,
      `- Magic requested: ${formatCount(authTotals.localops_magic_link_requested)}`,
      `- Magic completed: ${formatCount(authTotals.localops_magic_link_completed)}`,
      '- Failed/expired: not tracked yet',
      '- Caveat: auth drop may be visibility/friction signal, not proof OAuth is required.',
    ]),
    ...createReportLines('Magic Link Handoff', [
      `- Measurement: ${
        magicLinkHandoffScope.label ||
        'Current outcomes for new magic-link Players provisioned during the selected window.'
      }`,
      `- Provisioning window: ${formatDate(
        magicLinkHandoffScope.from,
      )} through ${formatDate(magicLinkHandoffScope.to)}`,
      `- Current outcome classification as of: ${formatDate(
        magicLinkHandoffScope.classificationAsOf ||
          magicLinkHandoffClassification.asOf,
      )}`,
      `- Classification status: ${
        magicLinkHandoffClassification.status || 'unavailable'
      }${
        magicLinkHandoffClassification.reason
          ? ` / ${magicLinkHandoffClassification.reason}`
          : ''
      }`,
      `- Configured link lifetime: ${
        magicLinkHandoffLifetime.label || 'Unavailable'
      }`,
      `- New Players provisioned: ${formatReportMetric(
        magicLinkHandoffCohort.provisioned,
      )}`,
      `- Current completed outcomes: ${formatReportMetric(
        magicLinkHandoffCohort.completed,
      )}`,
      `- Current incomplete outcomes: ${formatReportMetric(
        magicLinkHandoffCohort.incomplete,
      )}`,
      `- Pending: ${formatReportMetric(magicLinkHandoffCohort.pending)}`,
      `- Provisioned, not completed: ${formatReportMetric(
        magicLinkHandoffCohort.provisionedNotCompleted,
      )}`,
      `- Timing unknown: ${formatReportMetric(
        magicLinkHandoffCohort.timingUnknown,
      )}`,
      `- Matured completion rate: ${formatMagicLinkHandoffRate(
        magicLinkHandoffRate,
      )}`,
      `- Existing-Player request events: ${formatReportMetric(
        magicLinkExistingActivity.requestedEvents,
      )}`,
      `- Existing-Player completed-login events: ${formatReportMetric(
        magicLinkExistingActivity.completedLoginEvents,
      )}`,
      `- Filtered raw requested events: ${formatReportMetric(
        magicLinkRawActivity.requestedEvents,
      )}`,
      `- Filtered raw completed events: ${formatReportMetric(
        magicLinkRawActivity.completedEvents,
      )}`,
      '- Scope: the unique-Player cohort is global Player-record data; attribution event activity uses the selected report filters.',
      '- Window semantics: the selected window defines provisioning cohort membership. Outcomes are current as of classificationAsOf, so completion after the selected to-date still counts as completed.',
      '- Rate semantics: matured completion rate excludes pending and timing-unknown Players and is not an in-window conversion rate.',
      '- Reporting behavior: current handoff state is not persisted into Ops Health snapshots and has no delta or live-change styling.',
    ]),
    ...createReportLines('Location Permission / Fresh Saved Location Funnel', [
      ...(hasLocationFunnelEvents
        ? safeLocationFunnelRows.map(
            (row) =>
              `- ${row.label}: ${formatReportMetric(row.metric)} (${row.detail})`,
          )
        : ['- No location gate events recorded yet.']),
      `- Location saved/fresh: ${formatReportMetric(activation.locationSaved || location.saved)}`,
      `- Fresh saved location: ${formatReportMetric(location.saved)}`,
      `- Missing location: ${formatReportMetric(location.missing)}`,
      `- Stale location: ${formatReportMetric(location.stale)}`,
      '- Caveat: no location telemetry means no recorded location-gate events, not user failure.',
    ]),
    ...createReportLines('Task Creation / Accept / Completion State', [
      ...(taskRows.length
        ? taskRows.slice(0, 12).map(formatTaskReportLine)
        : ['- No task rows loaded.']),
    ]),
    ...(proofAttributionReport
      ? createReportLines('Proof Attribution Preview', [
          `- Issuance configuration: ${formatProofReferralConfigurationState(
            proofAttributionReport?.issuanceControlPreview?.configurationState,
          )}`,
          `- Funding source: ${
            proofAttributionReport?.issuanceControlPreview?.fundingSource ||
            'unknown'
          }`,
          `- Budget timezone: ${
            proofAttributionReport?.issuanceControlPreview?.budgetTimezone ||
            'unknown'
          }`,
          ...(proofAttributionItems.length
            ? proofAttributionItems
                .slice(0, 12)
                .map(formatProofAttributionReportLine)
            : ['- No proof attribution sidecars loaded for this view.']),
          '- Caveat: this is a read-only preview. It does not expose approval, retry, repair, or issue controls.',
        ])
      : []),
    ...createReportLines('Share Funnel Attribution', [
      ...(safeShareRows.length
        ? safeShareRows.slice(0, 12).map((row) => formatShareReportLine(row, taskRowsById))
        : ['- No share funnel rows loaded.']),
    ]),
    ...createReportLines('Current Triage Cards', [
      ...((safeActionability.triageCards || []).map(
        (card) =>
          `- ${card.title}: ${card.label} / count: ${formatCount(card.count)} / confidence: ${card.confidence || 'Unknown'} / reason: ${card.reason} / action: ${card.action} / evidence: ${card.evidence}`,
      ) || ['- No triage cards loaded.']),
    ]),
    ...createReportLines('Measurement Caveats', [
      '- Share accept success is not the same as confirmed task acceptance.',
      '- Low Fresh Saved Location means low fresh saved location, not failed browsing.',
      '- Auth drop may be visibility/friction signal, not proof OAuth is required.',
      '- Browse-first behavior can defer location saving until a protected action.',
      '- Create Sheet journey diagnostics can show what the UI reported; they do not prove user intent or backend task creation.',
      '- Every inferred problem should be treated by confidence level, not as certainty.',
    ]),
    ...createReportLines('Recommended Questions for Analysis', [
      '- Where is the largest confirmed drop-off?',
      '- Is the weak step measurement or actual behavior?',
      '- Which task needs operator action now?',
      '- Are helper supply and requester demand balanced?',
      '- Which change should be tested next?',
      '- What should not be changed yet?',
    ]),
    ...createReportLines('Debug Appendix', [
      `- Task IDs: ${truncateReportList((safeOperator.taskRows || []).map((row) => row.taskId))}`,
      `- Creator/requester IDs: ${truncateReportList((safeOperator.taskRows || []).map((row) => row.creatorPlayerId))}`,
      `- Helper IDs: ${truncateReportList((safeOperator.taskRows || []).map((row) => row.acceptedHelperPlayerId))}`,
      `- Event keys: ${truncateReportList([
        ...Object.keys(safeLaunch.totalsByEvent || {}),
        ...Object.keys(safeLaunch.visitTotalsByEvent || {}),
        ...Object.keys(safeLaunch.authTotalsByEvent || {}),
        ...Object.keys(safeLaunch.shareFunnelTotalsByEvent || {}),
      ])}`,
      `- Source row IDs: ${truncateReportList([
        ...(safeOperator.sourceAttributionRows || []).map((row) => row.sourceKey),
        ...safeShareRows.map((row) => row.sourceKey),
      ])}`,
      ...(proofAttributionReport
        ? [
            `- Proof attribution sidecar IDs: ${truncateReportList(
              proofAttributionItems.map((item) => item?.persistedAttribution?.id),
            )}`,
          ]
        : []),
    ]),
  ];

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const buildAiReportFilename = ({ launchAttribution, operatorVisibility, now = new Date() }) => {
  const date = now instanceof Date ? now : new Date(now);
  const stamp = Number.isNaN(date.getTime())
    ? 'unknown-date'
    : `${date.toISOString().slice(0, 10)}-${date
        .toISOString()
        .slice(11, 16)
        .replace(':', '')}`;
  const region =
    operatorVisibility?.filters?.marketSlug ||
    operatorVisibility?.filters?.regionSlug ||
    launchAttribution?.filters?.marketSlug ||
    launchAttribution?.filters?.regionSlug ||
    'camden';
  return `localops-ops-health-${stamp}-${cleanDisplayText(region, 40) || 'region'}.md`;
};

const DebugIdDetails = ({ summary = 'Debug IDs available', items = [] }) => {
  const safeItems = items
    .filter((item) => item?.value)
    .map((item) => ({
      label: item.label,
      value: formatShortId(item.value),
      rawValue: cleanDisplayText(item.value, 160),
    }));
  if (!safeItems.length) return null;
  return (
    <details className="ops-health-debug-details">
      <summary>{summary}</summary>
      {safeItems.map((item) => (
        <small key={`${item.label}-${item.rawValue}`}>
          {item.label}: {item.value}
        </small>
      ))}
    </details>
  );
};

const ActionabilityNote = ({ actionability }) =>
  actionability ? (
    <div className="ops-health-actionability-note">
      <strong>{actionability.label}</strong>
      <span>{actionability.reason}</span>
      <small>{actionability.evidence}</small>
    </div>
  ) : null;

const PlayerUsefulnessNote = ({ usefulness }) =>
  usefulness ? (
    <div className="ops-health-actionability-note">
      <strong>{usefulness.label}</strong>
      <small>{usefulness.evidence}</small>
    </div>
  ) : null;

const TriageCard = ({ card, delta }) => (
  <article
    className={`ops-health-triage-card${
      shouldHighlightDelta(delta) ? ' ops-health-triage-card--changed' : ''
    }`}
  >
    <div>
      <strong>{card.title}</strong>
      <span>
        {formatCount(card.count)}
        <DeltaBadge delta={delta} />
      </span>
    </div>
    <p>{card.label}</p>
    <small>{card.reason}</small>
    <em>{card.action}</em>
    {card.confidence ? <small>Confidence: {card.confidence}</small> : null}
    {card.confidenceReason ? <small>Reason: {card.confidenceReason}</small> : null}
    {card.operatorTranslation ? (
      <small>Operator translation: {card.operatorTranslation}</small>
    ) : null}
    <small>{card.evidence}</small>
  </article>
);

const JsonDetails = ({ title, payload, testId }) => (
  <details className="ops-health-json" data-testid={testId}>
    <summary>{title}</summary>
    <pre>{JSON.stringify(payload || {}, null, 2)}</pre>
  </details>
);

export default function LocalOpsOpsHealth() {
  const { player } = useContext(AuthContext);
  const { socket } = useSocket() || {};
  const allowed = canViewLocalOpsOps(player);
  const [readiness, setReadiness] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [taskFilters, setTaskFilters] = useState(() =>
    buildDefaultTaskFinderFilters(),
  );
  const [taskResults, setTaskResults] = useState([]);
  const [taskReport, setTaskReport] = useState(null);
  const [settlementRows, setSettlementRows] = useState([]);
  const [taskId, setTaskId] = useState('');
  const [loading, setLoading] = useState(false);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementActionBusy, setSettlementActionBusy] = useState('');
  const [error, setError] = useState('');
  const [taskSearchError, setTaskSearchError] = useState('');
  const [taskError, setTaskError] = useState('');
  const [settlementError, setSettlementError] = useState('');
  const [settlementNotice, setSettlementNotice] = useState('');
  const [launchAttribution, setLaunchAttribution] = useState(null);
  const launchAttributionRef = useRef(null);
  const launchVisitBaselineRef = useRef({
    initialized: false,
    envelope: null,
  });
  const launchRequestSequenceRef = useRef(0);
  const launchAttributionRefreshTimerRef = useRef(null);
  const opsHealthFocusRefreshTimerRef = useRef(null);
  const launchAttributionPendingWhileHiddenRef = useRef(false);
  const [launchAttributionLoading, setLaunchAttributionLoading] = useState(false);
  const [launchAttributionError, setLaunchAttributionError] = useState('');
  const [launchRealtimeStatus, setLaunchRealtimeStatus] = useState('manual');
  const [launchDeltaItems, setLaunchDeltaItems] = useState([]);
  const [launchLiveDeltaItems, setLaunchLiveDeltaItems] = useState([]);
  const [launchRefreshTrigger, setLaunchRefreshTrigger] = useState('');
  const [launchComparisonState, setLaunchComparisonState] = useState({
    status: 'idle',
    baseline: null,
    current: null,
    overlapDurationMs: 0,
  });
  const [sourceShareControls, setSourceShareControls] = useState(
    DEFAULT_SOURCE_SHARE_CONTROLS,
  );
  const [launchAttributionRefreshedAt, setLaunchAttributionRefreshedAt] =
    useState(null);
  const [operatorVisibility, setOperatorVisibility] = useState(null);
  const operatorVisibilityRef = useRef(null);
  const operatorVisitBaselineRef = useRef({
    initialized: false,
    envelope: null,
  });
  const operatorRequestSequenceRef = useRef(0);
  const [operatorVisibilityLoading, setOperatorVisibilityLoading] =
    useState(false);
  const [operatorVisibilityError, setOperatorVisibilityError] = useState('');
  const [operatorVisibilityRefreshedAt, setOperatorVisibilityRefreshedAt] =
    useState(null);
  const [operatorDeltaItems, setOperatorDeltaItems] = useState([]);
  const [operatorLiveDeltaItems, setOperatorLiveDeltaItems] = useState([]);
  const [operatorRefreshTrigger, setOperatorRefreshTrigger] = useState('');
  const [operatorComparisonState, setOperatorComparisonState] = useState({
    status: 'idle',
    baseline: null,
    current: null,
    overlapDurationMs: 0,
  });
  const proofAttributionAdminEnabled = isProofAttributionAdminEnabled();
  const [proofAttributionReport, setProofAttributionReport] = useState(null);
  const [proofAttributionLoading, setProofAttributionLoading] = useState(false);
  const [proofAttributionError, setProofAttributionError] = useState('');
  const [conciergeForm, setConciergeForm] = useState(() =>
    buildDefaultConciergeIntakeForm(),
  );
  const [conciergeSending, setConciergeSending] = useState(false);
  const [conciergeError, setConciergeError] = useState('');
  const [conciergeResult, setConciergeResult] = useState(null);
  const [roleGrantForm, setRoleGrantForm] = useState(() =>
    buildDefaultRoleGrantForm(),
  );
  const [roleGrantLookup, setRoleGrantLookup] = useState(null);
  const [roleGrantLoading, setRoleGrantLoading] = useState(false);
  const [roleGrantSaving, setRoleGrantSaving] = useState(false);
  const [roleGrantError, setRoleGrantError] = useState('');
  const [roleGrantNotice, setRoleGrantNotice] = useState('');
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [aiReportNotice, setAiReportNotice] = useState('');
  const [aiReportFallback, setAiReportFallback] = useState('');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );
  const launchSnapshotKey = useMemo(
    () =>
      buildOpsHealthSnapshotKey({
        reportType: 'launchAttribution',
        filters: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
      }),
    [],
  );
  const operatorSnapshotKey = useMemo(
    () =>
      buildOpsHealthSnapshotKey({
        reportType: 'operatorVisibility',
        filters: { marketSlug: 'camden' },
      }),
    [],
  );

  useEffect(
    () => () => {
      launchRequestSequenceRef.current += 1;
      operatorRequestSequenceRef.current += 1;
    },
    [],
  );


  useEffect(() => {
    if (!allowed) return;
    try {
      const remembered = window.localStorage.getItem(
        LOCALOPS_OPS_LAST_TASK_ID_KEY,
      );
      if (remembered) setTaskId((current) => current || remembered);
    } catch (err) {
      // Ignore localStorage issues; this page should still run diagnostics.
    }
  }, [allowed]);

  const refreshReports = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const [readinessRes, rollupRes] = await Promise.all([
        axiosConn.get('local-ops/debug/readiness', authConfig),
        axiosConn.get('local-ops/debug/accounting/rollup', authConfig),
      ]);
      setReadiness(readinessRes?.data?.data || readinessRes?.data || null);
      setRollup(rollupRes?.data?.data || rollupRes?.data || null);
      setLastCheckedAt(new Date().toISOString());
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load LocalOps ops health.',
      );
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchSettlementQueue = useCallback(async () => {
    if (!allowed) return;
    setSettlementLoading(true);
    setSettlementError('');
    try {
      const res = await axiosConn.get(
        'local-ops/debug/settlements/orders',
        authConfig,
      );
      const payload = res?.data?.data || res?.data || {};
      setSettlementRows(payload.rows || []);
    } catch (err) {
      setSettlementRows([]);
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load offering settlement queue.',
      );
    } finally {
      setSettlementLoading(false);
    }
  }, [allowed, authConfig]);

  const fetchLaunchAttribution = useCallback(async (options = {}) => {
    if (!allowed) return;
    const requestId = ++launchRequestSequenceRef.current;
    const refreshTrigger = options.reason || 'initial load';
    setLaunchAttributionLoading(true);
    setLaunchAttributionError('');
    try {
      const res = await axiosConn.get(LAUNCH_ATTRIBUTION_ENDPOINT, {
        ...authConfig,
        params: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
      });
      if (requestId !== launchRequestSequenceRef.current) return;
      const nextReport = res?.data?.data || res?.data || null;
      const capturedAt = new Date().toISOString();
      const previousRenderedSnapshot = launchAttributionRef.current
        ? buildLaunchAttributionSnapshot(launchAttributionRef.current)
        : null;
      const nextSnapshot = buildLaunchAttributionSnapshot(nextReport);
      const nextEnvelope = buildOpsHealthSnapshotEnvelope({
        reportType: 'launchAttribution',
        report: nextReport,
        snapshot: nextSnapshot,
        capturedAt,
      });
      const baselineState = launchVisitBaselineRef.current;
      const baselineCandidate = baselineState.initialized
        ? baselineState.envelope
        : readOpsHealthSnapshot(launchSnapshotKey);
      const resolution = resolveOpsHealthSnapshotComparison({
        baseline: baselineCandidate,
        current: nextEnvelope,
        reportType: 'launchAttribution',
        expectedScope: CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
        nowMs: new Date(capturedAt).getTime(),
      });
      launchVisitBaselineRef.current = {
        initialized: true,
        envelope: resolution.activeBaseline,
      };
      const deltas = resolution.shouldShowDeltas
        ? buildLaunchAttributionDeltas(
            resolution.activeBaseline.snapshot,
            nextSnapshot,
          )
        : [];
      const liveDeltas =
        resolution.currentValid && previousRenderedSnapshot
          ? markLiveDeltas(
              buildLaunchAttributionDeltas(
                previousRenderedSnapshot,
                nextSnapshot,
              ),
            )
          : [];
      if (resolution.currentValid) {
        launchAttributionRef.current = nextReport;
      } else {
        launchAttributionRef.current = null;
      }
      setLaunchAttribution(nextReport);
      setLaunchAttributionRefreshedAt(capturedAt);
      setLaunchLiveDeltaItems(liveDeltas);
      setLaunchDeltaItems(deltas);
      setLaunchRefreshTrigger(refreshTrigger);
      setLaunchComparisonState({
        status: resolution.comparable
          ? deltas.length
            ? 'deltas'
            : 'no_changes'
          : resolution.status,
        baseline: getOpsHealthSnapshotEnvelopeMeta(resolution.activeBaseline),
        current: getOpsHealthSnapshotEnvelopeMeta(nextEnvelope),
        overlapDurationMs: resolution.overlapDurationMs,
      });
      if (resolution.shouldPersistCurrent) {
        writeOpsHealthSnapshot(launchSnapshotKey, nextEnvelope);
      }
    } catch (err) {
      if (requestId !== launchRequestSequenceRef.current) return;
      setLaunchAttribution(null);
      launchAttributionRef.current = null;
      setLaunchLiveDeltaItems([]);
      setLaunchDeltaItems([]);
      setLaunchRefreshTrigger(refreshTrigger);
      setLaunchComparisonState((current) => ({
        ...current,
        status: 'fetch_error',
        current: null,
      }));
      setLaunchAttributionError('Unable to load launch attribution.');
    } finally {
      if (requestId === launchRequestSequenceRef.current) {
        setLaunchAttributionLoading(false);
      }
    }
  }, [allowed, authConfig, launchSnapshotKey]);

  const fetchOperatorVisibility = useCallback(async (options = {}) => {
    if (!allowed) return;
    const requestId = ++operatorRequestSequenceRef.current;
    const refreshTrigger = options.reason || 'initial load';
    setOperatorVisibilityLoading(true);
    setOperatorVisibilityError('');
    try {
      const res = await axiosConn.get(OPERATOR_VISIBILITY_ENDPOINT, {
        ...authConfig,
        params: { market: 'camden' },
      });
      if (requestId !== operatorRequestSequenceRef.current) return;
      const nextReport = res?.data?.data || res?.data || null;
      const capturedAt = new Date().toISOString();
      const previousRenderedSnapshot = operatorVisibilityRef.current
        ? buildOperatorVisibilitySnapshot(operatorVisibilityRef.current)
        : null;
      const nextSnapshot = buildOperatorVisibilitySnapshot(nextReport);
      const nextEnvelope = buildOpsHealthSnapshotEnvelope({
        reportType: 'operatorVisibility',
        report: nextReport,
        snapshot: nextSnapshot,
        capturedAt,
      });
      const baselineState = operatorVisitBaselineRef.current;
      const baselineCandidate = baselineState.initialized
        ? baselineState.envelope
        : readOpsHealthSnapshot(operatorSnapshotKey);
      const resolution = resolveOpsHealthSnapshotComparison({
        baseline: baselineCandidate,
        current: nextEnvelope,
        reportType: 'operatorVisibility',
        expectedScope: { regionSlug: 'camden', marketSlug: 'camden' },
        nowMs: new Date(capturedAt).getTime(),
      });
      operatorVisitBaselineRef.current = {
        initialized: true,
        envelope: resolution.activeBaseline,
      };
      const deltas = resolution.shouldShowDeltas
        ? buildOperatorVisibilityDeltas(
            resolution.activeBaseline.snapshot,
            nextSnapshot,
          )
        : [];
      const liveDeltas =
        resolution.currentValid && previousRenderedSnapshot
          ? markLiveDeltas(
              buildOperatorVisibilityDeltas(
                previousRenderedSnapshot,
                nextSnapshot,
              ),
            )
          : [];
      if (resolution.currentValid) {
        operatorVisibilityRef.current = nextReport;
      } else {
        operatorVisibilityRef.current = null;
      }
      setOperatorVisibility(nextReport);
      setOperatorVisibilityRefreshedAt(capturedAt);
      setOperatorDeltaItems(deltas);
      setOperatorLiveDeltaItems(liveDeltas);
      setOperatorRefreshTrigger(refreshTrigger);
      setOperatorComparisonState({
        status: resolution.comparable
          ? deltas.length
            ? 'deltas'
            : 'no_changes'
          : resolution.status,
        baseline: getOpsHealthSnapshotEnvelopeMeta(resolution.activeBaseline),
        current: getOpsHealthSnapshotEnvelopeMeta(nextEnvelope),
        overlapDurationMs: resolution.overlapDurationMs,
      });
      if (resolution.shouldPersistCurrent) {
        writeOpsHealthSnapshot(operatorSnapshotKey, nextEnvelope);
      }
    } catch (err) {
      if (requestId !== operatorRequestSequenceRef.current) return;
      setOperatorVisibility(null);
      operatorVisibilityRef.current = null;
      setOperatorDeltaItems([]);
      setOperatorLiveDeltaItems([]);
      setOperatorRefreshTrigger(refreshTrigger);
      setOperatorComparisonState((current) => ({
        ...current,
        status: 'fetch_error',
        current: null,
      }));
      setOperatorVisibilityError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load operator visibility.',
      );
    } finally {
      if (requestId === operatorRequestSequenceRef.current) {
        setOperatorVisibilityLoading(false);
      }
    }
  }, [allowed, authConfig, operatorSnapshotKey]);
  const fetchProofAttributions = useCallback(async () => {
    if (!allowed || !proofAttributionAdminEnabled) return;
    setProofAttributionLoading(true);
    setProofAttributionError('');
    try {
      const res = await axiosConn.get(PROOF_ATTRIBUTION_ENDPOINT, {
        ...authConfig,
        params: PROOF_ATTRIBUTION_ADMIN_PARAMS,
      });
      setProofAttributionReport(res?.data?.data || res?.data || null);
    } catch (err) {
      setProofAttributionReport(null);
      setProofAttributionError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load proof attribution preview.',
      );
    } finally {
      setProofAttributionLoading(false);
    }
  }, [allowed, authConfig, proofAttributionAdminEnabled]);

  useEffect(() => {
    if (allowed && proofAttributionAdminEnabled) return;
    setProofAttributionReport(null);
    setProofAttributionError('');
    setProofAttributionLoading(false);
  }, [allowed, proofAttributionAdminEnabled]);

  useEffect(() => {
    if (!launchLiveDeltaItems.length) return undefined;
    const timer = window.setTimeout(() => {
      setLaunchLiveDeltaItems([]);
    }, LIVE_DELTA_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [launchLiveDeltaItems]);

  useEffect(() => {
    if (!operatorLiveDeltaItems.length) return undefined;
    const timer = window.setTimeout(() => {
      setOperatorLiveDeltaItems([]);
    }, LIVE_DELTA_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [operatorLiveDeltaItems]);

  useEffect(() => {
    if (!allowed || !socket?.on || !socket?.off || !socket?.emit) {
      setLaunchRealtimeStatus('manual');
      return undefined;
    }

    let active = true;
    const subscribe = () => {
      if (!socket.connected) {
        setLaunchRealtimeStatus('manual');
        return;
      }
      socket.emit(
        'localops:ops:subscribe_launch_attribution',
        CAMDEN_LAUNCH_ATTRIBUTION_PARAMS,
        (ack = {}) => {
          if (!active) return;
          setLaunchRealtimeStatus(ack?.success ? 'live' : 'manual');
        },
      );
    };
    const scheduleRefresh = (payload = {}) => {
      const marketSlug = String(payload?.marketSlug || '').trim();
      if (marketSlug && marketSlug !== CAMDEN_LAUNCH_ATTRIBUTION_PARAMS.marketSlug) {
        return;
      }
      if (typeof document !== 'undefined' && document.hidden) {
        launchAttributionPendingWhileHiddenRef.current = true;
        return;
      }
      window.clearTimeout(launchAttributionRefreshTimerRef.current);
      launchAttributionRefreshTimerRef.current = window.setTimeout(() => {
        fetchLaunchAttribution({ reason: 'socket invalidation' });
        fetchOperatorVisibility({ reason: 'socket invalidation' });
        fetchProofAttributions();
      }, 900);
    };
    const handleConnect = () => subscribe();
    const handleDisconnect = () => setLaunchRealtimeStatus('manual');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('localops:launch_attribution_changed', scheduleRefresh);
    subscribe();

    return () => {
      active = false;
      window.clearTimeout(launchAttributionRefreshTimerRef.current);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('localops:launch_attribution_changed', scheduleRefresh);
    };
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchProofAttributions,
    socket,
  ]);

  useEffect(() => {
    if (!allowed) return undefined;
    const scheduleFocusRefresh = (reason = 'tab focus') => {
      if (document.hidden) return;
      window.clearTimeout(opsHealthFocusRefreshTimerRef.current);
      opsHealthFocusRefreshTimerRef.current = window.setTimeout(() => {
        refreshReports();
        fetchLaunchAttribution({ reason });
        fetchOperatorVisibility({ reason });
        fetchProofAttributions();
      }, 900);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const hadPendingInvalidation =
        launchAttributionPendingWhileHiddenRef.current;
      launchAttributionPendingWhileHiddenRef.current = false;
      scheduleFocusRefresh(
        hadPendingInvalidation ? 'hidden socket invalidation' : 'tab focus',
      );
    };
    const handleWindowFocus = () => scheduleFocusRefresh('tab focus');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.clearTimeout(opsHealthFocusRefreshTimerRef.current);
    };
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchProofAttributions,
    refreshReports,
  ]);

  const fetchTaskResults = useCallback(
    async (filters) => {
      if (!allowed) return;
      setTaskSearchLoading(true);
      setTaskSearchError('');
      try {
        const res = await axiosConn.get('local-ops/debug/accounting/tasks', {
          ...authConfig,
          params: buildTaskFinderParams(filters),
        });
        const payload = res?.data?.data || res?.data || {};
        setTaskResults(payload.tasks || payload.rows || []);
      } catch (err) {
        setTaskResults([]);
        setTaskSearchError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to find recent LocalOps tasks.',
        );
      } finally {
        setTaskSearchLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const refreshAll = useCallback(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution({ reason: 'manual refresh' });
    fetchTaskResults(taskFilters);
    fetchOperatorVisibility({ reason: 'manual refresh' });
    fetchProofAttributions();
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchProofAttributions,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
    taskFilters,
  ]);

  useEffect(() => {
    if (!allowed) return;
    refreshReports();
    fetchSettlementQueue();
    fetchLaunchAttribution({ reason: 'initial load' });
    fetchTaskResults(taskFilters);
    fetchOperatorVisibility({ reason: 'initial load' });
    fetchProofAttributions();
  }, [
    allowed,
    fetchLaunchAttribution,
    fetchOperatorVisibility,
    fetchProofAttributions,
    fetchSettlementQueue,
    fetchTaskResults,
    refreshReports,
  ]);

  const loadTaskReport = useCallback(
    async (nextTaskId) => {
      if (!allowed) return;
      const trimmedTaskId = String(nextTaskId || '').trim();
      if (!trimmedTaskId) {
        setTaskError('Enter a task id.');
        return;
      }

      setTaskId(trimmedTaskId);
      setTaskLoading(true);
      setTaskError('');
      try {
        const res = await axiosConn.get(
          `local-ops/debug/accounting/task/${encodeURIComponent(trimmedTaskId)}`,
          authConfig,
        );
        setTaskReport(res?.data?.data || res?.data || null);
        try {
          window.localStorage.setItem(
            LOCALOPS_OPS_LAST_TASK_ID_KEY,
            trimmedTaskId,
          );
        } catch (err) {
          // Last-task memory is optional and must never block the report.
        }
      } catch (err) {
        setTaskReport(null);
        setTaskError(
          err?.response?.data?.message ||
            err?.message ||
            'Unable to load task accounting report.',
        );
      } finally {
        setTaskLoading(false);
      }
    },
    [allowed, authConfig],
  );

  const handleTaskSearch = (event) => {
    event.preventDefault();
    if (!allowed) return;
    fetchTaskResults(taskFilters);
  };

  const updateSourceShareControl = (field, value) => {
    setSourceShareControls((current) => ({
      ...current,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const handleTaskLookup = async (event) => {
    event.preventDefault();
    await loadTaskReport(taskId);
  };

  const updateTaskFilter = (field, value) => {
    setTaskFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSettlementAction = async (row, action) => {
    if (!allowed || !row?.orderId) return;
    const isRefund = action === 'refund_buyer';
    const confirmation = isRefund
      ? 'Refund buyer? This returns held JGK to the buyer and restores finite offering quantity if applicable.'
      : 'Pay seller? This releases held JGK to the seller and marks the order fulfilled.';
    const confirmed = window.confirm(confirmation);
    if (!confirmed) return;

    setSettlementActionBusy(`${row.orderId}:${action}`);
    setSettlementError('');
    setSettlementNotice('');
    try {
      await axiosConn.post(
        `local-ops/debug/settlements/orders/${encodeURIComponent(
          row.orderId,
        )}/resolve`,
        {
          action,
          reason: 'ops_health_settlement_queue',
        },
        authConfig,
      );
      setSettlementNotice(
        isRefund
          ? 'Buyer refunded; held JGK returned.'
          : 'Seller paid; order marked fulfilled.',
      );
      await fetchSettlementQueue();
      await fetchOperatorVisibility({ reason: 'manual refresh' });
    } catch (err) {
      setSettlementError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to resolve this settlement.',
      );
    } finally {
      setSettlementActionBusy('');
    }
  };

  const updateConciergeForm = (field, value) => {
    setConciergeForm((current) => ({
      ...current,
      [field]: value,
    }));
    setConciergeError('');
  };

  const handleConciergeSubmit = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    const deliveryAddress = conciergeForm.deliveryAddress.trim();
    const customerEmail = conciergeForm.customerEmail.trim();
    if (!deliveryAddress) {
      setConciergeError('Destination is required.');
      return;
    }
    if (!customerEmail) {
      setConciergeError('Customer email is required.');
      return;
    }

    setConciergeSending(true);
    setConciergeError('');
    setConciergeResult(null);
    try {
      const res = await axiosConn.post(
        CONCIERGE_INTAKE_ENDPOINT,
        {
          intakeType: 'collision_specialist_ride',
          partnerSource: 'collision-specialist-camden',
          customerEmail,
          customerPhone: conciergeForm.customerPhone.trim() || undefined,
          deliveryAddress,
          notes: conciergeForm.notes.trim() || undefined,
        },
        authConfig,
      );
      setConciergeResult(res?.data?.data || res?.data || {});
      await fetchOperatorVisibility({ reason: 'manual refresh' });
      setConciergeForm((current) => ({
        ...buildDefaultConciergeIntakeForm(),
        customerPhone: current.customerPhone,
      }));
    } catch (err) {
      setConciergeError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to send the concierge magic link.',
      );
    } finally {
      setConciergeSending(false);
    }
  };

  const updateRoleGrantForm = (field, value) => {
    setRoleGrantForm((current) => ({
      ...current,
      [field]: value,
    }));
    setRoleGrantError('');
    setRoleGrantNotice('');
    if (field === 'playerId') setRoleGrantLookup(null);
  };

  const handleRoleGrantLookup = async (event) => {
    event.preventDefault();
    if (!allowed) return;
    const playerId = roleGrantForm.playerId.trim();
    if (!playerId) {
      setRoleGrantError('playerId is required.');
      return;
    }

    setRoleGrantLoading(true);
    setRoleGrantError('');
    setRoleGrantNotice('');
    try {
      const res = await axiosConn.get(
        `${ROLE_GRANTS_LOOKUP_ENDPOINT}/${encodeURIComponent(playerId)}`,
        authConfig,
      );
      setRoleGrantLookup(res?.data?.data || res?.data || null);
    } catch (err) {
      setRoleGrantLookup(null);
      setRoleGrantError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to lookup this playerId.',
      );
    } finally {
      setRoleGrantLoading(false);
    }
  };

  const buildRoleGrantPayload = () => ({
    playerId: roleGrantForm.playerId.trim(),
    role: roleGrantForm.role,
    regionSlug: roleGrantForm.regionSlug,
    note: roleGrantForm.note.trim() || undefined,
    organizationName: roleGrantForm.organizationName.trim() || undefined,
  });

  const confirmRoleGrantAction = (verb) => {
    const roleLabel = ROLE_GRANT_LABELS[roleGrantForm.role] || roleGrantForm.role;
    const playerId = roleGrantLookup?.playerId || roleGrantForm.playerId.trim();
    const regionLabel =
      String(roleGrantForm.regionSlug || '').trim().toLowerCase() === 'camden'
        ? 'Camden'
        : roleGrantForm.regionSlug;
    return window.confirm(
      `${verb} ${roleLabel} ${
        verb === 'Grant' ? 'to' : 'from'
      } player ${playerId} for ${regionLabel}?`,
    );
  };

  const handleGrantRole = async () => {
    if (!allowed) return;
    const playerId = roleGrantForm.playerId.trim();
    if (!playerId) {
      setRoleGrantError('playerId is required.');
      return;
    }
    if (!confirmRoleGrantAction('Grant')) return;

    setRoleGrantSaving(true);
    setRoleGrantError('');
    setRoleGrantNotice('');
    try {
      const res = await axiosConn.post(
        ROLE_GRANTS_ENDPOINT,
        buildRoleGrantPayload(),
        authConfig,
      );
      const payload = res?.data?.data || res?.data || null;
      setRoleGrantLookup(payload);
      setRoleGrantNotice('Role grant updated.');
    } catch (err) {
      setRoleGrantError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to grant this LocalOps role.',
      );
    } finally {
      setRoleGrantSaving(false);
    }
  };

  const handleRevokeRole = async () => {
    if (!allowed) return;
    const playerId = roleGrantForm.playerId.trim();
    if (!playerId) {
      setRoleGrantError('playerId is required.');
      return;
    }
    if (!confirmRoleGrantAction('Revoke')) return;

    setRoleGrantSaving(true);
    setRoleGrantError('');
    setRoleGrantNotice('');
    try {
      const res = await axiosConn.post(
        ROLE_GRANTS_REVOKE_ENDPOINT,
        buildRoleGrantPayload(),
        authConfig,
      );
      const payload = res?.data?.data || res?.data || null;
      setRoleGrantLookup(payload);
      setRoleGrantNotice('Role grant revoked.');
    } catch (err) {
      setRoleGrantError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to revoke this LocalOps role.',
      );
    } finally {
      setRoleGrantSaving(false);
    }
  };

  const anomalies = useMemo(
    () => collectLocalOpsOpsAnomalies({ readiness, rollup, taskReport }),
    [readiness, rollup, taskReport],
  );
  const opsStatus = deriveLocalOpsOpsStatus({
    readiness,
    rollup,
    anomalies,
    error,
  });
  const launchTotalEvents = getTotalLaunchEvents(launchAttribution);
  const launchVisitTotalEvents = getTotalLaunchVisitEvents(launchAttribution);
  const launchAuthTotalEvents = getTotalLaunchAuthEvents(launchAttribution);
  const launchMagicLinkPlayerActionTotalEvents =
    getTotalMagicLinkPlayerActionEvents(launchAttribution);
  const launchShareFunnelTotalEvents =
    getTotalShareFunnelEvents(launchAttribution);
  const launchSourceRows = useMemo(
    () => buildLaunchSourceRows(launchAttribution),
    [launchAttribution],
  );
  const launchVisitSourceRows = useMemo(
    () => buildVisitSourceRows(launchAttribution),
    [launchAttribution],
  );
  const landingChoiceRows = useMemo(
    () => buildLandingChoiceFunnelRows(launchAttribution),
    [launchAttribution],
  );
  const createSheetFunnelRows = useMemo(
    () => buildCreateSheetFunnelRows(launchAttribution),
    [launchAttribution],
  );
  const createSheetJourneyRows = useMemo(
    () => buildCreateSheetJourneyRows(launchAttribution),
    [launchAttribution],
  );
  const createSheetJourneyDiagnostics = useMemo(
    () => getCreateSheetJourneyDiagnostics(launchAttribution),
    [launchAttribution],
  );
  const locationFunnelRows = useMemo(
    () => buildLocationFunnelRows(launchAttribution),
    [launchAttribution],
  );
  const hasLocationFunnelEvents = useMemo(
    () => locationFunnelRows.some((row) => row.hasLocationEvents),
    [locationFunnelRows],
  );
  const locationFunnelMetricByKey = useMemo(
    () =>
      locationFunnelRows.reduce((acc, row) => {
        acc[row.key] = row.metric;
        return acc;
      }, {}),
    [locationFunnelRows],
  );
  const locationGateOpenedMetric = useMemo(() => {
    if (!hasLocationFunnelEvents) {
      return notTrackedDisplayMetric('No location gate events recorded yet.');
    }
    const total = [
      'gate_create_task',
      'gate_accept_task',
      'gate_start_task',
      'gate_create_offering',
      'gate_reserve_offering',
    ].reduce(
      (sum, key) => sum + getMetricNumber(locationFunnelMetricByKey[key]),
      0,
    );
    return trackedDisplayMetric(total);
  }, [hasLocationFunnelEvents, locationFunnelMetricByKey]);
  const demandSupplySignal = useMemo(
    () => buildDemandSupplySignal(launchAttribution),
    [launchAttribution],
  );
  const launchAuthSourceRows = useMemo(
    () => buildAuthSourceRows(launchAttribution),
    [launchAttribution],
  );
  const magicLinkPlayerActionRows = useMemo(
    () => buildMagicLinkPlayerActionRows(launchAttribution),
    [launchAttribution],
  );
  const operatorSummary = operatorVisibility?.systemSummary || {};
  const operatorActivationFunnel = operatorVisibility?.activationFunnel || {};
  const operatorMagicLinkHandoff = operatorVisibility?.magicLinkHandoff || {};
  const operatorMagicLinkHandoffScope = operatorMagicLinkHandoff.scope || {};
  const operatorMagicLinkHandoffCohort =
    operatorMagicLinkHandoff.newPlayerCohort || {};
  const operatorMagicLinkHandoffClassification =
    operatorMagicLinkHandoff.classification || {};
  const operatorMagicLinkHandoffLifetime =
    operatorMagicLinkHandoff.lifetime || {};
  const operatorMagicLinkExistingActivity =
    operatorMagicLinkHandoff.existingPlayerActivity || {};
  const operatorMagicLinkRawActivity =
    operatorMagicLinkHandoff.rawActivity || {};
  const operatorMagicLinkIncompleteRows = Array.isArray(
    operatorMagicLinkHandoff.recentIncompleteRows,
  )
    ? operatorMagicLinkHandoff.recentIncompleteRows
    : [];
  const operatorPlayerRows = operatorVisibility?.activePlayerRows || [];
  const operatorTaskRows = operatorVisibility?.taskRows || [];
  const operatorOfferingRows = operatorVisibility?.offeringRows || [];
  const operatorSourceRows = operatorVisibility?.sourceAttributionRows || [];
  const operatorEmailRows =
    operatorVisibility?.emailNotificationProofRows || [];
  const operatorLocationReadiness =
    operatorVisibility?.locationMapReadiness || {};
  const operatorActionCreditRows = operatorVisibility?.actionCreditRows || [];
  const operatorLocPlaceholder =
    operatorVisibility?.locReadinessPlaceholder || {};
  const operatorEntityTitleLookup = useMemo(() => {
    const lookup = new Map();
    operatorTaskRows.forEach((row) => {
      if (row?.taskId && row?.title) lookup.set(`task:${row.taskId}`, row.title);
    });
    operatorOfferingRows.forEach((row) => {
      if (row?.offeringId && row?.title) {
        lookup.set(`offering:${row.offeringId}`, row.title);
      }
    });
    return lookup;
  }, [operatorOfferingRows, operatorTaskRows]);
  const getOperatorEmailEntityTitle = useCallback(
    (row) => {
      const entityType = row?.entityType || (row?.taskId ? 'task' : '');
      const entityId = row?.entityId || row?.taskId || row?.offeringId || '';
      const lookupTitle =
        entityType && entityId
          ? operatorEntityTitleLookup.get(`${entityType}:${entityId}`)
          : '';
      return (
        row?.entityTitle ||
        row?.taskTitle ||
        row?.offeringTitle ||
        lookupTitle ||
        ''
      );
    },
    [operatorEntityTitleLookup],
  );
  const shareFunnelTaskTitleLookup = useMemo(
    () =>
      buildTaskTitleLookup({
        launchAttribution,
        operatorOfferingRows,
        operatorTaskRows,
        taskResults,
        taskReport,
      }),
    [
      launchAttribution,
      operatorOfferingRows,
      operatorTaskRows,
      taskResults,
      taskReport,
    ],
  );
  const shareFunnelRows = useMemo(
    () => buildShareFunnelRows(launchAttribution, shareFunnelTaskTitleLookup),
    [launchAttribution, shareFunnelTaskTitleLookup],
  );
  const sourceShareSourceOptions = useMemo(
    () =>
      Array.from(
        new Set(shareFunnelRows.map((row) => row.sourceKey).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [shareFunnelRows],
  );
  const managedShareFunnelRows = useMemo(
    () => buildManagedShareFunnelRows(shareFunnelRows, sourceShareControls),
    [shareFunnelRows, sourceShareControls],
  );
  const operatorActionability = useMemo(
    () =>
      buildOperatorActionability({
        operatorVisibility,
        shareFunnelRows,
        now: operatorVisibility?.generatedAt
          ? new Date(operatorVisibility.generatedAt)
          : new Date(),
      }),
    [operatorVisibility, shareFunnelRows],
  );
  const proofAttributionItems = useMemo(
    () =>
      Array.isArray(proofAttributionReport?.items)
        ? proofAttributionReport.items
        : [],
    [proofAttributionReport],
  );
  const proofAttributionStateCounts = useMemo(
    () =>
      proofAttributionItems.reduce((acc, item) => {
        const state = cleanDisplayText(
          item?.previewEvaluation?.previewState || 'unknown',
          80,
        );
        acc[state] = (acc[state] || 0) + 1;
        return acc;
      }, {}),
    [proofAttributionItems],
  );
  const proofAttributionPolicyStateCounts = useMemo(
    () =>
      proofAttributionItems.reduce((acc, item) => {
        const state = cleanDisplayText(
          item?.policyEvaluation?.state || 'unknown',
          80,
        );
        acc[state] = (acc[state] || 0) + 1;
        return acc;
      }, {}),
    [proofAttributionItems],
  );
  const proofAttributionIssuanceOutcomeCounts = useMemo(
    () =>
      proofAttributionItems.reduce((acc, item) => {
        const outcome = cleanDisplayText(
          item?.issuanceDecision?.outcome || 'none',
          80,
        );
        acc[outcome] = (acc[outcome] || 0) + 1;
        return acc;
      }, {}),
    [proofAttributionItems],
  );
  const selectedRoleGrantOption =
    ROLE_GRANT_OPTIONS.find((option) => option.value === roleGrantForm.role) ||
    ROLE_GRANT_OPTIONS[0];
  const roleGrantCurrentRoles = Array.isArray(roleGrantLookup?.localOpsRoles)
    ? roleGrantLookup.localOpsRoles
    : [];
  const launchDeltaLookup = useMemo(
    () => buildDeltaLookup([...launchDeltaItems, ...launchLiveDeltaItems]),
    [launchDeltaItems, launchLiveDeltaItems],
  );
  const operatorDeltaLookup = useMemo(
    () => buildDeltaLookup([...operatorDeltaItems, ...operatorLiveDeltaItems]),
    [operatorDeltaItems, operatorLiveDeltaItems],
  );
  const commandCenterRefreshing =
    loading ||
    taskSearchLoading ||
    launchAttributionLoading ||
    operatorVisibilityLoading ||
    proofAttributionLoading;
  const commandCenterUpdatedAt =
    operatorVisibilityRefreshedAt || launchAttributionRefreshedAt || lastCheckedAt;
  const commandCenterRefreshReason =
    launchRefreshTrigger || operatorRefreshTrigger || 'initial load';
  const aiReportMarkdown = useMemo(
    () =>
      buildLocalOpsAiReport({
        launchAttribution,
        operatorVisibility,
        proofAttributionReport,
        operatorActionability,
        landingChoiceRows,
        createSheetFunnelRows,
        createSheetJourneyRows,
        locationFunnelRows,
        demandSupplySignal,
        shareFunnelRows,
        generatedAt: commandCenterUpdatedAt || new Date(),
        snapshotComparisons: {
          launchAttribution: {
            ...launchComparisonState,
            trigger: launchRefreshTrigger,
          },
          operatorVisibility: {
            ...operatorComparisonState,
            trigger: operatorRefreshTrigger,
          },
        },
      }),
    [
      commandCenterUpdatedAt,
      createSheetFunnelRows,
      createSheetJourneyRows,
      demandSupplySignal,
      landingChoiceRows,
      launchAttribution,
      locationFunnelRows,
      operatorActionability,
      launchComparisonState,
      launchRefreshTrigger,
      operatorComparisonState,
      operatorRefreshTrigger,
      operatorVisibility,
      proofAttributionReport,
      shareFunnelRows,
    ],
  );

  const handleCopyAiReport = async () => {
    setAiReportNotice('');
    setAiReportFallback('');
    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(aiReportMarkdown);
      setAiReportNotice('Copied AI report');
    } catch (err) {
      setAiReportFallback(aiReportMarkdown);
      setAiReportNotice('Copy manually from the selected report text below.');
    }
  };

  const handleDownloadAiReport = () => {
    setAiReportNotice('');
    try {
      if (
        typeof Blob === 'undefined' ||
        !window?.URL?.createObjectURL ||
        !document?.createElement
      ) {
        throw new Error('Download unavailable');
      }
      const blob = new Blob([aiReportMarkdown], {
        type: 'text/markdown;charset=utf-8',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildAiReportFilename({
        launchAttribution,
        operatorVisibility,
        now: commandCenterUpdatedAt || new Date(),
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setAiReportNotice('Downloaded AI report');
    } catch (err) {
      setAiReportFallback(aiReportMarkdown);
      setAiReportNotice('Download unavailable. Copy the report text below.');
    }
  };

  if (!allowed) {
    return (
      <main className="ops-health-page ops-health-page--locked">
        <section className="ops-health-locked" data-testid="ops-health-locked">
          <h1>Ops Health unavailable</h1>
          <p>This internal LocalOps report is not available for this account.</p>
        </section>
      </main>
    );
  }

  const readinessSummary = readiness?.summary || {};
  const staticConfig = readiness?.staticConfig || {};
  const database = readiness?.database || {};
  const stripe = staticConfig?.stripe || {};
  const ignitionFlags = staticConfig?.ignition?.flags || {};
  const systemPlayers = database?.systemPlayers || {};

  return (
    <main className="ops-health-page" data-testid="ops-health-page">
      <header className="ops-health-header">
        <div>
          <p className="ops-health-kicker">LocalOps Internal</p>
          <h1>Ops Health</h1>
        </div>
        <button
          type="button"
          className="ops-health-refresh"
          onClick={refreshAll}
          disabled={
            loading ||
            taskSearchLoading ||
            launchAttributionLoading ||
            operatorVisibilityLoading
          }
        >
          {loading ||
          taskSearchLoading ||
          launchAttributionLoading ||
          operatorVisibilityLoading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </header>

      <section
        className="ops-health-command-status"
        data-testid="ops-health-command-status"
      >
        <div>
          <span
            className={`ops-health-live-pill ops-health-live-pill--${launchRealtimeStatus}`}
          >
            {launchRealtimeStatus === 'live'
              ? 'Live invalidation connected'
              : 'Live invalidation unavailable'}
          </span>
          <strong>Camden operator command center</strong>
          <small>
            {commandCenterRefreshing
              ? 'Refreshing...'
              : `Last updated ${formatDate(commandCenterUpdatedAt)}`}
          </small>
        </div>
        <div>
          <small>Last refresh reason</small>
          <strong>{commandCenterRefreshReason}</strong>
        </div>
        <div className="ops-health-command-actions">
          <button
            type="button"
            className="ops-health-refresh"
            onClick={handleCopyAiReport}
          >
            Copy AI Report
          </button>
          <button
            type="button"
            className="ops-health-refresh"
            onClick={handleDownloadAiReport}
          >
            Download AI Report
          </button>
          <button
            type="button"
            className="ops-health-refresh"
            onClick={refreshAll}
            disabled={commandCenterRefreshing}
          >
            {commandCenterRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </section>
      {aiReportNotice ? (
        <div
          className="ops-health-alert ops-health-alert--success ops-health-alert--inline"
          data-testid="ops-health-ai-report-notice"
        >
          {aiReportNotice}
        </div>
      ) : null}
      {aiReportFallback ? (
        <textarea
          className="ops-health-ai-report-fallback"
          data-testid="ops-health-ai-report-fallback"
          readOnly
          value={aiReportFallback}
          onFocus={(event) => event.target.select()}
        />
      ) : null}

      <details
        className="ops-health-panel ops-health-concierge ops-health-admin-utility"
        data-testid="ops-health-concierge-intake"
      >
        <summary>Admin utility: LocalOps Concierge Intake</summary>
        <div className="ops-health-section-heading">
          <h2>LocalOps Concierge Intake</h2>
          <span>collision-specialist-camden</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Create a customer-owned ride draft and send the existing magic-link
          handoff. The customer reviews, funds, and publishes the task.
        </p>
        <form
          className="ops-health-intake-form"
          onSubmit={handleConciergeSubmit}
        >
          <label htmlFor="ops-health-intake-partner">Partner source</label>
          <input
            id="ops-health-intake-partner"
            value="Collision Specialist"
            readOnly
          />

          <label htmlFor="ops-health-intake-pickup">Pickup</label>
          <input
            id="ops-health-intake-pickup"
            value={COLLISION_SPECIALIST_PICKUP_ADDRESS}
            readOnly
          />

          <label htmlFor="ops-health-intake-destination">Destination</label>
          <input
            id="ops-health-intake-destination"
            value={conciergeForm.deliveryAddress}
            onChange={(event) =>
              updateConciergeForm('deliveryAddress', event.target.value)
            }
            placeholder="Customer destination address"
            required
          />

          <label htmlFor="ops-health-intake-email">Customer email</label>
          <input
            id="ops-health-intake-email"
            type="email"
            value={conciergeForm.customerEmail}
            onChange={(event) =>
              updateConciergeForm('customerEmail', event.target.value)
            }
            placeholder="customer@example.com"
            required
          />

          <label htmlFor="ops-health-intake-phone">Customer phone</label>
          <input
            id="ops-health-intake-phone"
            value={conciergeForm.customerPhone}
            onChange={(event) =>
              updateConciergeForm('customerPhone', event.target.value)
            }
            placeholder="Optional manual reference"
          />

          <label htmlFor="ops-health-intake-notes">Notes</label>
          <textarea
            id="ops-health-intake-notes"
            value={conciergeForm.notes}
            onChange={(event) =>
              updateConciergeForm('notes', event.target.value)
            }
            placeholder="Optional internal note"
          />

          <div className="ops-health-intake-price">
            <span>Suggested price</span>
            <strong>
              {Number.isFinite(Number(conciergeResult?.suggestedPriceUsd))
                ? formatUsd(conciergeResult.suggestedPriceUsd)
                : 'Auto after destination is resolved'}
            </strong>
          </div>

          <div className="ops-health-intake-actions">
            <button type="submit" disabled={conciergeSending}>
              {conciergeSending ? 'Sending...' : 'Send magic link'}
            </button>
          </div>
        </form>
        {conciergeError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {conciergeError}
          </div>
        ) : null}
        {conciergeResult ? (
          <div
            className="ops-health-alert ops-health-alert--success ops-health-alert--inline"
            data-testid="ops-health-concierge-success"
          >
            <strong>Magic link sent.</strong>
            <p>
              Suggested amount{' '}
              {formatUsd(conciergeResult.suggestedPriceUsd)}. Draft token
              created: {conciergeResult.draftTokenCreated ? 'yes' : 'unknown'}.
            </p>
            {conciergeResult.fallbackReviewLink ? (
              <p>
                Review link:{' '}
                <code data-testid="ops-health-concierge-review-link">
                  {conciergeResult.fallbackReviewLink}
                </code>
              </p>
            ) : null}
          </div>
        ) : null}
      </details>

      <details
        className="ops-health-panel ops-health-role-grants ops-health-admin-utility"
        data-testid="ops-health-role-grants"
      >
        <summary>Admin utility: LocalOps Role Grants</summary>
        <div className="ops-health-section-heading">
          <h2>LocalOps Role Grants</h2>
          <span>true-admin only</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Grant narrow LocalOps operator access by exact playerId. Roles are
          region-scoped and do not create global admin access.
        </p>
        <form
          className="ops-health-role-grant-form"
          onSubmit={handleRoleGrantLookup}
        >
          <label htmlFor="ops-health-role-player">playerId</label>
          <input
            id="ops-health-role-player"
            value={roleGrantForm.playerId}
            onChange={(event) =>
              updateRoleGrantForm('playerId', event.target.value)
            }
            placeholder="camdenhelper"
            autoComplete="off"
          />

          <label htmlFor="ops-health-role-type">Role</label>
          <select
            id="ops-health-role-type"
            value={roleGrantForm.role}
            onChange={(event) => updateRoleGrantForm('role', event.target.value)}
          >
            {ROLE_GRANT_OPTIONS.map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label htmlFor="ops-health-role-region">Region</label>
          <select
            id="ops-health-role-region"
            value={roleGrantForm.regionSlug}
            onChange={(event) =>
              updateRoleGrantForm('regionSlug', event.target.value)
            }
          >
            <option value="camden">Camden</option>
          </select>

          <label htmlFor="ops-health-role-organization">
            Organization / context
          </label>
          <input
            id="ops-health-role-organization"
            value={roleGrantForm.organizationName}
            onChange={(event) =>
              updateRoleGrantForm('organizationName', event.target.value)
            }
            placeholder="Optional, e.g. Camden Shelter"
          />

          <label htmlFor="ops-health-role-note">Internal note</label>
          <textarea
            id="ops-health-role-note"
            value={roleGrantForm.note}
            onChange={(event) => updateRoleGrantForm('note', event.target.value)}
            placeholder="Optional reason for this role grant"
          />

          <div className="ops-health-role-grant-actions">
            <button type="submit" disabled={roleGrantLoading || roleGrantSaving}>
              {roleGrantLoading ? 'Looking up...' : 'Lookup player'}
            </button>
            <button
              type="button"
              disabled={roleGrantLoading || roleGrantSaving}
              onClick={handleGrantRole}
            >
              {roleGrantSaving ? 'Saving...' : 'Grant role'}
            </button>
            <button
              type="button"
              className="ops-health-secondary-button"
              disabled={roleGrantLoading || roleGrantSaving}
              onClick={handleRevokeRole}
            >
              Revoke role
            </button>
          </div>
        </form>

        <div
          className="ops-health-role-explainer"
          data-testid="ops-health-role-explainer"
        >
          <strong>{selectedRoleGrantOption.label}</strong>
          <p>{selectedRoleGrantOption.description}</p>
          <p>
            Community Tasks are for unpaid volunteer-style help. Paid errands,
            deliveries, shopping, driving, or supply runs should be posted as
            paid LocalOps tasks.
          </p>
          <ul>
            <li>Walk shelter dogs = Community Task</li>
            <li>Help organize donated supplies = Community Task</li>
            <li>Pick up cat food from Walmart = Paid Task</li>
            <li>Drive supplies across town = Paid Task</li>
          </ul>
        </div>

        {roleGrantError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {roleGrantError}
          </div>
        ) : null}
        {roleGrantNotice ? (
          <div
            className="ops-health-alert ops-health-alert--success ops-health-alert--inline"
            data-testid="ops-health-role-grant-notice"
          >
            {roleGrantNotice}
          </div>
        ) : null}
        {roleGrantLookup ? (
          <div
            className="ops-health-role-player-summary"
            data-testid="ops-health-role-player-summary"
          >
            <div className="ops-health-role-player-identity">
              {roleGrantLookup.profilePhoto ? (
                <img
                  src={roleGrantLookup.profilePhoto}
                  alt={`${roleGrantLookup.playerId} profile`}
                />
              ) : null}
              <div>
                <strong>{roleGrantLookup.playerId}</strong>
                <span>Global role: {roleGrantLookup.role || 'player'}</span>
              </div>
            </div>
            <div className="ops-health-role-current-grants">
              <span>Current LocalOps roles</span>
              {roleGrantCurrentRoles.length ? (
                <ul>
                  {roleGrantCurrentRoles.map((grant) => (
                    <li
                      key={`${grant.role}-${grant.regionSlug}`}
                      className={
                        grant.status === 'active'
                          ? 'ops-health-role-grant-active'
                          : 'ops-health-role-grant-disabled'
                      }
                    >
                      <strong>{grant.roleLabel || ROLE_GRANT_LABELS[grant.role] || grant.role}</strong>
                      <span>
                        {grant.regionSlug} / {grant.status || 'active'}
                      </span>
                      {grant.organizationName ? (
                        <small>{grant.organizationName}</small>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="ops-health-muted">No LocalOps role grants yet.</p>
              )}
            </div>
          </div>
        ) : null}
      </details>

      <section
        className={`ops-health-status ops-health-status--${opsStatus}`}
        data-testid="ops-health-status"
      >
        <div>
          <span className="ops-health-status-label">Overall status</span>
          <strong>{STATUS_LABELS[opsStatus]}</strong>
        </div>
        <Metric label="Last checked" value={formatDate(lastCheckedAt)} />
        <Metric label="Readiness" value={truthyLabel(readinessSummary.pass)} />
        <Metric label="Accounting" value={truthyLabel(rollup?.pass)} />
        <Metric label="Anomalies" value={anomalies.length} />
        <Metric label="Journal entries" value={rollup?.entryCount ?? '--'} />
        <Metric
          label="Rollup period"
          value={
            rollup?.from || rollup?.to
              ? `${rollup?.from || 'start'} to ${rollup?.to || 'now'}`
              : 'All time'
          }
        />
      </section>

      {error ? (
        <section className="ops-health-alert ops-health-alert--error">
          {error}
        </section>
      ) : null}

      <section className="ops-health-panel ops-health-anomalies">
        <div className="ops-health-section-heading">
          <h2>Anomalies</h2>
          <span>{anomalies.length}</span>
        </div>
        {anomalies.length > 0 ? (
          <ul>
            {anomalies.map((item, index) => (
              <li
                key={`${item.source}-${item.code}-${index}`}
                className={`ops-health-anomaly ops-health-anomaly--${item.severity}`}
              >
                <span>{item.severity}</span>
                <strong>{item.code}</strong>
                <p>{item.message}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ops-health-muted">No anomalies reported by the current checks.</p>
        )}
      </section>

      <section
        className="ops-health-panel ops-health-launch-attribution"
        data-testid="ops-health-launch-attribution"
      >
        <div className="ops-health-section-heading">
          <h2>Camden Launch Attribution</h2>
          <span>{launchAttribution?.proofType || 'durable events'}</span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Durable attribution from LocalOps events. Canonical task-source
          attribution shows which source created the task. Magic-link player
          actions show which source acquired the player who acted.
        </p>
        <div className="ops-health-launch-pulse" data-testid="ops-health-launch-pulse">
          <span
            className={`ops-health-live-pill ops-health-live-pill--${launchRealtimeStatus}`}
          >
            {launchRealtimeStatus === 'live'
              ? 'Live invalidation connected'
              : 'Live invalidation unavailable'}
          </span>
          <span>Refresh trigger: {launchRefreshTrigger || 'Not checked'}</span>
          {launchDeltaItems.length > 0 ? (
            <div
              className="ops-health-launch-delta-board"
              data-testid="ops-health-launch-delta-board"
            >
              <small>
                Changes in rolling-window report totals between the comparison
                baseline and current report
              </small>
              <ul className="ops-health-delta-list">
                {launchDeltaItems.map((item) => (
                  <li key={item.key}>
                    <strong>{formatDeltaValue(item)}</strong>
                    <span>{item.label}</span>
                    <small>{item.group}</small>
                  </li>
                ))}
              </ul>
              <small>{OPS_HEALTH_ROLLING_WINDOW_CAVEAT}</small>
            </div>
          ) : (
            <small>
              {getOpsHealthComparisonStatusMessage(launchComparisonState)}
            </small>
          )}
        </div>
        <OpsHealthComparisonMeta
          comparison={launchComparisonState}
          refreshTrigger={launchRefreshTrigger}
          testId="ops-health-launch-comparison-meta"
        />
        {launchAttributionLoading ? (
          <p className="ops-health-muted">
            Loading Camden launch attribution...
          </p>
        ) : null}
        {launchAttributionError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            <strong>Unable to load launch attribution.</strong>
            <p>Refresh this report or check admin access if the issue persists.</p>
          </div>
        ) : null}
        {!launchAttributionLoading &&
        !launchAttributionError &&
        launchAttribution &&
        launchTotalEvents === 0 &&
        launchVisitTotalEvents === 0 &&
        launchAuthTotalEvents === 0 &&
        launchMagicLinkPlayerActionTotalEvents === 0 &&
        launchShareFunnelTotalEvents === 0 ? (
          <div className="ops-health-empty">
            <p>No durable launch attribution events yet.</p>
            <p>
              New visits, CTA clicks, posts, accepts, reservations,
              magic-link auth, share funnel, settlements, and Action Credit
              events will appear here after they are recorded.
            </p>
          </div>
        ) : null}
        {!launchAttributionError && launchAttribution ? (
          <div
            className="ops-health-landing-choice"
            data-testid="ops-health-landing-choice-funnel"
          >
            <div className="ops-health-operator-list-heading">
              <h3>Landing Choice Funnel</h3>
              <span>zero and not tracked are separate</span>
            </div>
            <p className="ops-health-muted">
              Durable counts show as numbers, including 0. Missing durable
              PlayerMap requester telemetry shows as not tracked yet.
            </p>
            <article
              className={`ops-health-demand-supply ops-health-demand-supply--${demandSupplySignal.tone}`}
              data-testid="ops-health-demand-supply-signal"
            >
              <div>
                <span>Demand / Supply Signal</span>
                <strong>Signal: {demandSupplySignal.signal}</strong>
              </div>
              <div className="ops-health-demand-supply-counts">
                <span>{demandSupplySignal.earnLabel}</span>
                <span>{demandSupplySignal.helpLabel}</span>
              </div>
              <p>
                <strong>Insight:</strong> {demandSupplySignal.insight}
              </p>
              <p>
                <strong>Recommended action:</strong>{' '}
                {demandSupplySignal.recommendation}
              </p>
            </article>
            <div className="ops-health-landing-choice-grid">
              {landingChoiceRows.map((row) => {
                const rowDelta = getSummaryDelta(
                  launchDeltaLookup,
                  `landingChoiceFunnel:${row.key}`,
                );
                return (
                <div
                  className={`ops-health-landing-choice-row${
                    shouldHighlightDelta(rowDelta)
                      ? ' ops-health-landing-choice-row--changed'
                      : ''
                  }`}
                  key={row.key}
                >
                  <Metric label={row.label} value={row.metric} delta={rowDelta} />
                  <small>{row.detail}</small>
                </div>
                );
              })}
            </div>
          </div>
        ) : null}
        {!launchAttributionError && launchAttribution ? (
          <div
            className="ops-health-landing-choice"
            data-testid="ops-health-create-sheet-funnel"
          >
            <div className="ops-health-operator-list-heading">
              <h3>Create Sheet Funnel</h3>
              <span>durable CTA-backed steps</span>
            </div>
            <p className="ops-health-muted">
              These rows count Create Sheet funnel events persisted as durable
              launch CTA IDs. Missing rows mean not tracked yet; 0 means tracked
              and no event recorded.
            </p>
            <div className="ops-health-landing-choice-grid">
              {createSheetFunnelRows.map((row) => {
                const rowDelta = getSummaryDelta(
                  launchDeltaLookup,
                  `createSheetFunnel:${row.key}`,
                );
                return (
                  <div
                    className={`ops-health-landing-choice-row${
                      shouldHighlightDelta(rowDelta)
                        ? ' ops-health-landing-choice-row--changed'
                        : ''
                    }`}
                    key={row.key}
                  >
                    <Metric label={row.label} value={row.metric} delta={rowDelta} />
                    <small>{row.detail}</small>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        {!launchAttributionError && launchAttribution ? (
          <div
            className="ops-health-landing-choice"
            data-testid="ops-health-create-sheet-journey"
          >
            <div className="ops-health-operator-list-heading">
              <h3>Create Sheet Journey Diagnostics</h3>
              <span>client-reported tracking</span>
            </div>
            <p className="ops-health-muted">
              These rows show client-reported Create Sheet journeys. They
              explain interface progression and blocker states; canonical
              backend task events remain authoritative for task creation.
            </p>
            <div className="ops-health-landing-choice-grid">
              {createSheetJourneyRows.map((row) => {
                const rowDelta = getSummaryDelta(
                  launchDeltaLookup,
                  `createSheetJourney:${row.key}`,
                );
                return (
                  <div
                    className={`ops-health-landing-choice-row${
                      shouldHighlightDelta(rowDelta)
                        ? ' ops-health-landing-choice-row--changed'
                        : ''
                    }`}
                    key={row.key}
                  >
                    <Metric label={row.label} value={row.metric} delta={rowDelta} />
                    <small>{row.detail}</small>
                  </div>
                );
              })}
            </div>
            {createSheetJourneyDiagnostics?.blockerBreakdown?.length ? (
              <div className="ops-health-debug-list">
                <strong>Top reported blockers</strong>
                {createSheetJourneyDiagnostics.blockerBreakdown
                  .slice(0, 5)
                  .map((row) => (
                    <small key={row.signature || row.firstBlockingField}>
                      {cleanDisplayText(row.firstBlockingField || 'unknown', 80)}:{' '}
                      {(row.reasonCodes || []).join(', ') || 'unknown'} / flows:{' '}
                      {formatCount(row.flowCount)} / events:{' '}
                      {formatCount(row.count)}
                    </small>
                  ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {!launchAttributionError && launchAttribution ? (
          <div
            className="ops-health-landing-choice"
            data-testid="ops-health-location-funnel"
          >
            <div className="ops-health-operator-list-heading">
              <h3>Location Permission / Fresh Saved Location Funnel</h3>
              <span>durable CTA-backed steps</span>
            </div>
            <p className="ops-health-muted">
              Fresh Saved Location means a recently saved usable location.
              Browse-first can be healthy behavior; missing telemetry is not
              user failure.
            </p>
            {!hasLocationFunnelEvents ? (
              <div className="ops-health-empty ops-health-empty--compact">
                No location gate events recorded yet.
              </div>
            ) : (
              <div className="ops-health-landing-choice-grid">
                {locationFunnelRows.map((row) => {
                  const rowDelta = getSummaryDelta(
                    launchDeltaLookup,
                    `locationFunnel:${row.key}`,
                  );
                  return (
                    <div
                      className={`ops-health-landing-choice-row${
                        shouldHighlightDelta(rowDelta)
                          ? ' ops-health-landing-choice-row--changed'
                          : ''
                      }`}
                      key={row.key}
                    >
                      <Metric label={row.label} value={row.metric} delta={rowDelta} />
                      <small>{row.detail}</small>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
        {proofAttributionAdminEnabled ? (
          <div
            className="ops-health-landing-choice ops-health-proof-attribution-preview"
            data-testid="ops-health-proof-attribution-admin"
          >
            <div className="ops-health-operator-list-heading">
              <h3>Proof Attribution Preview</h3>
              <span>read-only policy and issuance truth</span>
            </div>
            <p className="ops-health-muted">
              Shows persisted proof-origin task sidecars and evaluates whether a
              completed attributed task appears ready, waiting, or blocked for
              policy review, then shows any persisted automatic issuance
              outcome. This panel has no approval, retry, repair, or issue
              controls.
            </p>
            {proofAttributionLoading ? (
              <div className="ops-health-empty ops-health-empty--compact">
                Loading proof attribution preview...
              </div>
            ) : null}
            {proofAttributionError ? (
              <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
                <strong>Unable to load proof attribution preview.</strong>
                <p>{proofAttributionError}</p>
              </div>
            ) : null}
            {!proofAttributionLoading && !proofAttributionError ? (
              <>
                <div className="ops-health-landing-choice-grid">
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Sidecars loaded"
                      value={formatCount(proofAttributionItems.length)}
                    />
                    <small>exact region/task/proof filters only</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Ready for evaluation"
                      value={formatCount(
                        (proofAttributionStateCounts.new_requester_candidate || 0) +
                          (proofAttributionStateCounts.ready || 0),
                      )}
                    />
                    <small>ready for policy evaluation, not payout</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Waiting"
                      value={formatCount(proofAttributionStateCounts.waiting)}
                    />
                    <small>funding, accept, or completion state not ready</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Blocked"
                      value={formatCount(proofAttributionStateCounts.blocked)}
                    />
                    <small>terminal state or integrity flag</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Meets v1 policy criteria"
                      value={formatCount(proofAttributionPolicyStateCounts.pass)}
                    />
                    <small>read-only policy result, not payout</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="No credit under v1"
                      value={formatCount(
                        proofAttributionPolicyStateCounts.no_credit,
                      )}
                    />
                    <small>trusted attribution with a v1 exclusion</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="JGK issued"
                      value={formatCount(
                        proofAttributionIssuanceOutcomeCounts.issued,
                      )}
                    />
                    <small>persisted issuance outcome, not manual approval</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Not issued - budget"
                      value={formatCount(
                        proofAttributionIssuanceOutcomeCounts.not_issued_budget,
                      )}
                    />
                    <small>daily program budget control</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Not issued - cap"
                      value={formatCount(
                        proofAttributionIssuanceOutcomeCounts.not_issued_cap,
                      )}
                    />
                    <small>proof-player daily cap control</small>
                  </div>
                  <div className="ops-health-landing-choice-row">
                    <Metric
                      label="Configuration state"
                      value={formatProofReferralConfigurationState(
                        proofAttributionReport?.issuanceControlPreview
                          ?.configurationState,
                      )}
                    />
                    <small>
                      {(
                        proofAttributionReport?.issuanceControlPreview
                          ?.reasonCodes || []
                      ).join(', ') || 'configured from backend environment'}
                    </small>
                  </div>
                </div>
                {proofAttributionItems.length ? (
                  <div className="ops-health-proof-attribution-list">
                    {proofAttributionItems.slice(0, 8).map((item) => {
                      const evaluation = item?.previewEvaluation || {};
                      const policy = item?.policyEvaluation || {};
                      const requesterFacts = item?.requesterAcquisitionFacts || {};
                      const persisted = item?.persistedAttribution || {};
                      const reasons = Array.isArray(evaluation.reasons)
                        ? evaluation.reasons.join(', ')
                        : '';
                      const flags = Array.isArray(evaluation.integrityFlags)
                        ? evaluation.integrityFlags.join(', ')
                        : '';
                      const policyReasons = Array.isArray(policy.reasonCodes)
                        ? policy.reasonCodes.join(', ')
                        : '';
                      const policyFlags = Array.isArray(policy.integrityFlags)
                        ? policy.integrityFlags.join(', ')
                        : '';
                      const issuance = item?.issuanceDecision || {};
                      const issuanceReasons = Array.isArray(
                        issuance.issuanceReasonCodes,
                      )
                        ? issuance.issuanceReasonCodes.join(', ')
                        : '';
                      return (
                        <article
                          className="ops-health-proof-attribution-row"
                          key={persisted.id || item.id || persisted.taskId}
                        >
                          <div>
                            <strong>{getProofAttributionTaskTitle(item)}</strong>
                            <small>{getProofAttributionStateLabel(item)}</small>
                            <DebugIdDetails
                              summary="Debug IDs"
                              items={[
                                { label: 'Sidecar ID', value: persisted.id },
                                { label: 'Task ID', value: persisted.taskId },
                                { label: 'Proof ID', value: persisted.proofId },
                                {
                                  label: 'Policy reason codes',
                                  value: policyReasons,
                                },
                                {
                                  label: 'Policy integrity flags',
                                  value: policyFlags,
                                },
                                {
                                  label: 'Ledger reference',
                                  value: issuance.ledgerReference,
                                },
                              ]}
                            />
                          </div>
                          <OperatorField
                            label="Source proof player"
                            value={getProofAttributionRefLabel(item)}
                            className="ops-health-operator-field--proof-identity"
                          />
                          <OperatorField
                            label="Requester"
                            value={
                              requesterFacts.canonicalTaskCreatorPublicId ||
                              'unknown'
                            }
                          />
                          <OperatorField
                            label="Acquisition"
                            value={formatProofAttributionAcquisition(
                              requesterFacts.acquisitionState,
                            )}
                            className="ops-health-operator-field--proof-wide"
                          />
                          <OperatorField
                            label="Prior funded tasks"
                            value={formatProofAttributionCount(
                              requesterFacts.priorFundedTaskCount,
                            )}
                          />
                          <OperatorField
                            label="Funded"
                            value={formatBooleanState(evaluation.funded)}
                          />
                          <OperatorField
                            label="Accepted"
                            value={formatBooleanState(evaluation.accepted)}
                          />
                          <OperatorField
                            label="Verified completed"
                            value={formatBooleanState(evaluation.verifiedCompleted)}
                          />
                          <OperatorField
                            label="Terminal state"
                            value={evaluation.terminalState || 'unknown'}
                          />
                          <OperatorField
                            label="Policy v1 result"
                            value={getProofAttributionPolicyLabel(item)}
                            className="ops-health-operator-field--proof-wide"
                          />
                          <OperatorField
                            label="Policy reason"
                            value={formatProofAttributionPolicyReason(item)}
                            className="ops-health-operator-field--proof-wide"
                          />
                          <OperatorField
                            label="Policy version"
                            value={policy.policyVersion || 'unknown'}
                          />
                          <OperatorField
                            label="Issuance"
                            value={getProofAttributionIssuanceLabel(item)}
                            className="ops-health-operator-field--proof-wide"
                          />
                          {issuance.rewardAmountJGK ? (
                            <OperatorField
                              label="Amount"
                              value={`${formatCount(issuance.rewardAmountJGK)} JGK`}
                            />
                          ) : null}
                          {issuance.fundingSource ? (
                            <OperatorField
                              label="Funding source"
                              value={issuance.fundingSource.replace(/_/g, ' ')}
                            />
                          ) : null}
                          {issuance.issuedAt ? (
                            <OperatorField
                              label="Issued"
                              value={formatDate(issuance.issuedAt)}
                              className="ops-health-operator-field--proof-wide"
                            />
                          ) : null}
                          {reasons ? (
                            <small className="ops-health-proof-attribution-note">
                              Reasons: {reasons}
                            </small>
                          ) : null}
                          {issuanceReasons ? (
                            <small className="ops-health-proof-attribution-note">
                              Issuance reason:{' '}
                              {formatProofAttributionIssuanceReason(item)}
                            </small>
                          ) : null}
                          <small className="ops-health-proof-attribution-note">
                            Read-only. No approval, retry, repair, or issue
                            controls are exposed here.
                          </small>
                          {flags ? (
                            <small className="ops-health-proof-attribution-note">
                              Integrity flags: {flags}
                            </small>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ops-health-empty ops-health-empty--compact">
                    No proof attribution sidecars loaded for this view.
                  </div>
                )}
              </>
            ) : null}
          </div>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchVisitTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-visit-summary"
            >
              {VISIT_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={VISIT_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.visitTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'visitTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            {launchVisitSourceRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for visit funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {VISIT_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {VISIT_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {launchVisitSourceRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'visitSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {VISIT_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'visitSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchAuthTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-auth-summary"
            >
              {AUTH_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={AUTH_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.authTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'authTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            {launchAuthSourceRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for magic-link auth funnel</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {AUTH_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {AUTH_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {launchAuthSourceRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'authSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {AUTH_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'authSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchShareFunnelTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-share-funnel-summary"
            >
              {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={SHARE_FUNNEL_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.shareFunnelTotalsByEvent?.[eventName],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'shareFunnelTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
            </div>
            <p className="ops-health-muted ops-health-proof-copy">
              Share funnel tracks public task-share visitors from share landing
              through backend-confirmed accept, verified completion, and Action
              Credit when the same share-acquired helper closes the loop. It is
              separate from canonical task-source attribution.
            </p>
            {shareFunnelRows.length > 0 ? (
              <div
                className="ops-health-managed-source"
                data-testid="ops-health-managed-share-funnel"
              >
                <div className="ops-health-managed-controls">
                  <label>
                    Search
                    <input
                      type="search"
                      value={sourceShareControls.search}
                      onChange={(event) =>
                        updateSourceShareControl('search', event.target.value)
                      }
                      placeholder="Source, task, offering, id"
                    />
                  </label>
                  <label>
                    Source
                    <select
                      value={sourceShareControls.source}
                      onChange={(event) =>
                        updateSourceShareControl('source', event.target.value)
                      }
                    >
                      <option value="all">All sources</option>
                      {sourceShareSourceOptions.map((sourceKey) => (
                        <option key={sourceKey} value={sourceKey}>
                          {sourceKey}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Type
                    <select
                      value={sourceShareControls.entityType}
                      onChange={(event) =>
                        updateSourceShareControl('entityType', event.target.value)
                      }
                    >
                      <option value="all">All types</option>
                      <option value="task">Tasks</option>
                      <option value="offering">Offerings</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </label>
                  <label>
                    Outcome
                    <select
                      value={sourceShareControls.outcome}
                      onChange={(event) =>
                        updateSourceShareControl('outcome', event.target.value)
                      }
                    >
                      {SOURCE_SHARE_OUTCOME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Sort
                    <select
                      value={sourceShareControls.sort}
                      onChange={(event) =>
                        updateSourceShareControl('sort', event.target.value)
                      }
                    >
                      {SOURCE_SHARE_SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="ops-health-managed-summary">
                  <span>
                    Showing {managedShareFunnelRows.totalRows
                      ? `${managedShareFunnelRows.start + 1}-${managedShareFunnelRows.end}`
                      : '0'}{' '}
                    of {formatCount(managedShareFunnelRows.totalRows)} share rows
                  </span>
                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        updateSourceShareControl(
                          'page',
                          Math.max(1, managedShareFunnelRows.page - 1),
                        )
                      }
                      disabled={managedShareFunnelRows.page <= 1}
                    >
                      Prev
                    </button>
                    <span>
                      Page {managedShareFunnelRows.page} of{' '}
                      {managedShareFunnelRows.totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        updateSourceShareControl(
                          'page',
                          Math.min(
                            managedShareFunnelRows.totalPages,
                            managedShareFunnelRows.page + 1,
                          ),
                        )
                      }
                      disabled={
                        managedShareFunnelRows.page >=
                        managedShareFunnelRows.totalPages
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
                {managedShareFunnelRows.pageRows.length ? (
                  <div className="ops-health-table-wrap">
                    <table className="ops-health-source-table ops-health-source-table--managed-share">
                      <caption>Managed source/share funnel</caption>
                      <thead>
                        <tr>
                          <th scope="col">Source / Share</th>
                          <th scope="col">Outcome</th>
                          {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => (
                            <th key={eventName} scope="col">
                              {SHARE_FUNNEL_EVENT_LABELS[eventName]}
                            </th>
                          ))}
                          <th scope="col">Landing -&gt; Accepted</th>
                          <th scope="col">Accepted -&gt; Completed</th>
                          <th scope="col">Landing -&gt; Completed</th>
                          <th scope="col">Funnel events</th>
                        </tr>
                      </thead>
                      <tbody>
                        {managedShareFunnelRows.pageRows.map((row) => {
                          const rowDelta = getSourceRowDelta(
                            launchDeltaLookup,
                            'shareFunnelSourceBreakdown',
                            row.sourceLabel,
                          );
                          return (
                          <tr
                            key={row.sourceLabel}
                            className={
                              rowDelta ? 'ops-health-source-row--changed' : ''
                            }
                          >
                            <th scope="row">
                              <span>{row.displayLabel || row.sourceLabel}</span>
                              {row.details ? <small>{row.details}</small> : null}
                              <small>
                                {row.entityType} / {row.sourceKey}
                              </small>
                              <DeltaBadge delta={rowDelta} />
                            </th>
                            <td>
                              <strong>{row.outcome?.label}</strong>
                              <small>{row.outcome?.evidence}</small>
                            </td>
                            {SHARE_FUNNEL_EVENT_ORDER.map((eventName) => {
                              const cellDelta = getSourceCellDelta(
                                launchDeltaLookup,
                                'shareFunnelSourceBreakdown',
                                row.sourceLabel,
                                eventName,
                              );
                              return (
                              <td
                                key={eventName}
                                className={
                                  cellDelta
                                    ? 'ops-health-source-cell--changed'
                                    : ''
                                }
                              >
                                {formatCount(row.counts[eventName])}
                                <DeltaBadge delta={cellDelta} />
                              </td>
                              );
                            })}
                            <td>
                              {formatConversionWithFraction(
                                row.conversion?.landingToAcceptSuccessRate,
                                row.counts.localops_share_task_accept_succeeded,
                                row.counts.localops_share_landing_viewed,
                              )}
                            </td>
                            <td>
                              {formatConversionWithFraction(
                                row.conversion
                                  ?.acceptSuccessToCompletionVerifiedRate,
                                row.counts.localops_task_completion_verified,
                                row.counts.localops_share_task_accept_succeeded,
                              )}
                            </td>
                            <td>
                              {formatConversionWithFraction(
                                row.conversion?.landingToCompletionVerifiedRate,
                                row.counts.localops_task_completion_verified,
                                row.counts.localops_share_landing_viewed,
                              )}
                            </td>
                            <td>
                              {formatCount(row.total)}
                              <DeltaBadge delta={rowDelta} />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="ops-health-muted">No share rows match these controls.</p>
                )}
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError &&
        launchAttribution &&
        launchMagicLinkPlayerActionTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-player-action-summary"
            >
              {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={LAUNCH_EVENT_LABELS[eventName]}
                  value={formatCount(
                    launchAttribution.magicLinkPlayerActionTotalsByEvent?.[
                      eventName
                    ],
                  )}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'magicLinkPlayerActionTotalsByEvent',
                    eventName,
                  )}
                />
              ))}
              <Metric
                label="Matched player actions"
                value={formatCount(
                  launchAttribution.magicLinkPlayerActionCoverage?.attributed,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'magicLinkPlayerActionCoverage:attributed',
                )}
              />
              <Metric
                label="Player-action coverage"
                value={formatActionCreditCoverage({
                  total: launchAttribution.magicLinkPlayerActionCoverage?.total,
                  attributed:
                    launchAttribution.magicLinkPlayerActionCoverage?.attributed,
                  coverage:
                    launchAttribution.magicLinkPlayerActionCoverage?.coverage,
                })}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'magicLinkPlayerActionCoverage:total',
                )}
              />
            </div>
            {magicLinkPlayerActionRows.length > 0 ? (
              <div className="ops-health-table-wrap">
                <table className="ops-health-source-table">
                  <caption>Source breakdown for magic-link player actions</caption>
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => (
                        <th key={eventName} scope="col">
                          {LAUNCH_EVENT_LABELS[eventName]}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {magicLinkPlayerActionRows.map((row) => {
                      const rowDelta = getSourceRowDelta(
                        launchDeltaLookup,
                        'magicLinkPlayerActionSourceBreakdown',
                        row.sourceLabel,
                      );
                      return (
                      <tr
                        key={row.sourceLabel}
                        className={
                          rowDelta ? 'ops-health-source-row--changed' : ''
                        }
                      >
                        <th scope="row">
                          <span>{row.sourceLabel}</span>
                          {row.details ? <small>{row.details}</small> : null}
                          <DeltaBadge delta={rowDelta} />
                        </th>
                        {MAGIC_LINK_PLAYER_ACTION_EVENT_ORDER.map((eventName) => {
                          const cellDelta = getSourceCellDelta(
                            launchDeltaLookup,
                            'magicLinkPlayerActionSourceBreakdown',
                            row.sourceLabel,
                            eventName,
                          );
                          return (
                          <td
                            key={eventName}
                            className={
                              cellDelta ? 'ops-health-source-cell--changed' : ''
                            }
                          >
                            {formatCount(row.counts[eventName])}
                            <DeltaBadge delta={cellDelta} />
                          </td>
                          );
                        })}
                        <td>
                          {formatCount(row.total)}
                          <DeltaBadge delta={rowDelta} />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
        {!launchAttributionError && launchAttribution && launchTotalEvents > 0 ? (
          <>
            <div
              className="ops-health-list ops-health-launch-summary"
              data-testid="ops-health-launch-canonical-summary"
            >
              {LAUNCH_EVENT_ORDER.map((eventName) => (
                <Metric
                  key={eventName}
                  label={LAUNCH_EVENT_LABELS[eventName]}
                  value={formatCount(launchAttribution.totalsByEvent?.[eventName])}
                  delta={getMetricDelta(
                    launchDeltaLookup,
                    'totalsByEvent',
                    eventName,
                  )}
                />
              ))}
              <Metric
                label="Unattributed canonical events"
                value={formatCount(
                  launchAttribution.unattributedCanonicalEventCount,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'unattributedCanonicalEventCount',
                )}
              />
              <Metric
                label="Source to Action Credit"
                value={formatActionCreditCoverage(
                  launchAttribution.actionCreditJoinCoverage,
                )}
                delta={getSummaryDelta(
                  launchDeltaLookup,
                  'actionCreditJoinCoverage:attributed',
                )}
              />
            </div>
            <div className="ops-health-table-wrap">
              <table className="ops-health-source-table">
                <caption>Source breakdown for canonical events</caption>
                <thead>
                  <tr>
                    <th scope="col">Source</th>
                    {LAUNCH_EVENT_ORDER.map((eventName) => (
                      <th key={eventName} scope="col">
                        {LAUNCH_EVENT_LABELS[eventName]}
                      </th>
                    ))}
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {launchSourceRows.map((row) => {
                    const rowDelta = getSourceRowDelta(
                      launchDeltaLookup,
                      'eventBreakdown',
                      row.sourceLabel,
                    );
                    return (
                    <tr
                      key={row.sourceLabel}
                      className={
                        rowDelta ? 'ops-health-source-row--changed' : ''
                      }
                    >
                      <th scope="row">
                        <span>{row.sourceLabel}</span>
                        {row.details ? <small>{row.details}</small> : null}
                        <DeltaBadge delta={rowDelta} />
                      </th>
                      {LAUNCH_EVENT_ORDER.map((eventName) => {
                        const cellDelta = getSourceCellDelta(
                          launchDeltaLookup,
                          'eventBreakdown',
                          row.sourceLabel,
                          eventName,
                        );
                        return (
                        <td
                          key={eventName}
                          className={
                            cellDelta ? 'ops-health-source-cell--changed' : ''
                          }
                        >
                          {formatCount(row.counts[eventName])}
                          <DeltaBadge delta={cellDelta} />
                        </td>
                        );
                      })}
                      <td>
                        {formatCount(row.total)}
                        <DeltaBadge delta={rowDelta} />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>

      <section
        className="ops-health-panel ops-health-operator"
        data-testid="ops-health-operator-visibility"
      >
        <div className="ops-health-section-heading">
          <h2>LocalOps Operator Visibility</h2>
          <span>
            {operatorVisibility?.proofType || 'read-only cockpit'}
          </span>
        </div>
        <p className="ops-health-muted ops-health-proof-copy">
          Read-only Camden operating view. Metrics marked not tracked yet are
          unavailable in current instrumentation and are not treated as zero.
        </p>
        <div
          className="ops-health-launch-pulse ops-health-operator-pulse"
          data-testid="ops-health-operator-pulse"
        >
          <span
            className={`ops-health-live-pill ops-health-live-pill--${launchRealtimeStatus}`}
          >
            {launchRealtimeStatus === 'live'
              ? 'Live invalidation connected'
              : 'Live invalidation unavailable'}
          </span>
          <span>
            Refresh trigger: {operatorRefreshTrigger || 'Not checked'}
          </span>
          <small>
            {getOpsHealthComparisonStatusMessage(operatorComparisonState)}
          </small>
        </div>
        <OpsHealthComparisonMeta
          comparison={operatorComparisonState}
          refreshTrigger={operatorRefreshTrigger}
          testId="ops-health-operator-comparison-meta"
        />
        <p className="ops-health-mini-meta">
          {OPS_HEALTH_ROLLING_WINDOW_CAVEAT}
        </p>
        {operatorVisibilityLoading ? (
          <p className="ops-health-muted">Loading operator visibility...</p>
        ) : null}
        {operatorVisibilityError ? (
          <div className="ops-health-alert ops-health-alert--error ops-health-alert--inline">
            {operatorVisibilityError}
          </div>
        ) : null}
        {operatorVisibility ? (
          <>
            <article
              className="ops-health-operator-narrative"
              data-testid="ops-health-operator-narrative"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Operator Summary</h3>
                <span>confirmed state vs tracking signal</span>
              </div>
              <p>
                Camden has {formatReportMetric(operatorSummary.totalLocalPlayers)} local
                players, {formatReportMetric(operatorSummary.verifiedLocalPlayers)} verified
                locals, {formatReportMetric(operatorSummary.mapReadyPlayers)} fresh saved
                locations, {formatReportMetric(operatorSummary.openFundedTasks)} open funded
                tasks, {formatReportMetric(operatorSummary.acceptedTasks)} currently accepted
                tasks, and {formatReportMetric(operatorSummary.completedTasks)} completed
                tasks.
              </p>
              <p>
                <strong>Main current risk:</strong>{' '}
                {getTrackedMetricNumber(operatorSummary.mapReadyPlayers) !== null &&
                getTrackedMetricNumber(operatorSummary.verifiedLocalPlayers) !== null &&
                getTrackedMetricNumber(operatorSummary.mapReadyPlayers) <
                  getTrackedMetricNumber(operatorSummary.verifiedLocalPlayers)
                  ? 'Fresh saved location is low compared with verified locals.'
                  : 'No single confirmed state risk dominates the loaded summary.'}
              </p>
              <p>
                <strong>Interpretation:</strong> Fresh saved location means a recent saved
                location timestamp. It does not count people who browsed first without
                sharing location.
              </p>
              <ol>
                <li>Compare location gate opened vs location saved when tracked.</li>
                <li>Compare task creation steps against funded task creation.</li>
                <li>Compare confirmed task accepted vs share accept success.</li>
              </ol>
            </article>
            <div
              className="ops-health-list ops-health-operator-summary"
              data-testid="ops-health-operator-summary"
            >
              <Metric
                label="Local players"
                value={operatorSummary.totalLocalPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.totalLocalPlayers')}
              />
              <Metric
                label="New players 7d"
                value={operatorSummary.newLocalPlayers7d}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.newLocalPlayers7d')}
              />
              <Metric
                label="Reachable"
                value={operatorSummary.reachablePlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.reachablePlayers')}
              />
              <Metric
                label="Fresh saved location"
                value={operatorSummary.mapReadyPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.mapReadyPlayers')}
              />
              <Metric
                label="Task eligible"
                value={operatorSummary.taskEligiblePlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.taskEligiblePlayers')}
              />
              <Metric
                label="Verified Local"
                value={operatorSummary.verifiedLocalPlayers}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.verifiedLocalPlayers')}
              />
              <Metric
                label="Open funded tasks"
                value={operatorSummary.openFundedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.openFundedTasks')}
              />
              <Metric
                label="Currently accepted tasks"
                value={operatorSummary.acceptedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.acceptedTasks')}
              />
              <Metric
                label="Completed tasks"
                value={operatorSummary.completedTasks}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.completedTasks')}
              />
              <Metric
                label="Available offerings"
                value={operatorSummary.availableOfferings}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.availableOfferings')}
              />
              <Metric
                label="Action Credit issued"
                value={operatorSummary.actionCreditIssued}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.actionCreditIssued')}
              />
              <Metric
                label="Source rows"
                value={operatorSummary.sourceActivityRows}
                delta={getSummaryDelta(operatorDeltaLookup, 'operator:systemSummary.sourceActivityRows')}
              />
            </div>

            <div
              className="ops-health-triage"
              data-testid="ops-health-operator-triage"
            >
              <div className="ops-health-operator-list-heading">
                <h3>What needs action now</h3>
                <span>{operatorActionability.triageCards.length} signals</span>
              </div>
              <p className="ops-health-muted">
                Action-ranked, read-only labels derived from existing Ops Health payloads.
              </p>
              <div className="ops-health-triage-grid">
                {operatorActionability.triageCards.map((card) => (
                  <TriageCard
                    key={card.title}
                    card={card}
                    delta={getTriageCardDelta(
                      card,
                      launchDeltaLookup,
                      operatorDeltaLookup,
                    )}
                  />
                ))}
              </div>
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-activation-funnel"
              >
                <h3>Activation Funnel</h3>
                <div className="ops-health-operator-fields">
                  <OperatorField
                    label="Magic requested"
                    value={operatorActivationFunnel.magicLinksRequested}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.magicLinksRequested')}
                  />
                  <OperatorField
                    label="Magic completed"
                    value={operatorActivationFunnel.magicLinksCompleted}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.magicLinksCompleted')}
                  />
                  <OperatorField
                    label="PlayerMap arrivals"
                    value={operatorActivationFunnel.playerMapArrivals}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.playerMapArrivals')}
                  />
                  <OperatorField
                    label="Location saved"
                    value={operatorActivationFunnel.locationSaved}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.locationSaved')}
                  />
                  <OperatorField
                    label="First task created"
                    value={operatorActivationFunnel.firstTaskCreated}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.firstTaskCreated')}
                  />
                  <OperatorField
                    label="First offering created"
                    value={operatorActivationFunnel.firstOfferingCreated}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:activationFunnel.firstOfferingCreated')}
                  />
                </div>
              </article>

              <article
                className={'ops-health-operator-card'}
                data-testid={'ops-health-magic-link-handoff'}
                style={{ gridColumn: '1 / -1' }}
              >
                <h3>Magic Link Handoff</h3>
                <p className={'ops-health-muted'}>
                  {operatorMagicLinkHandoffScope.label ||
                    'Current outcomes for new magic-link Players provisioned during the selected window.'}
                </p>
                <p className={'ops-health-mini-meta'}>
                  Provisioning window {formatDate(operatorMagicLinkHandoffScope.from)} through{' '}
                  {formatDate(operatorMagicLinkHandoffScope.to)} / current outcomes as of{' '}
                  {formatDate(
                    operatorMagicLinkHandoffScope.classificationAsOf ||
                      operatorMagicLinkHandoffClassification.asOf,
                  )}
                </p>
                <p className={'ops-health-muted'}>
                  Unique Player cohort: global Player records. Attribution activity:
                  selected report region and market filters. A completion after the
                  selected to-date still counts as the current completed outcome.
                </p>
                {operatorMagicLinkHandoffClassification.status === 'unavailable' ? (
                  <div className={'ops-health-alert ops-health-alert--error ops-health-alert--inline'}>
                    {operatorMagicLinkHandoffClassification.reason ||
                      'Magic-link time-state classification is unavailable.'}
                  </div>
                ) : null}
                <div className={'ops-health-operator-fields'}>
                  <OperatorField label={'Provisioned'} value={operatorMagicLinkHandoffCohort.provisioned} />
                  <OperatorField label={'Completed'} value={operatorMagicLinkHandoffCohort.completed} />
                  <OperatorField label={'Incomplete'} value={operatorMagicLinkHandoffCohort.incomplete} />
                  <OperatorField label={'Pending'} value={operatorMagicLinkHandoffCohort.pending} />
                  <OperatorField
                    label={'Provisioned, not completed'}
                    value={operatorMagicLinkHandoffCohort.provisionedNotCompleted}
                  />
                  <OperatorField label={'Timing unknown'} value={operatorMagicLinkHandoffCohort.timingUnknown} />
                  <OperatorField
                    label={'Matured completion rate'}
                    value={formatMagicLinkHandoffRate(operatorMagicLinkHandoffCohort.maturedCompletionRate)}
                  />
                  <OperatorField label={'Configured link lifetime'} value={operatorMagicLinkHandoffLifetime.label || '--'} />
                  <OperatorField label={'Existing-Player requests'} value={operatorMagicLinkExistingActivity.requestedEvents} />
                  <OperatorField label={'Existing-Player completed logins'} value={operatorMagicLinkExistingActivity.completedLoginEvents} />
                  <OperatorField label={'Filtered raw requests'} value={operatorMagicLinkRawActivity.requestedEvents} />
                  <OperatorField label={'Filtered raw completions'} value={operatorMagicLinkRawActivity.completedEvents} />
                  <OperatorField label={'Filtered new-Player events'} value={operatorMagicLinkRawActivity.playerCreatedEvents} />
                  <OperatorField label={'Filtered existing-Player login events'} value={operatorMagicLinkRawActivity.existingPlayerLoginEvents} />
                </div>
                <p className={'ops-health-muted'}>
                  Matured completion rate uses completed divided by completed plus
                  provisioned-not-completed. Pending and timing-unknown Players are
                  excluded. Current handoff values do not use snapshot deltas or
                  live-change highlighting.
                </p>
                {operatorMagicLinkIncompleteRows.length ? (
                  <div className={'ops-health-table-wrap'}>
                    <table className={'ops-health-source-table'}>
                      <caption>Recent incomplete magic-link Players</caption>
                      <thead>
                        <tr>
                          <th scope={'col'}>Player</th>
                          <th scope={'col'}>State</th>
                          <th scope={'col'}>Provisioned</th>
                          <th scope={'col'}>Latest request</th>
                          <th scope={'col'}>Age</th>
                          <th scope={'col'}>Expires</th>
                          <th scope={'col'}>Requests</th>
                        </tr>
                      </thead>
                      <tbody>
                        {operatorMagicLinkIncompleteRows.map((row) => (
                          <tr key={row.playerId}>
                            <th scope={'row'}>
                              {row.handle || row.playerId || 'Unknown Player'}
                              {row.emailDomain ? ` @ ${row.emailDomain}` : ''}
                            </th>
                            <td>{formatMagicLinkHandoffState(row.state)}</td>
                            <td>{formatDate(row.magicLinkProvisionedAt)}</td>
                            <td>{formatDate(row.lastMagicLinkRequestedAt)}</td>
                            <td>{formatMagicLinkHandoffAge(row.age)}</td>
                            <td>{row.expiresAt ? formatDate(row.expiresAt) : '--'}</td>
                            <td>{formatCount(row.magicLinkRequestCount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={'ops-health-muted'}>
                    No incomplete magic-link Players in the selected provisioning cohort.
                  </p>
                )}
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-location-readiness"
              >
                <h3>Fresh Saved Location</h3>
                <p className="ops-health-muted">
                  Fresh saved location means a recent saved location timestamp. It does
                  not measure users who browsed Camden without sharing location.
                </p>
                <div className="ops-health-operator-fields">
                  <OperatorField
                    label="Fresh saved"
                    value={operatorLocationReadiness.saved}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.saved')}
                  />
                  <OperatorField
                    label="Missing"
                    value={operatorLocationReadiness.missing}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.missing')}
                  />
                  <OperatorField
                    label="Stale"
                    value={operatorLocationReadiness.stale}
                    delta={getSummaryDelta(operatorDeltaLookup, 'operator:locationMapReadiness.stale')}
                  />
                  <OperatorField
                    label="Raw coordinates"
                    value={
                      operatorLocationReadiness.rawCoordinatesExposed
                        ? 'exposed'
                        : 'not exposed'
                    }
                  />
                  <OperatorField
                    label="Location primer shown"
                    value={
                      locationFunnelMetricByKey.primer_shown ||
                      notTrackedDisplayMetric('No location gate events recorded yet.')
                    }
                  />
                  <OperatorField
                    label="Browse-first clicked"
                    value={
                      locationFunnelMetricByKey.browse_first_clicked ||
                      notTrackedDisplayMetric('No location gate events recorded yet.')
                    }
                  />
                  <OperatorField
                    label="Use my location clicked"
                    value={
                      locationFunnelMetricByKey.use_my_location_clicked ||
                      notTrackedDisplayMetric('No location gate events recorded yet.')
                    }
                  />
                  <OperatorField
                    label="Location gate opened"
                    value={locationGateOpenedMetric}
                  />
                </div>
              </article>
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-active-player-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Useful Players / Helpers</h3>
                <span>{operatorPlayerRows.length} rows</span>
              </div>
              {operatorActionability.playerRows.length ? (
                operatorActionability.playerRows.slice(0, 8).map((row) => {
                  const playerDeltaId = getOpsSnapshotRowKey(row.playerId);
                  return (
                  <article className="ops-health-operator-row" key={row.playerId}>
                    <div>
                      <strong>{getPlayerDisplayLabel(row)}</strong>
                      <EntityDebugId label="Player ID" value={row.playerId} />
                      <PlayerUsefulnessNote usefulness={row.usefulness} />
                    </div>
                    <OperatorField label="Role" value={row.role || '--'} />
                    <OperatorField label="Location" value={row.locationStatus || '--'} />
                    <OperatorField
                      label="AC"
                      value={row.actionCreditCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:actionCreditCount`,
                      )}
                    />
                    <OperatorField
                      label="Helper profile"
                      value={
                        row.helperProfile?.requiredProfileComplete
                          ? 'complete'
                          : row.helperProfile?.missingSteps?.length
                            ? row.helperProfile.missingSteps
                                .map((step) => step.label || step.key)
                                .join(', ')
                            : '--'
                      }
                    />
                    <OperatorField
                      label="Verified Helper"
                      value={
                        row.localOpsVerifiedHelper?.approved ||
                        row.helperProfile?.verifiedHelperApproved
                          ? 'earned'
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Ride Ready"
                      value={
                        row.rideReady?.rideReady
                          ? 'ready'
                          : row.rideReady?.missingSteps?.length
                            ? row.rideReady.missingSteps
                                .map((step) => step.label || step.key)
                                .join(', ')
                            : '--'
                      }
                    />
                    <OperatorField
                      label="Magic completed"
                      value={row.magicLink?.loginCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:magicLink.loginCount`,
                      )}
                    />
                    <OperatorField
                      label="Activation sends"
                      value={row.activationEmail?.sendCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:activePlayerRows:${playerDeltaId}:activationEmail.sendCount`,
                      )}
                    />
                    <OperatorField label="Last active" value={formatDate(row.lastActiveAt)} />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No active LocalOps players in this window.</p>
              )}
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-task-visibility-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Task Visibility</h3>
                <span>{operatorTaskRows.length} rows</span>
              </div>
              {operatorActionability.taskRows.length ? (
                operatorActionability.taskRows.slice(0, 8).map((row) => {
                  const taskDeltaId = getOpsSnapshotRowKey(row.taskId);
                  return (
                  <article className="ops-health-operator-row" key={row.taskId}>
                    <div>
                      <strong>{getTaskDisplayTitle(row)}</strong>
                      <ActionabilityNote actionability={row.actionability} />
                      <DebugIdDetails
                        items={[
                          { label: 'Task ID', value: row.taskId },
                          { label: 'Requester ID', value: row.creatorPlayerId },
                          { label: 'Helper ID', value: row.acceptedHelperPlayerId },
                        ]}
                      />
                    </div>
                    <OperatorField label="Status" value={row.status || '--'} />
                    <OperatorField label="Region" value={getTaskRegionLabel(row)} />
                    <OperatorField label="Reward" value={formatTaskReward(row)} />
                    <OperatorField
                      label="Creator"
                      value={getOperatorPersonLabel(
                        row.creatorDisplay,
                        row.creatorPlayerId,
                        'Unknown creator',
                      )}
                    />
                    <OperatorField
                      label="Helper"
                      value={getOperatorPersonLabel(
                        row.helperDisplay,
                        row.acceptedHelperPlayerId,
                        'unassigned',
                      )}
                    />
                    <OperatorField
                      label="Reopens"
                      value={row.reopenCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:reopenCount`,
                      )}
                    />
                    <OperatorField
                      label="Releases"
                      value={row.helperReleaseCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:helperReleaseCount`,
                      )}
                    />
                    <OperatorField
                      label="Helper fanout email sent"
                      value={row.fanout?.emailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.emailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Helper fanout pixel image loads"
                      value={row.fanout?.emailPixelOpens}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.emailPixelOpens`,
                      )}
                    />
                    <OperatorField
                      label="Last fanout pixel image load"
                      value={
                        row.fanout?.lastEmailOpenedAt
                          ? formatDate(row.fanout.lastEmailOpenedAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Owner re-notify"
                      value={row.fanout?.ownerReNotifyCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.ownerReNotifyCount`,
                      )}
                    />
                    <OperatorField
                      label="Re-notify status"
                      value={row.fanout?.ownerReNotifyStatus || '--'}
                    />
                    <OperatorField
                      label="Last re-notify"
                      value={
                        row.fanout?.lastOwnerReNotifyAt
                          ? formatDate(row.fanout.lastOwnerReNotifyAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Re-notify recipients"
                      value={row.fanout?.lastOwnerReNotifyRecipientCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.lastOwnerReNotifyRecipientCount`,
                      )}
                    />
                    <OperatorField
                      label="Re-notify email sent"
                      value={row.fanout?.lastOwnerReNotifyEmailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:fanout.lastOwnerReNotifyEmailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Raise-pay status"
                      value={row.raisePay?.renotifyStatus || '--'}
                    />
                    <OperatorField
                      label="Last pay raise"
                      value={
                        row.raisePay?.lastPayRaiseAt
                          ? formatDate(row.raisePay.lastPayRaiseAt)
                          : '--'
                      }
                    />
                    <OperatorField
                      label="Raise-pay recipients"
                      value={row.raisePay?.renotifyRecipientCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:raisePay.renotifyRecipientCount`,
                      )}
                    />
                    <OperatorField
                      label="Raise-pay email sent"
                      value={row.raisePay?.renotifyEmailSentCount}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:taskRows:${taskDeltaId}:raisePay.renotifyEmailSentCount`,
                      )}
                    />
                    <OperatorField
                      label="Next re-notify"
                      value={
                        row.fanout?.nextEligibleOwnerReNotifyAt
                          ? formatDate(row.fanout.nextEligibleOwnerReNotifyAt)
                          : '--'
                      }
                    />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No task rows in this window.</p>
              )}
            </div>

            <div
              className="ops-health-operator-list"
              data-testid="ops-health-offering-visibility-rows"
            >
              <div className="ops-health-operator-list-heading">
                <h3>Offering Visibility</h3>
                <span>{operatorOfferingRows.length} rows</span>
              </div>
              {operatorOfferingRows.length ? (
                operatorOfferingRows.slice(0, 8).map((row) => {
                  const offeringDeltaId = getOpsSnapshotRowKey(row.offeringId);
                  return (
                  <article className="ops-health-operator-row" key={row.offeringId}>
                    <div>
                      <strong>{getOfferingDisplayTitle(row)}</strong>
                      <EntityDebugId label="Offering ID" value={row.offeringId} />
                      <EntityDebugId label="Owner ID" value={row.ownerPlayerId} />
                    </div>
                    <OperatorField label="Status" value={row.status || '--'} />
                    <OperatorField
                      label="Owner"
                      value={getOperatorPersonLabel(
                        row.ownerDisplay,
                        row.ownerPlayerId,
                        'Unknown owner',
                      )}
                    />
                    <OperatorField label="Price" value={row.price === null ? '--' : formatUsd(row.price)} />
                    <OperatorField
                      label="Reservations"
                      value={row.reservations}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:reservations`,
                      )}
                    />
                    <OperatorField
                      label="Fulfilled"
                      value={row.fulfilledOrders}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:fulfilledOrders`,
                      )}
                    />
                    <OperatorField
                      label="Share landings"
                      value={row.shareLandings}
                      delta={getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:offeringRows:${offeringDeltaId}:shareLandings`,
                      )}
                    />
                  </article>
                  );
                })
              ) : (
                <p className="ops-health-muted">No offering rows in this window.</p>
              )}
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-source-attribution"
              >
                <h3>Source Attribution</h3>
                {operatorSourceRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorSourceRows.slice(0, 6).map((row) => {
                      const rowDelta = getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:sourceAttributionRows:${row.sourceKey}:total`,
                      );
                      return (
                        <li key={row.sourceKey}>
                          <strong>{row.sourceKey}</strong>
                          <span>
                            {formatCount(row.total)} tracked events
                            <DeltaBadge delta={rowDelta} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No source rows in this window.</p>
                )}
                {operatorActionability.sourceRows.length ? (
                  <>
                    <h4 className="ops-health-operator-subhead">
                      Source Outcome Quality
                    </h4>
                    <ul className="ops-health-operator-stack">
                      {operatorActionability.sourceRows.slice(0, 6).map((row) => (
                        <li key={`outcome-${row.key}`}>
                          <strong>{row.displayLabel}</strong>
                          <span>{row.evidence}</span>
                          <em>{row.outcomeLabel}</em>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-email-proof"
              >
                <h3>Email & Notification Proof</h3>
                <p className="ops-health-muted">
                  Pixel image loads are approximate email-open signals, not read confirmation.
                </p>
                {operatorEmailRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorEmailRows.slice(0, 6).map((row) => {
                      const entityTitle = getOperatorEmailEntityTitle(row);
                      const entityType =
                        row.entityType || (row.taskId ? 'task' : 'entity');
                      const entityId =
                        row.entityId || row.taskId || row.offeringId || '';
                      const rowKey = row.proofId || row.notificationId;
                      const proofDeltaId = getOpsSnapshotRowKey(rowKey);
                      const pixelOpenDelta = getSummaryDelta(
                        operatorDeltaLookup,
                        `operator:emailNotificationProofRows:${proofDeltaId}:pixelOpenCount`,
                      );
                      return (
                        <li key={rowKey}>
                          <strong>{row.emailType || row.proofType}</strong>
                          {entityTitle ? <span>Task: {entityTitle}</span> : null}
                          <DebugIdDetails
                            summary="Email debug IDs available"
                            items={[
                              {
                                label: `${entityType || 'entity'} ID`,
                                value: entityId,
                              },
                              { label: 'Proof ID', value: rowKey },
                            ]}
                          />
                          <span>
                            {row.sendStatus || '--'} / pixel image loads{' '}
                            {getMetricValue(row.pixelOpenCount)}
                            <DeltaBadge delta={pixelOpenDelta} />
                          </span>
                          {row.firstPixelOpenedAt ? (
                            <span>
                              first image load {formatDate(row.firstPixelOpenedAt)}
                            </span>
                          ) : null}
                          {row.lastPixelOpenedAt ? (
                            <span>
                              last image load {formatDate(row.lastPixelOpenedAt)}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No email proof rows in this window.</p>
                )}
              </article>
            </div>

            <div className="ops-health-operator-grid">
              <article
                className="ops-health-operator-card"
                data-testid="ops-health-action-credit"
              >
                <h3>Action Credit / Verified Completion</h3>
                {operatorActionability.actionCreditRows.length ? (
                  <ul className="ops-health-operator-stack">
                    {operatorActionability.actionCreditRows.slice(0, 6).map((row) => (
                      <li key={row.actionCreditId}>
                        <strong>{row.title}</strong>
                        <span>{row.meta}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="ops-health-muted">No Action Credit rows in this window.</p>
                )}
              </article>

              <article
                className="ops-health-operator-card"
                data-testid="ops-health-loc-readiness"
              >
                <h3>LOC Readiness</h3>
                <div className="ops-health-operator-fields">
                  <OperatorField label="Status" value={operatorLocPlaceholder.status || 'not enabled yet'} />
                  <OperatorField label="Future page" value={operatorLocPlaceholder.futurePage || 'planned'} />
                  <OperatorField label="Future role" value={operatorLocPlaceholder.futureRole || 'planned'} />
                  <OperatorField label="Captured leads" value={operatorLocPlaceholder.capturedLeads || 'not enabled'} />
                </div>
              </article>
            </div>

            <details
              className="ops-health-diagnostics"
              data-testid="ops-health-operator-diagnostics"
            >
              <summary>Diagnostics</summary>
              <div className="ops-health-operator-fields">
                <OperatorField
                  label="Realtime"
                  value={
                    launchRealtimeStatus === 'live'
                      ? 'live invalidation connected'
                      : 'live invalidation unavailable'
                  }
                />
                <OperatorField
                  label="Last refresh reason"
                  value={launchRefreshTrigger || 'initial load'}
                />
                <OperatorField
                  label="Share rows loaded"
                  value={formatCount(shareFunnelRows.length)}
                />
                <OperatorField
                  label="Managed rows showing"
                  value={`${formatCount(
                    managedShareFunnelRows.pageRows.length,
                  )} of ${formatCount(managedShareFunnelRows.totalRows)}`}
                />
              </div>
              <ul className="ops-health-operator-stack">
                <li>
                  <strong>Task debug IDs</strong>
                  <span>
                    {operatorTaskRows.map((row) => row.taskId).join(', ') || '--'}
                  </span>
                </li>
                <li>
                  <strong>Offering debug IDs</strong>
                  <span>
                    {operatorOfferingRows.map((row) => row.offeringId).join(', ') ||
                      '--'}
                  </span>
                </li>
                <li>
                  <strong>Source keys</strong>
                  <span>
                    {sourceShareSourceOptions.join(', ') ||
                      operatorSourceRows.map((row) => row.sourceKey).join(', ') ||
                      '--'}
                  </span>
                </li>
              </ul>
            </details>
          </>
        ) : null}
      </section>

      <section
        className="ops-health-panel ops-health-settlement-queue"
        data-testid="ops-health-settlement-queue"
      >
        <div className="ops-health-section-heading">
          <h2>Offering Settlement Queue</h2>
          <span>{settlementRows.length} rows</span>
        </div>
        {settlementError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {settlementError}
          </div>
        ) : null}
        {settlementNotice ? (
          <div className="ops-health-alert ops-health-alert--success">
            {settlementNotice}
          </div>
        ) : null}
        {settlementLoading ? (
          <p className="ops-health-muted">Loading settlement queue...</p>
        ) : null}
        {!settlementLoading && settlementRows.length === 0 ? (
          <p className="ops-health-muted">
            No stuck offering settlements are waiting for admin action.
          </p>
        ) : null}
        {settlementRows.length > 0 ? (
          <div className="ops-health-settlement-results">
            {settlementRows.map((row) => {
              const rowKey = row.orderId || `${row.offeringTitle}-${row.updatedAt}`;
              const allowedActions = Array.isArray(row.allowedActions)
                ? row.allowedActions
                : [];
              const refundBusy =
                settlementActionBusy === `${row.orderId}:refund_buyer`;
              const payBusy =
                settlementActionBusy === `${row.orderId}:pay_seller`;
              return (
                <article
                  className="ops-health-settlement-row"
                  key={rowKey}
                  data-testid="ops-health-settlement-row"
                >
                  <div className="ops-health-task-row-main">
                    <strong>{row.offeringTitle || 'Untitled offering'}</strong>
                    <span>{row.orderId}</span>
                  </div>
                  <div className="ops-health-task-row-meta">
                    <span>{row.classification || 'Unclassified'}</span>
                    <span>buyer {row.buyer?.playerId || '--'}</span>
                    <span>seller {row.seller?.playerId || '--'}</span>
                    <span>qty {row.quantity ?? '--'}</span>
                    <span>{formatUsd(row.amountUsd)} / {formatJgk(row.amountJGK)}</span>
                  </div>
                  <div className="ops-health-task-row-badges">
                    <span>{row.status || 'unknown'} order</span>
                    <span>{row.escrowStatus || 'none'} escrow</span>
                    <span>{row.settlementStatus || 'none'} settlement</span>
                    <span>{row.settlementOutcome || 'none'} outcome</span>
                    <span>confirmed {formatDate(row.providerConfirmedAt)}</span>
                    <span>confirm by {formatDate(row.confirmByAt)}</span>
                    <span>updated {formatDate(row.updatedAt)}</span>
                  </div>
                  <div className="ops-health-settlement-actions">
                    {allowedActions.includes('refund_buyer') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'refund_buyer')}
                        disabled={!!settlementActionBusy}
                      >
                        {refundBusy ? 'Refunding...' : 'Refund buyer'}
                      </button>
                    ) : null}
                    {allowedActions.includes('pay_seller') ? (
                      <button
                        type="button"
                        onClick={() => handleSettlementAction(row, 'pay_seller')}
                        disabled={!!settlementActionBusy}
                      >
                        {payBusy ? 'Paying...' : 'Pay seller'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        window.navigator?.clipboard?.writeText?.(row.orderId)
                      }
                    >
                      Copy order ID
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="ops-health-grid">
        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Readiness</h2>
            <span>{truthyLabel(readinessSummary.pass)}</span>
          </div>
          <div className="ops-health-list">
            <Metric label="Env health" value={truthyLabel(staticConfig.pass)} />
            <Metric
              label="Escrow player"
              value={truthyLabel(systemPlayers.escrow?.exists)}
            />
            <Metric
              label="Treasury player"
              value={truthyLabel(systemPlayers.treasury?.exists)}
            />
            <Metric
              label="Stripe prices"
              value={truthyLabel(stripe.allPriceEnvsPresent)}
            />
            <Metric
              label="Stripe webhook"
              value={truthyLabel(stripe.webhookSecret?.present)}
            />
            <Metric
              label="Secrets redacted"
              value={truthyLabel(readinessSummary.secretsRedacted)}
            />
          </div>
          <div className="ops-health-flags">
            <span>Ignition</span>
            <strong>
              incentives {String(ignitionFlags.incentives?.effective ?? 'unknown')}
            </strong>
            <strong>
              rewards {String(ignitionFlags.rewards?.effective ?? 'unknown')}
            </strong>
            <strong>
              boost {String(ignitionFlags.boost?.effective ?? 'unknown')}
            </strong>
          </div>
        </div>

        <div className="ops-health-panel">
          <div className="ops-health-section-heading">
            <h2>Accounting Rollup</h2>
            <span>{rollup?.proofType || 'not loaded'}</span>
          </div>
          <div className="ops-health-list">
            <Metric
              label="Platform revenue"
              value={formatJgk(rollup?.platformRevenueJGK)}
            />
            <Metric
              label="Contra revenue / boosts"
              value={formatJgk(rollup?.contraRevenueJGK)}
            />
            <Metric
              label="Promo issuance / rewards"
              value={formatJgk(rollup?.promoIssuanceJGK)}
            />
            <Metric
              label="Helper payouts"
              value={formatJgk(rollup?.helperPayoutsJGK)}
            />
            <Metric label="Escrow held" value={formatJgk(rollup?.escrowHeldJGK)} />
            <Metric
              label="Escrow released"
              value={formatJgk(rollup?.escrowReleasedJGK)}
            />
            <Metric
              label="Cashout held"
              value={formatJgk(rollup?.cashoutHeldJGK)}
            />
            <Metric
              label="Cashout paid"
              value={formatJgk(rollup?.cashoutSettledJGK)}
            />
            <Metric
              label="Cashout released"
              value={formatJgk(rollup?.cashoutReleasedJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(rollup?.netPlatformPositionJGK)}
            />
          </div>
        </div>
      </section>

      <section className="ops-health-panel ops-health-task-finder">
        <div className="ops-health-section-heading">
          <h2>Find Recent Tasks</h2>
          <span>{taskResults.length} results</span>
        </div>
        <form
          className="ops-health-finder-form"
          data-testid="ops-health-task-finder-form"
          onSubmit={handleTaskSearch}
        >
          <label htmlFor="ops-health-player-id">Player</label>
          <input
            id="ops-health-player-id"
            value={taskFilters.playerId}
            onChange={(event) => updateTaskFilter('playerId', event.target.value)}
            placeholder="creator or helper playerId"
          />

          <label htmlFor="ops-health-task-title">Title</label>
          <input
            id="ops-health-task-title"
            value={taskFilters.q}
            onChange={(event) => updateTaskFilter('q', event.target.value)}
            placeholder="title fragment"
          />

          <label htmlFor="ops-health-task-from">From</label>
          <input
            id="ops-health-task-from"
            type="date"
            value={taskFilters.from}
            onChange={(event) => updateTaskFilter('from', event.target.value)}
          />

          <label htmlFor="ops-health-task-to">To</label>
          <input
            id="ops-health-task-to"
            type="date"
            value={taskFilters.to}
            onChange={(event) => updateTaskFilter('to', event.target.value)}
          />

          <label htmlFor="ops-health-task-status">Status</label>
          <select
            id="ops-health-task-status"
            value={taskFilters.status}
            onChange={(event) => updateTaskFilter('status', event.target.value)}
          >
            {TASK_STATUS_OPTIONS.map((statusOption) => (
              <option key={statusOption || 'all'} value={statusOption}>
                {statusOption || 'Any status'}
              </option>
            ))}
          </select>

          <label htmlFor="ops-health-task-limit">Limit</label>
          <input
            id="ops-health-task-limit"
            type="number"
            min="1"
            max="100"
            value={taskFilters.limit}
            onChange={(event) => updateTaskFilter('limit', event.target.value)}
          />

          <label className="ops-health-toggle" htmlFor="ops-health-boosted">
            <input
              id="ops-health-boosted"
              type="checkbox"
              checked={taskFilters.boosted}
              onChange={(event) =>
                updateTaskFilter('boosted', event.target.checked)
              }
            />
            Boosted only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-rewarded">
            <input
              id="ops-health-rewarded"
              type="checkbox"
              checked={taskFilters.rewarded}
              onChange={(event) =>
                updateTaskFilter('rewarded', event.target.checked)
              }
            />
            Rewarded only
          </label>

          <label className="ops-health-toggle" htmlFor="ops-health-anomaly-only">
            <input
              id="ops-health-anomaly-only"
              type="checkbox"
              checked={taskFilters.anomalyOnly}
              onChange={(event) =>
                updateTaskFilter('anomalyOnly', event.target.checked)
              }
            />
            Anomaly only
          </label>

          <div className="ops-health-finder-actions">
            <button type="submit" disabled={taskSearchLoading}>
              {taskSearchLoading ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => fetchTaskResults(taskFilters)}
              disabled={taskSearchLoading}
            >
              Refresh
            </button>
          </div>
        </form>
        {taskSearchError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskSearchError}
          </div>
        ) : null}
        <div
          className="ops-health-task-results"
          data-testid="ops-health-task-results"
        >
          {taskResults.length > 0 ? (
            taskResults.map((task) => (
              <article
                className="ops-health-task-row"
                key={task.taskId}
                data-testid="ops-health-task-result"
              >
                <div className="ops-health-task-row-main">
                  <strong>{task.title || 'Untitled task'}</strong>
                  <span className="ops-health-entity-meta">
                    debug id {formatShortId(task.taskId)}
                  </span>
                </div>
                <div className="ops-health-task-row-meta">
                  <span>{task.status || 'unknown'}</span>
                  <span>creator {task.creatorPlayerId || '--'}</span>
                  <span>helper {task.helperPlayerId || 'unassigned'}</span>
                  <span>updated {formatDate(task.updatedAt)}</span>
                </div>
                <div className="ops-health-task-row-badges">
                  <span>{formatJgk(task.priceJGK)} escrow</span>
                  <span>{task.escrowStatus || 'none'} escrow</span>
                  <span>{task.payoutStatus || 'unpaid'} payout</span>
                  {task.hasBoost ? (
                    <span>{formatJgk(task.platformBoostJGK)} boost</span>
                  ) : null}
                  {task.hasRewards ? (
                    <span>{formatJgk(task.creatorRewardsJGK)} rewards</span>
                  ) : null}
                  <span>{task.anomalyCount || 0} anomalies</span>
                </div>
                <button
                  type="button"
                  onClick={() => loadTaskReport(task.taskId)}
                  disabled={taskLoading}
                >
                  Open accounting report
                </button>
              </article>
            ))
          ) : (
            <p className="ops-health-muted">
              No recent tasks match the current filters.
            </p>
          )}
        </div>
      </section>

      <section className="ops-health-panel">
        <div className="ops-health-section-heading">
          <h2>Advanced: Lookup By Task Id</h2>
          <span>task-level proof</span>
        </div>
        <form
          className="ops-health-task-form"
          data-testid="ops-health-task-lookup-form"
          onSubmit={handleTaskLookup}
        >
          <label htmlFor="ops-health-task-id">Task id</label>
          <input
            id="ops-health-task-id"
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            placeholder="Paste task ObjectId"
          />
          <button type="submit" disabled={taskLoading}>
            {taskLoading ? 'Checking...' : 'Lookup'}
          </button>
        </form>
        {taskError ? (
          <div className="ops-health-alert ops-health-alert--error">
            {taskError}
          </div>
        ) : null}
        {taskReport ? (
          <div className="ops-health-task-report" data-testid="ops-health-task-report">
            <Metric label="Task title" value={getTaskReportTitle(taskReport)} />
            <Metric label="Debug task id" value={formatShortId(taskReport.taskId) || '--'} />
            <Metric label="Escrow" value={formatJgk(taskReport.escrowAmountJGK)} />
            <Metric
              label="Payout"
              value={formatJgk(
                taskReport.expectedHelperPayoutJGK ??
                  taskReport.summary?.helperPayoutJGK,
              )}
            />
            <Metric label="Fee" value={formatJgk(taskReport.netPlatformFeeJGK)} />
            <Metric label="Boost" value={formatJgk(taskReport.platformBoostJGK)} />
            <Metric
              label="Rewards"
              value={formatJgk(taskReport.summary?.creatorRewardsJGK)}
            />
            <Metric
              label="Net platform position"
              value={formatJgk(taskReport.summary?.netPlatformPositionJGK)}
            />
            <Metric
              label="Task anomalies"
              value={(taskReport.anomalies || []).length}
            />
            <JsonDetails
              title="Raw task JSON"
              payload={taskReport}
              testId="ops-health-task-json"
            />
          </div>
        ) : null}
      </section>

      <JsonDetails
        title="Raw readiness and rollup JSON"
        payload={{ readiness, rollup }}
        testId="ops-health-raw-json"
      />
    </main>
  );
}
